// RealtimeLLMToElevenLabs.js
import { RealTimeLipSyncTap } from "./RealTimeLipSyncTap.js";
import { MinimalLipSyncController } from "./LipSyncController.minimal.js";
import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";
import { A2FEmotionRenderer } from "./A2FEmotionRenderer.js";
import { shouldUseA2FLipSync } from "./A2FLipSyncConfig.js";
import {
  A2FDebugRecorder,
  isA2FTimingDebugEnabled,
} from "./A2FDebugRecorder.js";
// Task #33: temporary opt-in raw-chunk recorder. No-op unless explicitly
// enabled server-side via the ELEVENLABS_CHUNK_RECORDER_ENABLED env var.
import { ElevenLabsChunkRecorder } from "./ElevenLabsChunkRecorder.js";
// Task #347 (THE BOARD GRAMMAR) — consume the alignment payloads ElevenLabs
// already streams back (we send sync_alignment:true) instead of discarding
// them: per-turn word timings for live/debug consumers. Stored lesson anchors
// NEVER resolve against this live stream — they pin to the HTTP path's
// hash-pinned alignment; hall lectures without stored timing keep degrading
// to beat-start exactly as today.
import { createWsAlignmentAggregator } from "./CueScheduler.js";
// Shared A2F forward-pacing tuning lives in `A2FTuning.js` so the chat
// and greeting paths can't drift apart. See that module for the
// rationale behind each value. (Task #99: the suspend-based
// frame-starvation pause/resume subsystem was removed — speaker audio is
// never paused; lip-sync conforms to the audio. The forward-pacer is
// kept because it meters only the bridge-bound copy, never the speaker.)
import {
  A2F_FORWARD_PACING_ENABLED,
  A2F_FORWARD_PACING_RATE,
  A2F_FORWARD_PACING_WARMUP_MS,
} from "./A2FTuning.js";

// Task #33 A/B: when true, send `auto_mode: true` in the ElevenLabs WS
// init message and let ElevenLabs auto-tune buffering (their recommended
// streaming-TTS setting and a known fix for missing-audio-at-start). When
// false, fall back to the manual `chunk_length_schedule`. Flip this single
// constant to A/B test, then rebuild with `npm run build`.
const ELEVEN_AUTO_MODE = true;

// Task #33 "decoy primer": ElevenLabs realtime WS sometimes drops the
// first ~100-300ms of synthesized audio for a fresh generation. Workaround:
// prepend a tiny throwaway text to the FIRST phrase of each new turn so
// the drop lands on the decoy instead of the real opening words. Common
// choices (most natural first):
//   * "... "     → soft pause, usually inaudible
//   * "Mm, "     → tiny natural-sounding filler
//   * "Hm. "     → similar
//   * ""         → disable the primer (no decoy)
// Edit this single string and rebuild with `npm run build` to A/B.
const ELEVEN_FIRST_TEXT_PRIMER = "";

// Task #99: ElevenLabs eats a few ms of audio at the very start of each
// generation (the "missing opening words" symptom, e.g. "As you asked, let").
// Prepend a single inaudible leading space to the FIRST phrase of every turn
// so the eaten audio is silence, not the opening words. Combined with the
// guaranteed trailing space on every outbound payload (clean word boundaries
// so consecutive sentences never fuse, e.g. "...ridone_akash.It is a pleasure"),
// this closes the start/end-of-sentence word drops.
const ELEVEN_LEADING_SPACE_PRIME = true;

// Task #99 (diagnostics): the production esbuild bundle strips every console.*
// call, so the per-turn [AUDIO-LEDGER] line is invisible in the browser. When
// this is true, the engine also POSTs the ledger + the exact text it sent to
// ElevenLabs to /api/debug/audio-ledger so it lands in the (reliable) server
// log. Set to false to silence the server-side mirror.
const AUDIO_LEDGER_SERVER_LOG = true;

// Task #7 first-chunk buffer: when enabled, the very first `{text}` payload
// sent to ElevenLabs in each new turn is held in a client-side buffer until
// it reaches `ELEVEN_FIRST_CHUNK_MIN_CHARS` characters, an idle timer of
// `ELEVEN_FIRST_CHUNK_TIMEOUT_MS` ms fires (no new text from the LLM), or
// the turn ends (flush). After the first payload is sent, all subsequent
// phrases in that turn are forwarded immediately (current behavior). The
// decoy primer (`ELEVEN_FIRST_TEXT_PRIMER`) attaches to the first outgoing
// payload, i.e. the buffered chunk. Flip the flag and rebuild with
// `npm run build` to A/B test.
const ELEVEN_FIRST_CHUNK_BUFFER_ENABLED = false;
const ELEVEN_FIRST_CHUNK_MIN_CHARS = 300;
const ELEVEN_FIRST_CHUNK_TIMEOUT_MS = 800;

// Task #37 client-side master off-switch for the ElevenLabs raw-chunk
// recorder (Task #33). When `false` (default), no `ElevenLabsChunkRecorder`
// instance is ever constructed, no chunks are buffered, and no POSTs to
// `/api/debug/elevenlabs-chunks` are made — even if the server-side env
// var `ELEVENLABS_CHUNK_RECORDER_ENABLED=1` is set. Flip to `true` and
// rebuild with `npm run build` to re-enable recording (still also gated
// server-side by the env var: BOTH must be on to actually persist files).
const ELEVEN_CHUNK_RECORDER_ENABLED = false;

// Task #30: canonical PCM-duration helper. ElevenLabs PCM_16000 is fixed at
// 16000 Hz / 16-bit / mono regardless of the destination AudioContext sample
// rate, so duration math from raw bytes MUST use 16000 Hz — not
// audioCtx.sampleRate (typically 48000), which would under-count by 3x.
// AudioContext.sampleRate is only valid AFTER PCM has been decoded into an
// AudioBuffer (the buffer carries its own sampleRate field).
// Task #21 — Client-side forward-to-A2F pacer. Tuning constants
// (A2F_FORWARD_PACING_*) are imported from `A2FTuning.js`.

// Task #99: end-of-reply teardown no longer depends on ElevenLabs'
// `isFinal` / `audio_end` / `flush_complete` signals (which were often
// never sent) or on the old flush-close watchdog. The mouth now closes
// on a COMPUTED clock derived from the scheduled PCM audio duration:
// every scheduled buffer re-arms a timer for the exact wall time the
// audio is known to end (`_armComputedTeardown`). When audio stops
// arriving, the last-armed timer fires at the true end and runs the
// shared idle teardown. No vendor callback is on the critical path.

const A2F_PCM_SOURCE_SAMPLE_RATE_HZ = 16000;
const A2F_PCM_SOURCE_BYTES_PER_SECOND = A2F_PCM_SOURCE_SAMPLE_RATE_HZ * 2;
function pcm16Mono16000BytesToMs(byteLength) {
  const n = Number(byteLength);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return (n / A2F_PCM_SOURCE_BYTES_PER_SECOND) * 1000;
}

// optional: your AnimationController if you need it in here.
// import { AnimationController } from "./AnimationController.js";

/* ============================
   Global TTS speaking state
   ============================ */
export let TTSActive = false;

// Keep module export + window mirror in sync
function _setTTSActive(val) {
  TTSActive = !!val;
  try {
    if (typeof window !== "undefined") window.TTSActive = TTSActive;
  } catch (_) {}
}

// Lightweight event helper (so UI can listen for audio-start, audio-end, volume-change, stop-visibility)
class TinyEvents {
  constructor() {
    this._et = new (window.EventTarget ||
      class {
        addEventListener() {}
        removeEventListener() {}
        dispatchEvent() {}
      })();
  }
  on(type, fn) {
    try {
      this._et.addEventListener(type, fn);
    } catch {}
  }
  off(type, fn) {
    try {
      this._et.removeEventListener(type, fn);
    } catch {}
  }
  emit(type, detail) {
    try {
      this._et.dispatchEvent(new CustomEvent(type, { detail }));
    } catch {}
  }
}

export class RealtimeLLMToElevenLabs {
  constructor(opts) {
    opts = opts || {};
    this.voiceId = opts.voiceId;
    this.apiKey = opts.elevenLabsApiKey;
    this.modelId =
      opts.modelId === undefined ? "eleven_multilingual_v2" : opts.modelId;
    this.outputFormat =
      opts.outputFormat === undefined ? "pcm_16000" : opts.outputFormat;
    this.syncAlignment =
      opts.syncAlignment === undefined ? true : !!opts.syncAlignment;

    // Task #99: OUTBOUND sentence segmentation. Phrases are emitted ONLY
    // on sentence/clause boundaries — no 250 ms timeout flush and no
    // token-count mid-word chop (both produced fragmented text that
    // ElevenLabs would synthesize as separate utterances, causing the
    // dropped/clipped opening words). Two knobs remain:
    //   - firstPhraseMaxChars: once the first sentence boundary is this
    //     far out, allow an early emit at a comma/clause boundary so the
    //     very first audio arrives fast.
    //   - phraseSafetyCapChars: a high ceiling so a reply with no
    //     punctuation at all can't buffer unboundedly — emit at the last
    //     word break once the buffer grows past this.
    this.firstPhraseMaxChars =
      opts.firstPhraseMaxChars === undefined ? 90 : opts.firstPhraseMaxChars;
    this.phraseSafetyCapChars =
      opts.phraseSafetyCapChars === undefined
        ? 320
        : opts.phraseSafetyCapChars;
    this._emittedAnyPhrase = false;

    this.jitterMs = opts.jitterMs === undefined ? 120 : opts.jitterMs;

    // Keep the WS alive across the whole conversation
    this.persistentWs =
      opts.persistentWs === undefined ? true : !!opts.persistentWs;

    // Task #274 (iOS jetsam): when a barge-in hard-closes the WS to cut
    // off the dead turn's still-streaming audio, this one-shot flag tells
    // the close handler NOT to auto-reconnect — the NEXT turn's
    // `_ensureConnected()` owns the reconnect, so two connect paths never
    // race each other into duplicate sockets.
    this._suppressReconnectOnce = false;

    // Inactivity / keep-alive
    this.inactivityTimeoutSec = Math.min(
      180,
      Math.max(
        5,
        opts.inactivityTimeoutSec === undefined
          ? 180
          : Number(opts.inactivityTimeoutSec),
      ),
    );
    this._keepAliveIntervalMs = Math.max(
      5000,
      this.inactivityTimeoutSec * 1000 - 5000,
    );

    // Auto-preempt
    this.autoPreempt =
      opts.autoPreempt === undefined ? true : !!opts.autoPreempt;
    this.newResponseIdleMs =
      opts.newResponseIdleMs === undefined
        ? 1200
        : Number(opts.newResponseIdleMs);
    this._streamActive = false;
    this._streamIdleTimer = null;
    this._awaitingNewResponse = false;

    // WS state
    this.ws = null;
    this.wsOpen = false;
    this._wsKeepAliveTimer = null;
    this._userCanceled = false;
    this._reconnecting = false;
    this._reconnectAttempt = 0;
    this._maxBackoffMs = 15000;
    this._baseBackoffMs = 600;

    // Audio/text queues
    this.audioCtx = null;
    this.audioQueue = [];
    // Task #99 (accounting): always-on per-turn audio ledger so word-skip
    // reports are diagnosable WITHOUT listening. `received` counts every
    // ElevenLabs chunk handed to us; `scheduled` counts every chunk that
    // actually reached the Web Audio graph. received === scheduled means
    // ZERO audio was dropped (the user's "store every bottle" rule). The
    // `dropped*` fields attribute any loss. `frameUnderruns` is read from
    // the lip-sync driver at teardown (late frames = mouth skip, not audio
    // skip). Mirrored to window.__audioStats and logged once per turn.
    this._audioStats = this._freshAudioStats();
    // Task #347 — per-turn word-timing aggregator fed by the WS `alignment`
    // messages (chars→words, tolerant chunk rebasing — see CueScheduler.js).
    this._wsAlignment = createWsAlignmentAggregator();
    this._audioEpoch = 0;
    this.playing = false;
    this.lastAudioAt = 0;
    this.idleCloseTimer = null;

    // 🔊 Master volume state
    // after (70% default)
    this.volume =
      opts.volume === undefined
        ? 0.6
        : Math.max(0, Math.min(1, Number(opts.volume)));
    this.muted = !!opts.muted;
    this.masterGain = null;

    // Active nodes for preemption
    this._activeNodes = new Set(); // { src, gain }

    this.buf = "";
    this._emittedAnyPhrase = false;
    this.flushTimer = null;

    this.outboundQueue = [];
    this.closeAfterDrain = false;
    this.pendingBatches = 0;
    this.readyPromise = null;
    this.readyResolve = null;

    // Task #7 first-chunk buffer state (per-epoch). When the feature flag
    // is on, incoming phrases for the FIRST outgoing payload of a turn
    // accumulate in `_firstChunkBuffer` until size/timeout/EOT triggers a
    // flush. After the first flush, `_firstChunkSent` flips true and
    // subsequent phrases bypass the buffer entirely.
    this._firstChunkBuffer = "";
    this._firstChunkTimer = null;
    this._firstChunkSent = false;

    // Lip sync
    this._wantLipSync = false;
    this._lipSyncController = null;
    this._lipSyncOpts = opts.lipSyncOpts || {};
    this._analyser = null;
    this._lipSyncTap = null;
    this._tapCalibrated = false;

    // Drop shield
    this._droppingIncomingAudio = false;
    this._hasBeganThisEpoch = false;
    // Task #33 decoy primer: tracks whether the leading primer text has
    // been sent for the current epoch. Reset alongside _hasBeganThisEpoch.
    this._primerSentThisEpoch = false;
    this._userStoppedCurrentResponse = false; // Prevents residual deltas from resetting drop shield
    this._expectingNewTurn = false; // Set by ChatController when new request is sent
    this._currentTurnId = 0; // Incremented on each new turn for delta ownership verification

    this._lslog = (...args) => console.log("[LipSync]", ...args);

    // A2F Bridge (NVIDIA Audio2Face-3D)
    this._a2fEnabled = opts.a2fEnabled !== false;
    this._a2fWs = null;
    this._a2fWsOpen = false;
    this._a2fConnecting = false;
    this._a2fPendingAudio = [];
    this._a2fLipSyncDriver = null;
    // Emotion renderer is paired 1:1 with the lip-sync driver and lives
    // for the same lifetime — created in `enableLipSync()` when A2F is
    // active for this character, disposed in `disableLipSync()`. It writes
    // ARKit emotion shapes (brow / eye / nose only) on top of every
    // lipsync frame the driver applies, with a slow decay back to neutral
    // after silence.
    this._a2fEmotionRenderer = null;
    this._useA2FLipSync = false;
    this._a2fSessionActive = false;
    this._a2fSessionGen = 0;
    this._a2fActiveGen = null;
    this._a2fAcceptingFrames = false;
    // _a2fAudioInputDone: set true when EndOfAudio has been sent to the
    // bridge. Used purely for idempotency on `_a2fEndSession("end")` —
    // it does NOT switch the inbound frame handler to a special "burst"
    // mode. Frames are accepted and buffered identically before and
    // after EndOfAudio; the sync gate only watches buffer fill.
    this._a2fAudioInputDone = false;
    this._a2fSyncGate = null;
    this._a2fSyncGateResolve = null;
    this._a2fFirstFrameReceived = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fBufferedFrames = [];
    this._a2fDriverStarted = false;
    this._a2fKeepAliveTimer = null;

    // Task #21 — A2F forward-pacer state. Single source of truth for
    // the bridge-bound chunk queue and its wall-clock release schedule.
    // Reset at every turn boundary via `_a2fResetPacer(reason)`.
    // (Task #99: this meters ONLY the bridge-bound copy of the audio —
    // the speaker playback is never throttled or paused.)
    this._pacerQueue = [];
    this._pacerEngaged = false;
    this._pacerForwardedMs = 0;
    this._pacerNextAllowedWall = 0;
    this._pacerDrainTimer = null;
    this._pacerPendingEndOfAudio = false;
    this._pacerHeldMsTotal = 0;
    this._pacerMaxQueueDepth = 0;
    this._pacerChunkCount = 0;
    this._a2fKeepAliveIntervalMs = 25000;

    // Task #99: end-of-reply teardown state.
    //
    // `_a2fIdleTeardownRan` is the per-turn one-shot guard for the
    // shared idle teardown (graceful zero-blendshapes, idle animation,
    // end-session, reset-timeline, finalize debug). It is reached from
    // two paths that go through `_a2fForceIdleTeardown(reason)` so the
    // teardown is identical and can never run twice for the same turn:
    //   1. the COMPUTED-clock timer (`_armComputedTeardown`) — primary,
    //   2. the natural `src.onended` fullyIdle branch — backstop.
    // Reset to false at the start of every A2F session / new turn.
    //
    // `_computedTeardownTimer` is the handle for the computed-clock
    // teardown. It is re-armed in `_scheduleBuffer` to the exact wall
    // time the currently-scheduled audio is known to finish, and
    // cleared on every turn boundary via `_resetTimeline`.
    this._a2fIdleTeardownRan = false;
    this._computedTeardownTimer = null;

    // Task #99 (diagnostics): per-turn ledger heartbeat. The end-of-turn
    // publish depends on a teardown path firing; a barge-in, a fallback
    // greeting path, or a never-completing turn could all skip it and
    // leave us with zero data. The heartbeat POSTs the running ledger
    // every 2 s while a turn has audio, so we ALWAYS capture numbers.
    this._ledgerHeartbeatTimer = null;

    // Per-utterance JSON debug recorder (off by default — gated by
    // window.a2fTimingDebug / ?a2fTimingDebug=1). One instance per
    // RealtimeLLMToElevenLabs; holds at most one in-flight session.
    // Append methods are no-ops when the flag is off, so the cost
    // when disabled is one truthy check at each call site.
    this._a2fDebugRecorder = new A2FDebugRecorder();
    this._a2fDebugCharacterId = null;

    // ---- Sync gate (preroll) tunables ----
    // The gate releases as soon as EITHER threshold is met:
    //   • _a2fPrerollFrames buffered frames (~0.4s at 30fps), OR
    //   • _a2fPrerollMs of frame timecodes accumulated.
    // The hard ceiling _a2fGateMaxMs only fires when the bridge appears
    // truly dead (no frames at all). Whether the server streams frames
    // before EndOfAudio or only after is irrelevant — we just wait for
    // the buffer to fill, then start audio with frames already on hand.
    this._a2fPrerollFrames = 12;
    this._a2fPrerollMs = 200;
    this._a2fGateMaxMs = 30000;
    this._a2fGateArmedAtMs = null;

    // Task #30: extra "required lead" gate. The legacy preroll
    // thresholds (12 frames OR 200 ms of frame timecodes) release
    // audio as soon as a TINY pre-roll exists, which leaves the
    // queue dry shortly after audio starts and produces the
    // FRAME_QUEUE_LEAD_BELOW_* warnings the recorder is meant to
    // surface. The lead gate forces playback to wait until at least
    // `lastT >= a2fRequiredLeadMs` of frames are buffered, on top of
    // the existing thresholds (BOTH must be satisfied). Default
    // 1000 ms; overridable per-load via `?a2fRequiredLeadMs=NNN`,
    // per-construct via `opts.a2fRequiredLeadMs`, or at runtime via
    // `window.a2fRequiredLeadMs`. Set to 0 to disable (legacy
    // behaviour).
    this._a2fRequiredLeadMs = (() => {
      try {
        const u = new URL(
          typeof window !== "undefined" ? window.location.href : "http://x/",
        );
        if (u.searchParams.has("a2fRequiredLeadMs")) {
          const v = Number(u.searchParams.get("a2fRequiredLeadMs"));
          if (Number.isFinite(v) && v >= 0) return v;
        }
      } catch (_) {}
      if (Number.isFinite(opts && opts.a2fRequiredLeadMs)) {
        return Math.max(0, Number(opts.a2fRequiredLeadMs));
      }
      if (
        typeof window !== "undefined" &&
        Number.isFinite(window.a2fRequiredLeadMs)
      ) {
        return Math.max(0, Number(window.a2fRequiredLeadMs));
      }
      // Task #99 (math fix): the A2F pipeline takes ~1.3 s to turn audio
      // into lip frames (measured eoa_complete ~1.16–1.34 s). The old
      // 1000 ms lead was SHORTER than that latency, so the frame queue ran
      // dry almost immediately after playback started — which is what used
      // to trigger the (now-disabled) pause-on-underrun and stuttered the
      // start of every reply. With the forward-pacer feeding the bridge at
      // ~1× real-time, a CONSTANT start lead ≥ pipeline latency keeps
      // playback behind production for the entire clip, no pausing needed.
      // 1800 ms = ~1.3 s pipeline + margin (network + worst-case chunk).
      return 1800;
    })();

    // NOTE: A "lip-lock hold-on-underrun" subsystem once lived here. It
    // paused the speaker (`audioCtx.suspend()`) whenever A2F frames ran dry
    // so the mouth could stay welded to the audio. It was disabled and then
    // removed because it violates the core rule of this engine: the speaker
    // audio is NEVER paused — lip-sync conforms to the audio, not the other
    // way around. The start lead (`_a2fRequiredLeadMs` above) + forward-pacer
    // keep playback behind production for the whole clip, so no hold is ever
    // needed. Do NOT reintroduce any audioCtx.suspend()/pause-on-underrun.
    // See `.agents/memory/audio-conversation-playbook.md`.

    // Task #99 (tail fix): deterministic end-of-audio watchdog. See
    // `_a2fArmQuietEndWatchdog`. Ends the A2F session a short quiet window
    // after ElevenLabs stops sending audio for a closed turn, instead of
    // waiting on ElevenLabs' late/missing isFinal/audio_end marker.
    this._a2fQuietEndArmed = false;
    this._a2fQuietEndTimer = null;
    this._a2fQuietEndMs = 800;
    this._a2fComputedTeardownCtxEnd = 0;

    // Task #30: count of times `syncClock(0, wallStart)` was actually
    // invoked on the lipsync driver in this turn. Bumped at the call
    // site (initial schedule + watchdog re-call). The recorder fires
    // SYNC_CLOCK_CALLED_MORE_THAN_ONCE on the second increment.
    this._a2fSyncClockCallCount = 0;

    // ---- [A2F-TIMELINE] per-turn timestamps (frontend side) ----
    // Reset on every `_a2fArmSyncGate()`. Used to emit timeline log lines
    // matching the bridge's `[A2F-TIMELINE]` lines so the two sides can
    // be aligned by `gen=` to reconstruct the full audio→frame→playback
    // pipeline timing in real recordings.
    this._a2fTurnStartMs = null;
    this._a2fTurnFirstElevenChunkMs = null;
    this._a2fTurnFirstForwardMs = null;
    this._a2fTurnEndOfAudioMs = null;
    this._a2fTurnFirstHeaderMs = null;
    this._a2fTurnFirstFramesMs = null;
    this._a2fTurnGateReleaseMs = null;
    this._a2fTurnPlaybackStartMs = null;

    // Response Timeline — single source-of-truth for audio + A2F sync
    this._timeline = {
      armed: false,
      started: false,
      ctxStart: 0,
      wallStart: 0,
      nextCtxTime: 0,
    };
    this._timelineStartTimer = null;

    // Sync token: monotonic counter to invalidate old delayed callbacks
    this._syncToken = 0;
    this._a2fGateTimeoutId = null;
    this._a2fWatchdogId = null;

    // UI events
    this.events = new TinyEvents();

    // ===== Stop-button gating flags =====
    this.SToken = false; // true after first token received
    this.STokenAudio = false; // true after audio actually starts
    this.TSToken = false; // true after 2s of no new tokens (while SToken & STokenAudio are true)
    this._tokenIdleMs =
      opts.tokenIdleMs === undefined ? 2000 : Number(opts.tokenIdleMs); // default 2s
    this._tokenIdleTimer = null;
    this._lastTokenAt = 0;

    // ---------- Idle detection (for Spawn animation) ----------
    this.idleTimeoutMs =
      opts.idleTimeoutMs === undefined
        ? 15000 // 15s
        : Math.max(0, Number(opts.idleTimeoutMs));

    this._idleTimer = null;
    this._lastActivityAt =
      (typeof performance !== "undefined" && performance.now()) || Date.now();
    this._idleDetectionEnabled = !opts.disableIdleDetection;

    const g =
      (typeof window !== "undefined" && window) ||
      (typeof self !== "undefined" && self) ||
      null;
    if (
      g &&
      typeof g.addEventListener === "function" &&
      !opts.disableAutoShutdown
    ) {
      g.addEventListener("beforeunload", () =>
        this.shutdown({ viaPageUnload: true }),
      );
    }

    // Initialize idle detection timer
    this._resetIdleTimer();

    // ---- Per-chunk A2F timing (browser-side fields 14-17) ----
    // Map<chunkIndex, {
    //   chunkIndex,
    //   audioClockMsAtFirstFrame,        // null + reason if pre-audio-start
    //   audioClockMsAtFirstFrameReason,  // string|null
    //   frameQueueLeadMsAtFirstFrame,    // null + reason if pre-audio-start
    //   frameQueueLeadEverBelow300Ms,    // sticky bool
    //   frameQueueLeadEverBelow0Ms,      // sticky bool
    //   minFrameQueueLeadMs,             // smallest lead seen this chunk
    // }>
    // Cleared on session start / reset / cancel.
    this._a2fChunkTimings = new Map();

    // ---- Task #26: Per-utterance browser audio-pipeline counters ----
    // All fields are reset by `_a2fDebugBeginUtterance` and only ever
    // read/written when `this._a2fDebugRecorder?.isRecording` is true,
    // so they have zero cost when the debug flag is off. Naming the
    // state on `this` (rather than capturing in closures) keeps each
    // hook as a single guarded if-block — easier to reason about and
    // easy to remove in one spot if the schema changes later.
    this._a2fDebugElevenLabsCounter = 0;
    this._a2fDebugCumulativeAudioMs = 0;
    this._a2fDebugFramesMessageCounter = 0;
    this._a2fDebugLastQueuedFrameNormalizedMs = null;
    this._a2fDebugFramesQueuedTotal = 0;
    this._a2fDebugFrameQueueLeadEverBelow500 = false;
    this._a2fDebugFrameQueueLeadEverBelow300 = false;
    this._a2fDebugFrameQueueLeadEverBelow0 = false;
    this._a2fDebugAudioBufferCounter = 0;
    this._a2fDebugLastScheduledStartCtxSec = null;
    this._a2fDebugLastScheduledEndCtxSec = null;
    this._a2fDebugTotalQueuedAudioMs = 0;
    this._a2fDebugFirstScheduledPlaybackStartTime = null;
    this._a2fDebugPrevScheduledPlaybackStartTime = null;
    this._a2fDebugPlaybackStartChangedFired = false;
  }

  /* ============================================================
     Stop-button gating helpers
     ============================================================ */
  _emitStopVisibility(show) {
    this.events.emit("stop-visibility", { visible: !!show });
  }

  _resetStopGate() {
    this.SToken = false;
    this.STokenAudio = false;
    this.TSToken = false;
    this._a2fDoneReceived = false;
    if (this._tokenIdleTimer) {
      clearTimeout(this._tokenIdleTimer);
      this._tokenIdleTimer = null;
    }
    this._emitStopVisibility(false);
  }

  _onTokenArrived() {
    this.SToken = true;
    this._lastTokenAt = performance.now();
    this.TSToken = false; // new token breaks idle condition
    this._emitStopVisibility(false); // hide while tokens come
    if (this.STokenAudio) this._armTokenIdleTimer();
  }

  _onAudioStartedForGate() {
    if (!this.STokenAudio) {
      this.STokenAudio = true;
      if (this.SToken) this._armTokenIdleTimer();
    }
  }

  _stopGateAllMet() {
    if (!this.SToken || !this.STokenAudio) return false;
    return true;
  }

  _tryShowStop() {
    if (this._stopGateAllMet()) {
      this.TSToken = true;
      this._emitStopVisibility(true);
    }
  }

  _armTokenIdleTimer() {
    if (this._tokenIdleTimer) clearTimeout(this._tokenIdleTimer);
    this._tokenIdleTimer = setTimeout(() => {
      const idleFor = performance.now() - this._lastTokenAt;
      if (idleFor >= this._tokenIdleMs && this.SToken && this.STokenAudio) {
        this.TSToken = true;
        if (this._stopGateAllMet()) {
          this._emitStopVisibility(true);
        }
      } else {
        if (this.SToken && this.STokenAudio) this._armTokenIdleTimer();
      }
    }, this._tokenIdleMs);
  }

  /* ============================================================
     Idle detection → spawn animation when TTS is totally idle
     ============================================================ */

  _nowMs() {
    try {
      return performance.now();
    } catch {
      return Date.now();
    }
  }

  /**
   * Call whenever this TTS pipeline is doing *any* meaningful work:
   * - LLM tokens arriving
   * - audio data received
   * - new response started
   * - stream activity noted
   * - stop/cancel/explicit interactions
   */
  _noteIdleActivity(reason) {
    this._lastActivityAt = this._nowMs();
    if (this._idleDetectionEnabled && this.idleTimeoutMs > 0) {
      this._resetIdleTimer();
    }
    // Optional debug:
    // console.log("[Idle] Activity:", reason, "at", this._lastActivityAt);
  }

  _resetIdleTimer() {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
    if (!this._idleDetectionEnabled || this.idleTimeoutMs <= 0) return;

    this._idleTimer = setTimeout(
      () => this._onIdleTimeout(),
      this.idleTimeoutMs,
    );
  }

  _onIdleTimeout() {
    const now = this._nowMs();
    const idleFor = now - this._lastActivityAt;

    // If something happened recently (race with timer), just restart timer
    if (idleFor < this.idleTimeoutMs - 5) {
      this._resetIdleTimer();
      return;
    }

    // Fully idle means:
    // - no audio playing
    // - no queued audio
    // - stream is not active
    // - global TTSActive is false (avatar not speaking)
    const fullyIdle =
      !this.playing &&
      this.audioQueue.length === 0 &&
      !this._streamActive &&
      !TTSActive;

    if (!fullyIdle) {
      // Something is happening; don't trigger spawn, just restart timer
      this._resetIdleTimer();
      return;
    }

    // ✅ At this point, TTS has done nothing for at least idleTimeoutMs
    this._triggerIdleSpawn(idleFor);

    // After spawning once, keep detecting so we can spawn again after another idle period
    this._resetIdleTimer();
  }

  _triggerIdleSpawn(idleForMs) {
    console.log(
      `[Idle] No TTS activity for ${(idleForMs / 1000).toFixed(
        1,
      )}s → Spawn animation`,
    );

    // Fire an event so other parts of the app can react if they want
    this.events.emit("idle-spawn", { idleForMs });

    // Directly play the spawn animation on the Avatar (if available)
    try {
      if (typeof Avatar !== "undefined" && Avatar.play) {
        // If your clip is named "Spwan" instead of "spawn", change here:
        Avatar.play("spawn");
      }
    } catch (e) {
      console.warn("[Idle] Failed to play spawn animation:", e);
    }
  }

  /* ============================================================
     Hooks to flip the global speaking flag + emit UI events
     ============================================================ */
  _PlaySpeakAnimaiton() {
    if (!TTSActive) {
      _setTTSActive(true);
      this.events.emit("audio-start", { active: true });
      try {
        if (typeof Avatar !== "undefined") Avatar.play?.("speak");
      } catch {}
      console.log("[Anim] _PlaySpeakAnimaiton() → TTSActive =", TTSActive);
    }
  }

  _PlayIdleAnimaiton() {
    if (TTSActive) {
      _setTTSActive(false);
      this.events.emit("audio-end", { active: false });
      try {
        if (typeof Avatar !== "undefined") Avatar.play?.("idle");
      } catch {}
      console.log("[Anim] _PlayIdleAnimaiton() → TTSActive =", TTSActive);
    }
  }

  // -------------------- Public API --------------------

  on(type, fn) {
    this.events.on(type, fn);
  }
  off(type, fn) {
    this.events.off(type, fn);
  }

  bumpIdleForUserInteraction(reason = "user-interaction") {
    this._noteIdleActivity(reason);
  }

  async start() {
    await this._ensureConnected();
    await this._prewarmAudio();
    this._noteIdleActivity("start");
  }

  async beginNewResponse(fadeMs = 120) {
    console.log("[TTS] beginNewResponse(): preempting any current playback");

    // Task #99 (mid-sentence-drop fix): mark the stream active and clear the
    // "awaiting new turn" flag SYNCHRONOUSLY, before the first `await` below,
    // so a delta that arrives while this async begin is still in flight cannot
    // re-enter `beginNewResponse` via the auto-preempt block in handleLLMDelta.
    //
    // Why this matters: explicit-begin-then-stream callers (greeting,
    // tutor/historian, epub read-aloud — all the `ensureReadyForNextResponse`
    // sites) call beginNewResponse OUTSIDE handleLLMDelta, so `_streamActive`
    // is never set true by `_noteStreamActivity` before tokens flow. The first
    // LLM delta then hit `(_awaitingNewResponse || !_streamActive)` === true and
    // fired a SECOND, async beginNewResponse. Its teardown (_sendFlush +
    // _resetText + _clearAudioQueueOnly) landed mid-stream and wiped the opening
    // sentences that had already been fed to ElevenLabs — the intermittent
    // "first words of the reply are missing" symptom. Setting these flags up
    // front closes that re-entrancy window. (The auto-preempt path itself also
    // sets them; this is a no-op there.)
    this._streamActive = true;
    this._awaitingNewResponse = false;

    // This is a clear user/model interaction
    this._noteIdleActivity("beginNewResponse");

    // Task #99 (diagnostics): a new turn preempts the prior one. If the
    // prior turn never reached a teardown publish (barge-in / interrupt),
    // emit its final ledger now BEFORE the stats are reset below, so an
    // interrupted reply still leaves a record.
    try {
      if (
        !this._audioStatsPublished &&
        this._audioStats &&
        this._audioStats.received > 0
      ) {
        this._audioStatsPublished = true;
        this._publishAudioStats("preempt");
      }
    } catch (_) {}
    this._stopLedgerHeartbeat();

    // Task #29: arm the drop shield BEFORE the preempt window so any
    // in-flight old-turn audio chunks that arrive between our
    // `_sendFlush()` (which terminates the prior generation) and the
    // end of `_preemptStopAllAudio` are discarded. The shield is
    // cleared further below, synchronously, after the audio queues
    // are reset and the new epoch is in place. (The shield is also
    // re-armed in the stop/stopNow paths, but those are separate
    // entry points — beginNewResponse needs its own up-edge here.)
    this._droppingIncomingAudio = true;

    // Task #33: finalize any in-flight chunk-recorder session before the
    // epoch flips so the previous turn's chunks are saved as their own
    // session file. The new session is started below after the epoch bump.
    if (this._elevenChunkRecorder) {
      this._elevenChunkRecorder.stop({ reason: "beginNewResponse" });
    }

    // Task #21: drop any paced bytes from the prior turn before we
    // start a new one so they can't trickle into the new session.
    this._a2fResetPacer("beginNewResponse");

    // Task #99 (tail fix): a new turn supersedes any prior turn's pending
    // quiet-end watchdog so it can never fire into the new reply.
    this._a2fClearQuietEndWatchdog();

    // Stop current playback but don't leave flags stuck
    this._sendFlush();
    await this._preemptStopAllAudio(fadeMs);

    // Reset state for new response
    this.playing = false;
    this._audioEpoch++;
    this._hasBeganThisEpoch = false;
    this._primerSentThisEpoch = false;
    this._resetFirstChunkBuffer();
    if (this._elevenChunkRecorder) {
      this._elevenChunkRecorder.start({
        outputFormat: this.outputFormat,
        voiceId: this.voiceId,
        turnId: this._audioEpoch,
        wallStart: performance.now(),
        reason: "beginNewResponse",
      });
    }
    // Task #29: mark the first audio chunk of this new epoch so the
    // tiny-silent skip in _playLoop doesn't discard the genuine
    // opening chunk of a new turn (some short opening syllables
    // arrive as <20ms chunks).
    this._firstChunkOfEpochPending = true;
    // NOTE: Do NOT clear _userStoppedCurrentResponse here - it should only be cleared
    // when ChatController calls expectNewTurn() after sending the request
    this._textBuffer = ""; // Clear any pending text

    // Clear audio and text queues
    this._resetText();
    this._clearAudioQueueOnly();

    // Task #29: drop-shield handoff.
    //
    // The shield must stay TRUE across the preempt window (so any
    // leftover audio from the prior turn that arrives between
    // `_sendFlush()` above and the end of `_preemptStopAllAudio` is
    // discarded), and then be cleared HERE — synchronously, right
    // after the audio queues are cleared and the new epoch is set.
    //
    // Previously this was cleared inside `handleLLMDelta` on the
    // first delta. Because `handleLLMDelta` calls
    // `this.beginNewResponse().catch(() => {})` WITHOUT awaiting it,
    // the delta-handler's sync code raced ahead and cleared the
    // shield, then this async path arrived and re-armed it — leaving
    // the shield stuck TRUE until the *next* delta arrived. The audio
    // for the new turn's first phrase arrived inside that window and
    // got silently dropped, producing the "missing opening syllables"
    // symptom. Clearing here instead removes the race entirely.
    this._droppingIncomingAudio = false;

    // Task #99 (accounting): start this turn's audio ledger from zero.
    this._audioStats = this._freshAudioStats();
    this._audioStatsPublished = false;
    // Task #347 — new turn, fresh word-timing aggregation.
    try { this._wsAlignment.reset(); } catch (_) {}
    this._alignAudioMsSoFar = 0;

    // Task #99 (diagnostics): mark turn start (proves the WS streaming
    // engine is being used for this reply, vs the GreetingService blob
    // fallback which never touches this ledger) and start the heartbeat
    // so we capture running numbers even if no teardown publish fires.
    this._publishAudioStats("start");
    this._startLedgerHeartbeat();

    // Pause lip sync and zero mouth
    this._pauseLipSync();
    this._zeroLipSyncBlendshapes();

    // NOW idle (no audio is playing after preemption)
    this._PlayIdleAnimaiton();

    // reset stop-gating flags for new response
    this._resetStopGate();

    // reset timeline for new response
    this._resetTimeline();

    // Task #99: reset the end-of-reply one-shot at EVERY turn start, not
    // only in `_a2fSendStart`. Turns that never open an A2F session (A2F
    // disabled, bridge unavailable, fallback path) would otherwise leave
    // `_a2fIdleTeardownRan = true` from a prior turn and silently skip
    // teardown on this reply. `_resetTimeline()` above already cleared
    // the computed-teardown timer.
    this._a2fIdleTeardownRan = false;

    // End any existing A2F session so a fresh gRPC session starts for this response
    this._a2fEndSession();

    // Full A2F driver reset for new response
    if (this._a2fLipSyncDriver) {
      try {
        this._a2fLipSyncDriver.reset();
      } catch (_) {}
    }
    this._a2fDriverStarted = false;
    this._a2fFirstFrameReceived = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fBufferedFrames = [];
    this._a2fSyncGate = null;
    this._a2fSyncGateResolve = null;

    // Arm A2F sync gate NOW — before audio arrives, so _playLoop() will await it
    if (this._useA2FLipSync) {
      this._a2fArmSyncGate();
      this._v2log("[A2F] Sync gate pre-armed in beginNewResponse");
    }

    if (!this.wsOpen) {
      try {
        await this._ensureConnected();
      } catch (_) {}
    }

    // ✅ CRITICAL FIX: Ensure audio context is ready (especially for Safari)
    try {
      await this._ensureAudioContext();
    } catch (e) {
      console.warn(
        "[TTS] Failed to ensure audio context in beginNewResponse:",
        e,
      );
    }

    console.log(
      "[TTS] Ready for new response - drop shield cleared, awaiting first phrase",
    );
  }

  /**
   * Called by ChatController when streaming metadata is received.
   * Returns a turn ID that must be passed to handleLLMDelta for ownership verification.
   * This ensures residual deltas from old cancelled requests are blocked.
   * NOTE: We do NOT clear _userStoppedCurrentResponse here - it's cleared in handleLLMDelta
   * only when a delta with matching turn ID arrives.
   */
  expectNewTurn() {
    this._currentTurnId++;
    console.log(`[TTS] expectNewTurn() - starting turn ${this._currentTurnId}`);
    this._expectingNewTurn = true;
    // A2F debug recorder: open a fresh per-utterance record now that
    // the turn id is locked in. No-op when window.a2fTimingDebug is off.
    try {
      this._a2fDebugBeginUtterance();
    } catch (e) {
      try {
        console.warn("[A2FDebug] beginUtterance hook failed:", e);
      } catch (_) {}
    }
    // Do NOT clear _userStoppedCurrentResponse here - defer until validated delta arrives
    return this._currentTurnId;
  }

  /**
   * Optional: ChatController can stamp the active character id onto the
   * recorder so finalized JSON files include it in utteranceMetadata.
   * Safe no-op when the recorder isn't wired or the value is falsy.
   */
  setDebugCharacterId(characterId) {
    this._a2fDebugCharacterId = characterId || null;
  }

  // ============================================================
  // A2F debug recorder — lifecycle + config snapshot helpers
  // ============================================================

  /**
   * Build the config snapshot recorded into utteranceMetadata.config.
   * All values reflect the live runtime, not opts.* defaults, so the
   * file accurately captures *this* utterance's environment.
   */
  _a2fDebugBuildConfigSnapshot() {
    const fmt = this.outputFormat || "pcm_16000";
    // Parse "<codec>_<rate>" e.g. pcm_16000 → 16000.
    let sampleRate = 16000;
    const m = /_(\d+)$/.exec(fmt);
    if (m) sampleRate = Number(m[1]) || 16000;
    const bytesPerSample = 2; // 16-bit PCM
    const channels = 1;
    const bytesPerSecond = sampleRate * bytesPerSample * channels;

    // A2F bridge chunk size — keep the constants in sync with
    // _a2fForwardAudio. 1000ms / 32 KB at pcm_16000.
    const a2fChunkSizeMs = 1000;
    const a2fChunkSizeBytes = Math.round(
      (bytesPerSecond * a2fChunkSizeMs) / 1000,
    );
    const grpcSubChunkSizeBytes = 3200;

    const tau =
      this._a2fLipSyncDriver &&
      Number.isFinite(this._a2fLipSyncDriver._smoothTau)
        ? this._a2fLipSyncDriver._smoothTau
        : null;

    return {
      elevenLabsOutputFormat: fmt,
      sampleRate,
      bytesPerSample,
      channels,
      bytesPerSecond,
      a2fChunkSizeMs,
      a2fChunkSizeBytes,
      grpcSubChunkSizeBytes,
      smoothingTauMs: tau,
      audioPrerollMs: this._a2fPrerollMs != null ? this._a2fPrerollMs : null,
      // _a2fPrerollFrames is a frame count (default 12). The schema
      // field is *_Ms*, so convert via the A2F server's nominal 30 fps
      // emit rate. The gate fires on whichever of audio-ms or
      // blendshape-ms hits first, so reporting frames-as-frames here
      // would mislead downstream analysis (12 vs 200 looks like
      // blendshape preroll dominates; the truth is 12 frames ≈ 400 ms,
      // so audio's 200 ms threshold normally wins).
      blendshapePrerollMs:
        this._a2fPrerollFrames != null
          ? Math.round(this._a2fPrerollFrames * (1000 / 30))
          : null,
      blendshapePrerollFrames:
        this._a2fPrerollFrames != null ? this._a2fPrerollFrames : null,
      // Task #30: surface the playback gate value in the recorded
      // config so analysis tools can correlate `?a2fRequiredLeadMs=…`
      // overrides with observed FRAME_QUEUE_LEAD_BELOW_* warnings.
      a2fRequiredLeadMs: Number.isFinite(this._a2fRequiredLeadMs)
        ? this._a2fRequiredLeadMs
        : null,
      clockSource: "audioContext.currentTime",
    };
  }

  /**
   * Open a recorder session for the current turn. Cheap no-op when the
   * debug flag is off — the recorder itself snapshots and gates on the
   * flag, so we just have to call it.
   */
  _a2fDebugBeginUtterance() {
    if (!this._a2fDebugRecorder) return;

    // Task #26: reset per-utterance browser-pipeline counters
    // unconditionally (even when the debug flag is off) so that any
    // stale state from a prior turn cannot leak into a future
    // recording if the flag flips on mid-session. The cost is a
    // dozen primitive assignments — negligible.
    this._a2fDebugElevenLabsCounter = 0;
    this._a2fDebugCumulativeAudioMs = 0;
    this._a2fDebugFramesMessageCounter = 0;
    this._a2fDebugLastQueuedFrameNormalizedMs = null;
    this._a2fDebugFramesQueuedTotal = 0;
    this._a2fDebugFrameQueueLeadEverBelow500 = false;
    this._a2fDebugFrameQueueLeadEverBelow300 = false;
    this._a2fDebugFrameQueueLeadEverBelow0 = false;
    this._a2fDebugAudioBufferCounter = 0;
    this._a2fDebugLastScheduledStartCtxSec = null;
    this._a2fDebugLastScheduledEndCtxSec = null;
    this._a2fDebugTotalQueuedAudioMs = 0;
    this._a2fDebugFirstScheduledPlaybackStartTime = null;
    this._a2fDebugPrevScheduledPlaybackStartTime = null;
    this._a2fDebugPlaybackStartChangedFired = false;

    // Cheap pre-check so we don't even build the config snapshot when
    // the flag is off. The recorder repeats the check internally for
    // safety, but this saves the snapshot work in the hot path.
    if (!isA2FTimingDebugEnabled()) {
      // Still call begin so any stale prior session gets force-finalized
      // and the recorder's internal state stays clean.
      this._a2fDebugRecorder.beginUtterance({}, {});
      // Task #27: zero the driver-side render-loop counters even
      // when the flag is off so a flag-flip mid-session can never
      // pick up stale per-utterance state from a prior turn.
      try {
        if (
          this._a2fLipSyncDriver &&
          typeof this._a2fLipSyncDriver._a2fDebugBeginRenderUtterance ===
            "function"
        ) {
          this._a2fLipSyncDriver._a2fDebugBeginRenderUtterance();
        }
      } catch (_) {}
      return;
    }
    let sr = null;
    try {
      sr = this.audioCtx ? this.audioCtx.sampleRate : null;
    } catch (_) {}
    const meta = {
      utteranceId: String(this._currentTurnId),
      characterId: this._a2fDebugCharacterId || null,
      modelId: this.modelId || null,
      audioContextSampleRate: sr,
    };
    this._a2fDebugRecorder.beginUtterance(
      meta,
      this._a2fDebugBuildConfigSnapshot(),
    );
    // Task #27: reset render-loop counters in the driver so the
    // first sample for this turn starts at index 0 with empty
    // rolling buffers. Wrapped in try/catch — never break the turn
    // start because of a debug hook.
    try {
      if (
        this._a2fLipSyncDriver &&
        typeof this._a2fLipSyncDriver._a2fDebugBeginRenderUtterance ===
          "function"
      ) {
        this._a2fLipSyncDriver._a2fDebugBeginRenderUtterance();
      }
    } catch (e) {
      try {
        console.warn("[A2FDebug] driver render-loop reset hook failed:", e);
      } catch (_) {}
    }
  }

  /**
   * Finalize the current recorder session (writes JSON to disk via the
   * /api/debug/a2f-recorder endpoint). Safe no-op when nothing is open.
   */
  _a2fDebugFinalize(earlyTermination, viaPageUnload = false) {
    if (!this._a2fDebugRecorder) return;
    const tau =
      this._a2fLipSyncDriver &&
      Number.isFinite(this._a2fLipSyncDriver._smoothTau)
        ? this._a2fLipSyncDriver._smoothTau
        : null;
    // Task #21 — per-turn pacer summary. Always emit one log line
    // (even when the recorder isn't capturing) so the user can read
    // pacer behavior from the regular browser console. When the
    // recorder IS capturing, also poke the three fields into the
    // record's summary BEFORE finalize so they land in the saved JSON.
    try {
      console.log(
        `[A2F-PACER] enabled=${A2F_FORWARD_PACING_ENABLED} rate=${A2F_FORWARD_PACING_RATE} warmupMs=${A2F_FORWARD_PACING_WARMUP_MS} chunks=${this._pacerChunkCount} heldMsTotal=${Math.round(this._pacerHeldMsTotal)} maxQueueDepth=${this._pacerMaxQueueDepth}`,
      );
      if (this._a2fDebugRecorder.isRecording) {
        const rec = this._a2fDebugRecorder._record;
        if (rec && rec.summary) {
          rec.summary.pacingChunkCount = this._pacerChunkCount;
          rec.summary.pacingHeldMsTotal = Math.round(this._pacerHeldMsTotal);
          rec.summary.pacingMaxQueueDepth = this._pacerMaxQueueDepth;
        }
      }
    } catch (_) {}
    try {
      this._a2fDebugRecorder.finalize({
        earlyTermination: !!earlyTermination,
        smoothingTauMs: tau,
        viaPageUnload: !!viaPageUnload,
      });
    } catch (e) {
      try {
        console.warn("[A2FDebug] finalize hook failed:", e);
      } catch (_) {}
    }
  }

  // =====================================================================
  // Task #26 — browser audio-pipeline observation hooks.
  //
  // Each helper below is a single guarded if-block that does NOTHING
  // when the recorder isn't actively recording (debug flag off, or no
  // open utterance). They never throw — every body is wrapped in
  // try/catch so a recorder bug can't break the lipsync hot path.
  //
  // The three hook helpers correspond to the three arrays they fill:
  //
  //   _a2fDebugRecordElevenLabsChunk      → elevenLabsChunks[]
  //   _a2fDebugRecordA2FFrameMessage      → a2fBrowserReceiveEvents[]
  //   _a2fDebugRecordAudioBufferSchedule  → audioSchedulingEvents[]
  //
  // Plus the shared deduped-warning emitter `_a2fDebugFireWarningOnce`.
  // =====================================================================

  /**
   * Forward a typed warning to the recorder. The recorder dedupes by
   * `type` (first-wins per session), so calling this on every event
   * is safe — only the first occurrence is stored.
   */
  _a2fDebugFireWarningOnce(type, details) {
    if (!this._a2fDebugRecorder?.isRecording) return;
    try {
      this._a2fDebugRecorder.addWarning(type, details || {});
    } catch (_) {
      // Pure observability — never let a warning emit failure
      // surface to the audio path.
    }
  }

  /**
   * Worst-case-wins variant. The recorder's `upsertWarning` keeps the
   * sample with the largest deviation (per `isWorse`). Used for
   * FRAME_QUEUE_LEAD_BELOW_*, FRAME_TIMELINE_GAP, AUDIO_BUFFER_GAP,
   * etc. so a single representative sample is kept rather than the
   * first occurrence.
   */
  _a2fDebugUpsertWarning(type, details, isWorse) {
    if (!this._a2fDebugRecorder?.isRecording) return;
    try {
      this._a2fDebugRecorder.upsertWarning(type, details || {}, isWorse);
    } catch (_) {
      // Pure observability.
    }
  }

  /**
   * Task #30: emit SYNC_CLOCK_CALLED_MORE_THAN_ONCE the second time
   * `_a2fSyncClockCallCount` ticks past 1 in a single turn. The first
   * call (initial schedule) is the legitimate one — only the second
   * (typically watchdog-fired) deserves a warning, because that's the
   * one that re-anchors timing mid-playback. Caller passes the
   * call-site name for diagnosis.
   */
  _a2fDebugMaybeWarnSyncClockDup(callSite, wallStart) {
    if (!this._a2fDebugRecorder?.isRecording) return;
    if ((this._a2fSyncClockCallCount || 0) <= 1) return;
    this._a2fDebugFireWarningOnce("SYNC_CLOCK_CALLED_MORE_THAN_ONCE", {
      totalCalls: this._a2fSyncClockCallCount,
      lastCallSite: callSite,
      lastWallStart: wallStart,
      atMonoMs: performance.now(),
    });
  }

  /**
   * elevenLabsChunks[] — one entry per audio chunk received from
   * ElevenLabs over the WS, fired from the binary / blob / base64
   * receive sites in `_handleSocketMessage`.
   *
   * Per the spec: PCM 16-bit mono is the format the bridge expects.
   * Task #30 — duration is computed via the canonical
   * `pcm16Mono16000BytesToMs` helper. The previous implementation
   * preferred `audioCtx.sampleRate` (typically 48000), which under-
   * counted PCM_16000 durations by 3x and produced
   * `cumulativeAudioMs ≈ totalQueuedAudioMs / 3` divergence in the
   * recorded JSON. ElevenLabs PCM_16000 is fixed at 16 kHz on the
   * wire regardless of the destination AudioContext rate.
   */
  _a2fDebugRecordElevenLabsChunk(byteLength, encoding, queuedToAudio) {
    if (!this._a2fDebugRecorder?.isRecording) return;
    try {
      const estimatedDurationMs = pcm16Mono16000BytesToMs(byteLength);
      this._a2fDebugCumulativeAudioMs += estimatedDurationMs;
      const idx = this._a2fDebugElevenLabsCounter++;
      this._a2fDebugRecorder.addElevenLabsChunk({
        chunkIndex: idx,
        receivedAtMonoMs: performance.now(),
        encoding: encoding || "unknown",
        wasBinary: encoding !== "base64",
        byteLength: Number(byteLength) || 0,
        estimatedDurationMs,
        cumulativeAudioMsAfterReceive: this._a2fDebugCumulativeAudioMs,
        queuedToAudio: !!queuedToAudio,
        // `_a2fForwardAudio` runs unconditionally at all three receive
        // sites before this hook, so this is always true today. Kept
        // as an explicit field so future early-drop logic can flip it.
        forwardedToA2FBridge: true,
      });
    } catch (e) {
      try {
        console.warn("[A2FDebug] eleven-chunk hook failed:", e);
      } catch (_) {}
    }
  }

  /**
   * a2fBrowserReceiveEvents[] — one entry per `a2f_frames` WS message.
   * Called from the `a2f_frames` handler AFTER frames have been
   * normalized (so `time_ms` lives in the same "first frame = 0" space
   * as the driver's `_frameQueue`) and BEFORE they are enqueued, so
   * we can compute monotonic / gap / overlap against the previous
   * batch's last frame.
   *
   * `normalized` is the array of frames AFTER browser normalization;
   * `t0` is `_a2fFirstFrameTimeMs` for context only.
   */
  _a2fDebugRecordA2FFrameMessage(rawFrames, normalized, msg) {
    if (!this._a2fDebugRecorder?.isRecording) return;
    if (!normalized || normalized.length === 0) return;
    try {
      const messageIndex = this._a2fDebugFramesMessageCounter++;

      const firstNormMs = Number(normalized[0]?.time_ms) || 0;
      const lastNormMs =
        Number(normalized[normalized.length - 1]?.time_ms) || 0;

      // Compare against the last frame we observed in any prior
      // a2f_frames message of this turn. Null on the first batch.
      const prevLastMs = this._a2fDebugLastQueuedFrameNormalizedMs;

      let gapFromPreviousMessageMs = null;
      let overlapFromPreviousMessageMs = null;
      let monotonic = true;
      if (Number.isFinite(prevLastMs)) {
        const delta = firstNormMs - prevLastMs;
        if (delta >= 0) {
          gapFromPreviousMessageMs = delta;
          overlapFromPreviousMessageMs = 0;
        } else {
          gapFromPreviousMessageMs = 0;
          overlapFromPreviousMessageMs = -delta;
          monotonic = false;
        }
        // Strict-monotonic check INSIDE the batch too.
        let prev = prevLastMs;
        for (let i = 0; i < normalized.length; i++) {
          const t = Number(normalized[i]?.time_ms) || 0;
          if (!(t > prev)) {
            monotonic = false;
            break;
          }
          prev = t;
        }
      }

      // Best-effort frame queue stats AFTER this batch is enqueued.
      this._a2fDebugFramesQueuedTotal += normalized.length;
      let frameQueueLength = null;
      let frameQueueLengthSource = null;
      let earliestFrameInQueueMs = null;
      let latestFrameInQueueMs = null;
      if (this._a2fDriverStarted && this._a2fLipSyncDriver?._frameQueue) {
        const dq = this._a2fLipSyncDriver._frameQueue;
        frameQueueLength = dq.length + normalized.length;
        frameQueueLengthSource = "driver_queue_after_enqueue";
        // Earliest is the queue head (or this batch's first frame
        // if the driver queue is empty — i.e. everything pre this
        // batch has already been consumed).
        earliestFrameInQueueMs =
          dq.length > 0 ? Number(dq[0]?.time_ms) || 0 : firstNormMs;
        latestFrameInQueueMs = lastNormMs;
      } else {
        const buf = this._a2fBufferedFrames || [];
        frameQueueLength = buf.length + normalized.length;
        frameQueueLengthSource = "preroll_buffer_after_enqueue";
        earliestFrameInQueueMs =
          buf.length > 0 ? Number(buf[0]?.time_ms) || 0 : firstNormMs;
        latestFrameInQueueMs = lastNormMs;
      }

      // Lead = how far ahead of the audio clock the latest frame is.
      // Null until audio actually starts (clock returns null).
      const clock = this._a2fCurrentAudioClockMs();
      const currentAudioTimeMs = clock?.value ?? null;
      const currentAudioTimeReason = clock?.reason ?? null;
      let frameQueueLeadMs = null;
      if (Number.isFinite(currentAudioTimeMs)) {
        frameQueueLeadMs = lastNormMs - currentAudioTimeMs;
      }

      const ctx = this.audioCtx;
      const currentAudioContextTime =
        ctx && ctx.state !== "closed" ? ctx.currentTime : null;
      const playbackStartTime = this._timeline?.wallStart || null;

      const receivedAtMonoMs = performance.now();

      // Task #41 — capture the RAW frames (bridge-forwarded, no
      // browser-side normalization, smoothing, or clamping applied)
      // into the recorder's sibling raw-frame buffer. This is the
      // verification surface for "are mouth/jaw values being amplified
      // between server and avatar?" — the saved file shows the exact
      // {shapeName: value} maps the WS delivered. The recorder caps
      // and truncates internally; this call is a cheap append.
      try {
        this._a2fDebugRecorder.addRawA2FFrames({
          messageIndex,
          chunkIndex: typeof msg?.chunk_idx === "number" ? msg.chunk_idx : null,
          receivedAtMonoMs,
          frames: rawFrames,
        });
      } catch (_) {
        // Pure observability — never let raw-frame capture break the
        // lipsync hot path.
      }

      this._a2fDebugRecorder.addA2FBrowserReceiveEvent({
        messageIndex,
        receivedAtMonoMs,
        // Spec name is `frameCount`; the summary derivation reads
        // it directly. (Earlier draft used `framesInBatch`.)
        frameCount: normalized.length,
        chunkIndex: typeof msg?.chunk_idx === "number" ? msg.chunk_idx : null,
        firstFrameTimeMs: firstNormMs,
        lastFrameTimeMs: lastNormMs,
        gapFromPreviousMessageMs,
        overlapFromPreviousMessageMs,
        monotonic,
        frameQueueLength,
        frameQueueLengthSource,
        frameQueueLeadMs,
        currentAudioTimeMs,
        currentAudioTimeReason,
        currentAudioContextTime,
        playbackStartTime,
        earliestFrameInQueueMs,
        latestFrameInQueueMs,
      });

      // ---- Worst-case-wins warnings (Task #30) ----
      // The recorder dedup keeps one entry per type, but `upsertWarning`
      // with an `isWorse` predicate replaces the locked-in payload
      // whenever a later sample is more diagnostic. This way the saved
      // JSON shows the *worst* observation for the utterance, not the
      // first crossing.

      // Frame timeline integrity — gap/overlap require a prior
      // batch to compare against.
      if (Number.isFinite(prevLastMs)) {
        if (gapFromPreviousMessageMs > 50) {
          this._a2fDebugUpsertWarning(
            "FRAME_TIMELINE_GAP",
            {
              messageIndex,
              gapMs: gapFromPreviousMessageMs,
              prevLastFrameTimeMs: prevLastMs,
              firstFrameTimeMs: firstNormMs,
            },
            (prev, next) =>
              (Number(next.gapMs) || 0) > (Number(prev.gapMs) || 0),
          );
        }
        if (overlapFromPreviousMessageMs > 50) {
          this._a2fDebugUpsertWarning(
            "FRAME_TIMELINE_OVERLAP",
            {
              messageIndex,
              overlapMs: overlapFromPreviousMessageMs,
              prevLastFrameTimeMs: prevLastMs,
              firstFrameTimeMs: firstNormMs,
            },
            (prev, next) =>
              (Number(next.overlapMs) || 0) > (Number(prev.overlapMs) || 0),
          );
        }
      }

      // "Restarted at zero" — the bridge stopped applying its
      // time_code_offset, so a batch shows up near 0 even though
      // we're already deep into playback. Independent of whether
      // a prior batch was observed (the very first inbound batch
      // could in principle qualify if an earlier batch from this
      // turn was already consumed past the audio clock).
      if (
        firstNormMs < 50 &&
        Number.isFinite(currentAudioTimeMs) &&
        currentAudioTimeMs > 200
      ) {
        this._a2fDebugUpsertWarning(
          "FRAME_TIMELINE_RESTARTED_AT_ZERO",
          {
            messageIndex,
            firstFrameTimeMs: firstNormMs,
            currentAudioTimeMs,
          },
          // Worst case = audio clock is furthest past the restart point.
          (prev, next) =>
            (Number(next.currentAudioTimeMs) || 0) >
            (Number(prev.currentAudioTimeMs) || 0),
        );
      }

      // Task #30 — A2F_FRAMES_ARRIVED_AFTER_AUDIO_ALREADY_PASSED: the
      // entire batch's last frame timestamp is BEHIND the current audio
      // clock. Every blendshape in this batch is too late to ever be
      // applied — the renderer will skip past them as they're enqueued.
      // This is distinct from FRAME_TIMELINE_RESTARTED_AT_ZERO (which
      // catches the first-frame-near-zero case); this one catches the
      // arrived-too-late case for any batch whose end is in the past.
      // Worst case = batch lateness is largest (i.e. audio is furthest
      // ahead of the latest frame in the batch).
      if (
        Number.isFinite(currentAudioTimeMs) &&
        Number.isFinite(lastNormMs) &&
        currentAudioTimeMs > lastNormMs &&
        currentAudioTimeMs > 200
      ) {
        const latenessMs = currentAudioTimeMs - lastNormMs;
        this._a2fDebugUpsertWarning(
          "A2F_FRAMES_ARRIVED_AFTER_AUDIO_ALREADY_PASSED",
          {
            messageIndex,
            currentAudioTimeMs,
            lastFrameTimeMs: lastNormMs,
            firstFrameTimeMs: firstNormMs,
            latenessMs,
            framesInBatch: normalized.length,
          },
          (prev, next) =>
            (Number(next.latenessMs) || 0) > (Number(prev.latenessMs) || 0),
        );
      }

      // Frame queue lead alarms — only meaningful once audio has
      // started AND we have at least one frame in flight. Worst case
      // = lead is most negative (i.e. queue is furthest behind audio).
      if (Number.isFinite(frameQueueLeadMs)) {
        const leadIsWorse = (prev, next) =>
          (Number(next.leadMs) || 0) < (Number(prev.leadMs) || 0);
        if (frameQueueLeadMs < 500) {
          this._a2fDebugUpsertWarning(
            "FRAME_QUEUE_LEAD_BELOW_500",
            {
              messageIndex,
              leadMs: frameQueueLeadMs,
              currentAudioTimeMs,
              latestFrameTimeMs: lastNormMs,
            },
            leadIsWorse,
          );
        }
        if (frameQueueLeadMs < 300) {
          this._a2fDebugUpsertWarning(
            "FRAME_QUEUE_LEAD_BELOW_300",
            {
              messageIndex,
              leadMs: frameQueueLeadMs,
              currentAudioTimeMs,
              latestFrameTimeMs: lastNormMs,
            },
            leadIsWorse,
          );
        }
        if (frameQueueLeadMs < 0) {
          this._a2fDebugUpsertWarning(
            "FRAME_QUEUE_LEAD_BELOW_0",
            {
              messageIndex,
              leadMs: frameQueueLeadMs,
              currentAudioTimeMs,
              latestFrameTimeMs: lastNormMs,
            },
            leadIsWorse,
          );
        }
      }

      // Update the "last queued frame" pointer LAST so any earlier
      // throw above leaves it unchanged (next call still has a
      // sensible baseline).
      this._a2fDebugLastQueuedFrameNormalizedMs = lastNormMs;
    } catch (e) {
      try {
        console.warn("[A2FDebug] a2f-frames hook failed:", e);
      } catch (_) {}
    }
  }

  /**
   * audioSchedulingEvents[] — one entry per AudioContext buffer
   * scheduled via `_scheduleBuffer`. Called AFTER `src.start(startAt)`
   * and `nextCtxTime` update so we can record the actual scheduled
   * window and detect gaps between contiguous buffers.
   *
   * `firstScheduleOfTurn` indicates whether this call is the one that
   * armed `_timeline` (so we know to capture playbackStartTime as the
   * baseline for PLAYBACK_START_TIME_CHANGED).
   */
  _a2fDebugRecordAudioBufferSchedule(
    audioBuf,
    startAt,
    firstScheduleOfTurn,
    sourcePcmBytes,
  ) {
    if (!this._a2fDebugRecorder?.isRecording) return;
    try {
      const audioBufferIndex = this._a2fDebugAudioBufferCounter++;
      // Task #30: NOTE — sampleRate here reads `audioBuf.sampleRate`
      // (the AudioBuffer's own rate, set at createBuffer time, not
      // audioCtx.sampleRate). For PCM_16000 we explicitly created the
      // buffer at 16000 Hz, so audioBuf.duration is the truthful
      // duration. The `audioCtx.sampleRate` fallback only applies
      // when `audioBuf` itself is missing — a safety net, not a
      // primary calculation path.
      const sampleRate = audioBuf?.sampleRate || this.audioCtx?.sampleRate || 0;
      const durationSec =
        audioBuf?.duration ||
        (sampleRate > 0 ? (audioBuf?.length || 0) / sampleRate : 0);
      const audioBufferDurationMs = durationSec * 1000;

      // Task #30: AUDIO_DURATION_MISMATCH — for PCM_16000 we know the
      // canonical duration from the source byte count. If the
      // AudioBuffer's reported duration disagrees by >50 ms, the
      // decode path got the sample rate wrong somewhere (regression
      // detector).
      if (Number.isFinite(sourcePcmBytes) && sourcePcmBytes > 0) {
        const sourcePcmDurationMs = pcm16Mono16000BytesToMs(sourcePcmBytes);
        const deltaMs = Math.abs(audioBufferDurationMs - sourcePcmDurationMs);
        if (deltaMs > 50) {
          // Task #30: worst-case-wins on the largest abs delta.
          this._a2fDebugUpsertWarning(
            "AUDIO_DURATION_MISMATCH",
            {
              audioBufferIndex,
              sourcePcmBytes,
              sourcePcmDurationMs,
              audioBufferDurationMs,
              audioBufferSampleRate: audioBuf?.sampleRate || null,
              audioContextSampleRate: this.audioCtx?.sampleRate || null,
              deltaMs,
            },
            (prev, next) =>
              (Number(next.deltaMs) || 0) > (Number(prev.deltaMs) || 0),
          );
        }
      }

      const audioBufferStartAudioMs = this._a2fDebugTotalQueuedAudioMs;
      const audioBufferEndAudioMs =
        audioBufferStartAudioMs + audioBufferDurationMs;

      const ctx = this.audioCtx;
      const audioContextCurrentTime =
        ctx && ctx.state !== "closed" ? ctx.currentTime : null;

      // Gap is measured against the *previous* buffer's scheduled
      // end in the AudioContext timeline (seconds). >5ms is the
      // tolerance for FP jitter; the summary derivation uses the
      // same threshold for `audioSchedulingGapCount`.
      let gapFromPreviousScheduledBufferMs = null;
      if (Number.isFinite(this._a2fDebugLastScheduledEndCtxSec)) {
        gapFromPreviousScheduledBufferMs =
          (startAt - this._a2fDebugLastScheduledEndCtxSec) * 1000;
      }

      const playbackStartTime = this._timeline?.wallStart || null;

      // PLAYBACK_START_TIME_CHANGED: per-step semantic — true only when
      // wallStart was reassigned at THIS scheduling step (compared to
      // the immediately previous step's value). Capture the original
      // baseline on the first scheduling event of the turn so the
      // warning details can show original vs new.
      let playbackStartTimeChanged = false;
      if (
        firstScheduleOfTurn ||
        this._a2fDebugFirstScheduledPlaybackStartTime == null
      ) {
        this._a2fDebugFirstScheduledPlaybackStartTime = playbackStartTime;
      } else if (
        playbackStartTime !== this._a2fDebugPrevScheduledPlaybackStartTime
      ) {
        playbackStartTimeChanged = true;
        // Task #30: worst-case-wins on the absolute drift from the
        // original playbackStartTime so the locked-in payload reflects
        // the LARGEST observed shift, not just the first.
        const deltaMs =
          Number.isFinite(playbackStartTime) &&
          Number.isFinite(this._a2fDebugFirstScheduledPlaybackStartTime)
            ? playbackStartTime - this._a2fDebugFirstScheduledPlaybackStartTime
            : null;
        this._a2fDebugUpsertWarning(
          "PLAYBACK_START_TIME_CHANGED",
          {
            audioBufferIndex,
            originalPlaybackStartTime:
              this._a2fDebugFirstScheduledPlaybackStartTime,
            previousPlaybackStartTime:
              this._a2fDebugPrevScheduledPlaybackStartTime,
            newPlaybackStartTime: playbackStartTime,
            deltaMs,
          },
          (prev, next) =>
            Math.abs(Number(next.deltaMs) || 0) >
            Math.abs(Number(prev.deltaMs) || 0),
        );
      }
      this._a2fDebugPrevScheduledPlaybackStartTime = playbackStartTime;

      // syncClock(0, wallStart) is invoked exactly once on the first
      // buffer of the turn (line where we set `_a2fDriverStarted =
      // true`). The watchdog 250ms re-call uses the SAME wallStart,
      // so it doesn't correspond to a new scheduling event.
      const syncClockCalled = !!firstScheduleOfTurn;
      const streamTimeMsPassedToSyncClock = syncClockCalled ? 0 : null;
      const wallNowMsPassedToSyncClock = syncClockCalled
        ? playbackStartTime
        : null;

      // totalPlayedAudioMs = how much audio has actually elapsed,
      // capped at totalQueuedAudio so we never report "played > queued".
      let totalPlayedAudioMs = 0;
      if (
        ctx &&
        ctx.state !== "closed" &&
        Number.isFinite(this._timeline?.ctxStart)
      ) {
        const elapsed = (ctx.currentTime - this._timeline.ctxStart) * 1000;
        totalPlayedAudioMs = Math.max(
          0,
          Math.min(elapsed, audioBufferEndAudioMs),
        );
      }

      this._a2fDebugRecorder.addAudioSchedulingEvent({
        audioBufferIndex,
        scheduledAtMonoMs: performance.now(),
        audioBufferDurationMs,
        audioBufferStartAudioMs,
        audioBufferEndAudioMs,
        scheduledStartTime: startAt,
        audioContextCurrentTimeAtSchedule: audioContextCurrentTime,
        playbackStartTime,
        playbackStartTimeChanged,
        gapFromPreviousScheduledBufferMs,
        totalQueuedAudioMs: audioBufferEndAudioMs,
        totalPlayedAudioMs,
        syncClockCalled,
        streamTimeMsPassedToSyncClock,
        wallNowMsPassedToSyncClock,
      });

      // AUDIO_BUFFER_GAP — same 5ms tolerance as the summary
      // derivation in A2FDebugRecorder._computeSummary.
      if (
        Number.isFinite(gapFromPreviousScheduledBufferMs) &&
        gapFromPreviousScheduledBufferMs > 5
      ) {
        // Task #30: worst-case-wins on the largest scheduling gap so a
        // brief 6ms hiccup at buffer 1 isn't preserved over a true 200ms
        // gap at buffer 7.
        this._a2fDebugUpsertWarning(
          "AUDIO_BUFFER_GAP",
          {
            audioBufferIndex,
            gapMs: gapFromPreviousScheduledBufferMs,
            previousEndCtxSec: this._a2fDebugLastScheduledEndCtxSec,
            scheduledStartCtxSec: startAt,
          },
          (prev, next) => (Number(next.gapMs) || 0) > (Number(prev.gapMs) || 0),
        );
      }

      // Update bookkeeping LAST so an earlier throw leaves the
      // baselines unchanged.
      this._a2fDebugLastScheduledStartCtxSec = startAt;
      this._a2fDebugLastScheduledEndCtxSec = startAt + durationSec;
      this._a2fDebugTotalQueuedAudioMs = audioBufferEndAudioMs;
    } catch (e) {
      try {
        console.warn("[A2FDebug] schedule hook failed:", e);
      } catch (_) {}
    }
  }

  /**
   * Called by intentional speech methods (speakDirectly, speakDiagnosticQuestion) before streaming.
   * Clears the stop guard so intentional speech can play without a turn ID.
   */
  clearStopGuard() {
    console.log("[TTS] clearStopGuard() - allowing intentional speech");
    this._userStoppedCurrentResponse = false;
  }

  /**
   * @param {string} delta - Text delta from LLM
   * @param {number} [turnId] - Optional turn ID for ownership verification.
   *   If provided, delta is only accepted if it matches the current turn.
   *   If not provided (legacy calls), delta is accepted based on _userStoppedCurrentResponse flag.
   */
  handleLLMDelta(delta, turnId = null) {
    if (!delta || !delta.trim()) return;

    // Turn ID verification: if a turnId is provided, only accept if it matches current turn
    if (turnId !== null && turnId !== this._currentTurnId) {
      console.log(
        `[TTS] Ignoring delta from old turn ${turnId} (current turn is ${this._currentTurnId})`,
      );
      return;
    }

    // Valid turn ID matched - clear stopped flag now that we have verified new turn delta
    if (
      turnId !== null &&
      turnId === this._currentTurnId &&
      this._userStoppedCurrentResponse
    ) {
      console.log(
        `[TTS] Valid delta for turn ${turnId} - clearing stopped flag`,
      );
      this._userStoppedCurrentResponse = false;
    }

    // Legacy check for calls without turnId - must still respect stop guard
    // Intentional speech callers should call clearStopGuard() before streaming
    if (turnId === null && this._userStoppedCurrentResponse) {
      console.log("[TTS] Ignoring delta - no turn ID and stop guard active");
      return;
    }

    // If we were expecting a new turn, mark it as started
    if (this._expectingNewTurn) {
      console.log("[TTS] First delta of expected new turn received");
      this._expectingNewTurn = false;
    }

    // Any incoming text token = active conversation
    this._noteIdleActivity("llm-delta");

    // token arrived → mark immediately
    this._onTokenArrived();

    if (
      this.autoPreempt &&
      (this._awaitingNewResponse || !this._streamActive)
    ) {
      this.beginNewResponse().catch(() => {});
      this._awaitingNewResponse = false;
    }

    this._noteStreamActivity();

    // Task #29: the drop shield is no longer cleared here. It's
    // cleared synchronously inside `beginNewResponse` once the audio
    // queues are flushed and the new epoch is in place, which avoids
    // the async race that previously left it stuck TRUE for the first
    // audio chunks of the new turn.
    if (!this._hasBeganThisEpoch) {
      this._hasBeganThisEpoch = true;
    }

    this.buf += delta;

    // Task #99: drain every COMPLETE sentence/clause currently buffered.
    // `_takePhrase` returns "" when only a partial sentence remains, so we
    // never emit a mid-word fragment and never rely on a wall-clock
    // timeout. The remainder stays in `this.buf` until more deltas (or
    // `flushAndClose`) complete it.
    let phrase;
    while ((phrase = this._takePhrase()) !== "") {
      console.log("[TTS] Sending phrase to ElevenLabs:", phrase);
      this._enqueuePhrase(phrase);
      this._emittedAnyPhrase = true;
    }
  }

  flushAndClose() {
    if (this.buf.trim()) {
      const phrase = this.buf.trim();
      this.buf = "";
      this._emittedAnyPhrase = true;
      console.log("[TTS] Flushing tail phrase:", phrase);
      this._enqueuePhrase(phrase);
    }

    // Task #33: finalize the raw-chunk recorder at the end of a turn.
    if (this._elevenChunkRecorder) {
      this._elevenChunkRecorder.stop({ reason: "flushAndClose" });
    }

    this._awaitingNewResponse = true;
    this._streamActive = false;
    this._clearStreamIdleTimer();

    // Task #99 (tail fix): the LLM stream is now closed, so arm the
    // quiet-end watchdog. Once ElevenLabs stops sending audio for this
    // turn, it sends the A2F `end` deterministically — instead of waiting
    // on ElevenLabs' late/missing isFinal/audio_end marker, which left the
    // bridge's final partial slice (and the last word's lip frames)
    // stranded seconds after the audio finished. Mouth-close itself still
    // runs on the computed-clock teardown re-armed per scheduled buffer.
    this._a2fArmQuietEndWatchdog();

    if (this.persistentWs) {
      console.log("[TTS] Persistent mode: sending flush (WS stays open)");
      this._enqueueFlush();
      return;
    }

    console.log("[TTS] Closing WS gracefully (after flush)...");
    this.closeAfterDrain = true;
    this._enqueueFlush();
    this._attemptCloseIfDrained();
  }

  cancel() {
    console.log("[TTS] Canceling playback");

    this._noteIdleActivity("cancel");

    // Task #99: drop any pending computed-clock teardown for this turn.
    this._clearComputedTeardown();
    this._a2fClearQuietEndWatchdog();

    // A2F debug recorder: barge-in / user cancel — finalize current
    // session as early termination so we still get a diagnostic file.
    this._a2fDebugFinalize(true);

    this._userCanceled = true;

    if (!this.persistentWs) this._hardClose();

    this._resetFirstChunkBuffer();
    this._resetAudio();
    this._resetText();
    this._zeroLipSyncBlendshapes();

    // Drop any per-chunk timing records that never received a matching
    // a2f_chunk_summary (chunkIndex namespace resets with the next
    // bridge session anyway, but keeping these around would leak).
    // Also cancel any deferred [A2F_CHUNK_TIMING] emit timers so a
    // canceled turn doesn't print stale records into the next one.
    if (this._a2fChunkTimings && this._a2fChunkTimings.size) {
      for (const r of this._a2fChunkTimings.values()) {
        if (r && r._pendingEmitTimer) {
          clearTimeout(r._pendingEmitTimer);
          r._pendingEmitTimer = null;
        }
      }
      this._a2fChunkTimings.clear();
    }

    // NOW idle
    this._PlayIdleAnimaiton();

    // reset stop gate
    this._resetStopGate();

    // reset timeline
    this._resetTimeline();

    this._userCanceled = false;
    this._awaitingNewResponse = true;
    this._streamActive = false;
    this._clearStreamIdleTimer();
  }

  /* ============================================================
     A2F Bridge (NVIDIA Audio2Face-3D) — forwarding layer
     ============================================================ */

  /**
   * Verbose-gated log for A2F wiring. Only emits when window.a2fVerbose is
   * truthy. Cheap branch-and-skip when off.
   */
  _v2log(...args) {
    if (typeof window !== "undefined" && window.a2fVerbose) {
      console.log(...args);
    }
  }

  async _a2fConnect() {
    if (!this._a2fEnabled) {
      this._v2log("[A2F] Bridge disabled, skipping connect");
      return;
    }
    if (this._a2fWsOpen) {
      this._v2log("[A2F] Bridge already connected, skipping");
      return;
    }
    if (this._a2fConnecting) {
      this._v2log("[A2F] Bridge connection already in progress");
      return;
    }
    this._a2fConnecting = true;
    try {
      const proto = location.protocol === "https:" ? "wss:" : "ws:";
      let url = `${proto}//${location.host}/a2f-bridge`;
      try {
        const user = window.firebaseAuth?.currentUser;
        if (user) {
          const token = await user.getIdToken();
          if (token) url += `?token=${encodeURIComponent(token)}`;
        }
      } catch (_) {}
      this._v2log("[A2F] Connecting to bridge:", url.split("?")[0]);
      this._a2fWs = new WebSocket(url);

      this._a2fWs.addEventListener("open", () => {
        this._v2log("[A2F] Bridge WebSocket connected");
        this._a2fWsOpen = true;
        this._a2fConnecting = false;
        this._a2fStartKeepAlive();
        if (this._a2fPendingAudio && this._a2fPendingAudio.length > 0) {
          this._a2fSendStart();
          const validChunks = this._a2fPendingAudio.filter((c) => c !== null);
          this._v2log(
            "[A2F] Flushing",
            validChunks.length,
            "queued audio chunks after reconnect",
          );
          for (const chunk of validChunks) {
            try {
              this._a2fWs.send(chunk);
            } catch (_) {}
          }
          this._a2fPendingAudio = [];
        }
      });

      this._a2fWs.addEventListener("message", (ev) => {
        try {
          const msg = JSON.parse(ev.data);

          if (msg.type === "a2f_header") {
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) {
              this._v2log(
                "[A2F] Ignoring stale a2f_header (gen",
                msg.gen,
                "vs current",
                this._a2fActiveGen,
                ")",
              );
              return;
            }
            if (
              this._a2fTurnFirstHeaderMs == null &&
              this._a2fTurnStartMs != null
            ) {
              this._a2fTurnFirstHeaderMs = performance.now();
              this._a2fTl("first_a2f_header_received", {
                blendshape_names: msg.blendshape_names?.length || 0,
              });
            }
            this._v2log(
              "[A2F] Blendshape header received:",
              msg.blendshape_names?.length,
              "names",
            );
            return;
          }

          if (msg.type === "a2f_frames") {
            // We intentionally do NOT gate on _a2fAcceptingFrames here.
            // That flag governs OUTBOUND audio (whether we're still pushing
            // PCM into the bridge), not INBOUND blendshape frames from the
            // server. The two are independent: the bridge keeps draining
            // server frames until `a2f_done` regardless of EndOfAudio.
            if (!this._a2fSessionActive) {
              return;
            }
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) {
              return;
            }
            // Forward the per-batch emotion vector (if present) to the
            // driver so downstream consumers can subscribe via
            // `driver.onEmotions(cb)` or read `driver.lastEmotions`.
            // Plumbing only — the driver does not consume these to drive
            // morph targets here.
            const frames = Array.isArray(msg.frames) ? msg.frames : [];
            if (msg.emotions && this._a2fLipSyncDriver) {
              // Task #99 (B): hand the driver a timecode on the SAME
              // normalized "frame-0 == 0" timeline the lips use, so it can
              // fire the vector when the audio cursor reaches it instead of
              // on receipt (which drifted the expression ahead of the words
              // by the prepare-then-play lead). `emotion_time_code` is in
              // SECONDS and already rebased by the bridge; convert to ms and
              // subtract the first-frame origin. Before the first frame is
              // seen, this batch's frames[0] establishes that origin (it is
              // what `_a2fFirstFrameTimeMs` becomes below); afterwards reuse
              // the cached one.
              const t0 = this._a2fFirstFrameReceived
                ? this._a2fFirstFrameTimeMs ?? 0
                : frames[0]?.time_ms ?? 0;
              const tc = msg.emotion_time_code;
              const emoTcMs =
                typeof tc === "number" && Number.isFinite(tc)
                  ? tc * 1000 - t0
                  : null;
              this._a2fLipSyncDriver.setEmotions(msg.emotions, emoTcMs);
            }
            if (!frames.length) return;

            // ---- Per-chunk A2F timing (browser-side fields 14-17) ----
            // Joined to the bridge-side record by `chunkIndex`. We fold
            // both first-frame point-in-time measurements (14, 15) and
            // sticky-across-the-chunk flags (16, 17) into the same
            // record. Every batch tagged with the same chunk_idx
            // contributes; the first one stamps 14/15.
            try {
              const chunkIdx =
                typeof msg.chunk_idx === "number" ? msg.chunk_idx : null;
              if (chunkIdx !== null) {
                this._a2fRecordChunkBatchTiming(chunkIdx, frames);
              }
            } catch (_) {
              // Pure observability — never let a tracking error block
              // the lipsync path.
            }

            // Task #30: track the first-frame timestamp INDEPENDENTLY of
            // `_useA2FLipSync` so the debug recorder can normalize and
            // log every batch even when no character is bound. Without
            // this, runs that exercise the bridge but don't have a
            // driver attached produce empty `a2fBrowserReceiveEvents`,
            // defeating the recorder's purpose.
            if (!this._a2fFirstFrameReceived) {
              this._a2fFirstFrameReceived = true;
              this._a2fFirstFrameTimeMs = frames[0]?.time_ms ?? 0;
              if (
                this._a2fTurnFirstFramesMs == null &&
                this._a2fTurnStartMs != null
              ) {
                this._a2fTurnFirstFramesMs = performance.now();
                const sinceArm = (
                  this._a2fTurnFirstFramesMs - this._a2fTurnStartMs
                ).toFixed(1);
                const sinceFirstForward =
                  this._a2fTurnFirstForwardMs != null
                    ? (
                        this._a2fTurnFirstFramesMs - this._a2fTurnFirstForwardMs
                      ).toFixed(1)
                    : "n/a";
                const sinceEndOfAudio =
                  this._a2fTurnEndOfAudioMs != null
                    ? (
                        this._a2fTurnFirstFramesMs - this._a2fTurnEndOfAudioMs
                      ).toFixed(1)
                    : "before_eoa";
                this._a2fTl("first_a2f_frames_received", {
                  framesInBatch: frames.length,
                  rawFirstTimeMs: this._a2fFirstFrameTimeMs,
                  msSinceTurnStart: sinceArm,
                  msSinceFirstForward: sinceFirstForward,
                  msSinceEndOfAudio: sinceEndOfAudio,
                });
              }

              this._v2log(
                "[A2F] First frame received. rawFirstTimeMs=",
                this._a2fFirstFrameTimeMs,
                "→ will normalize to 0",
              );
            }

            const t0 = this._a2fFirstFrameTimeMs ?? 0;

            const normalized = frames.map((f) => ({
              ...f,
              time_ms: Math.max(0, (f.time_ms ?? 0) - t0),
            }));

            // Task #26 / Task #30: record this batch BEFORE enqueue so
            // we can compare its first frame against the previously
            // observed last queued frame. Lifted OUT of the
            // `_useA2FLipSync` gate (Task #30) so the recorder
            // captures bridge traffic in headless / no-character
            // sessions too. The helper is a no-op when the debug
            // recorder isn't active and never throws into the lipsync
            // path.
            this._a2fDebugRecordA2FFrameMessage(frames, normalized, msg);

            if (this._useA2FLipSync && this._a2fLipSyncDriver) {
              if (this._a2fDriverStarted) {
                this._a2fLipSyncDriver.enqueueFrames(normalized);
              } else {
                const prevCount = this._a2fBufferedFrames.length;
                this._a2fBufferedFrames.push(...normalized);

                // Explicit "first frame buffered" checkpoint: the very first
                // moment any A2F frame is held in our preroll buffer for this
                // turn. Distinct from `first_a2f_frames_received` (which fires
                // when a batch arrives over the WS, before normalization).
                if (prevCount === 0 && this._a2fBufferedFrames.length > 0) {
                  this._a2fTl("first_frame_buffered", {
                    bufferedFrames: this._a2fBufferedFrames.length,
                    firstFrameTimeMs:
                      this._a2fBufferedFrames[0]?.time_ms?.toFixed?.(1) ?? null,
                  });
                }

                if (this._a2fBufferedFrames.length > 400) {
                  this._a2fBufferedFrames.splice(
                    0,
                    this._a2fBufferedFrames.length - 400,
                  );
                }

                // Periodic preroll progress log so we can see the buffer
                // filling on long replies without spamming the console.
                const newCount = this._a2fBufferedFrames.length;
                if (
                  this._a2fSyncGateResolve &&
                  Math.floor(newCount / 30) > Math.floor(prevCount / 30)
                ) {
                  const lastTms =
                    newCount > 0
                      ? (this._a2fBufferedFrames[newCount - 1]?.time_ms ?? 0)
                      : 0;
                  this._a2fTl("preroll_progress", {
                    bufferedFrames: newCount,
                    bufferedMs: lastTms.toFixed(1),
                  });
                }

                // ---- Preroll gate release condition ----
                // Release as soon as either threshold is met. With a
                // truly streaming server this fires on the first batch;
                // with a batched server it fires on whichever burst
                // brings us past the threshold. Either way, audio only
                // starts when we have frames on hand.
                //
                // Task #30: AND in the `_a2fRequiredLeadMs` ceiling.
                // The legacy "12 frames OR 200 ms" thresholds release
                // far too early relative to the real bridge latency
                // (~1 s per chunk), so the queue runs dry shortly
                // after audio starts. The lead gate forces the
                // buffered-frames timeline to reach at least
                // `_a2fRequiredLeadMs` BEFORE we let audio play —
                // independent of the frames/ms count thresholds.
                // Set `_a2fRequiredLeadMs = 0` to recover legacy
                // behaviour.
                const lastT =
                  this._a2fBufferedFrames[this._a2fBufferedFrames.length - 1]
                    ?.time_ms ?? 0;
                const enoughFrames =
                  this._a2fBufferedFrames.length >= this._a2fPrerollFrames;
                const enoughMs = lastT >= this._a2fPrerollMs;
                const requiredLead = this._a2fRequiredLeadMs || 0;
                const meetsRequiredLead = lastT >= requiredLead;
                if (
                  this._a2fSyncGateResolve &&
                  (enoughFrames || enoughMs) &&
                  meetsRequiredLead
                ) {
                  const reason =
                    (enoughFrames ? "preroll_frames" : "preroll_ms") +
                    (requiredLead > 0 ? "+required_lead" : "");
                  this._a2fTurnGateReleaseMs = performance.now();
                  this._a2fTl("gate_released", {
                    reason,
                    bufferedFrames: this._a2fBufferedFrames.length,
                    bufferedMs: lastT.toFixed(1),
                    requiredLeadMs: requiredLead,
                  });
                  this._v2log(
                    "[A2F] Buffered",
                    this._a2fBufferedFrames.length,
                    "normalized frames (",
                    lastT.toFixed(1),
                    "ms) — releasing gate (" + reason + ")",
                  );
                  this._a2fReleaseSyncGate();
                }
              }
            }
            return;
          }

          // Per-chunk consolidated timing record from the bridge. Joined
          // by `chunkIndex` to whatever the browser tracked for that
          // chunk in `_a2fChunkTimings`. Pure observability — emit a
          // single `[A2F_CHUNK_TIMING]` console line and discard the
          // record from the per-session map.
          if (msg.type === "a2f_chunk_summary") {
            try {
              if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) {
                return;
              }
              this._a2fEmitChunkSummary(msg);
            } catch (_) {
              // Pure observability — never fail an a2f_chunk_summary.
            }
            return;
          }

          if (msg.type === "a2f_done") {
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) {
              this._v2log(
                "[A2F] Ignoring stale a2f_done (gen",
                msg.gen,
                "vs current",
                this._a2fActiveGen,
                ")",
              );
              return;
            }
            this._v2log(
              "[A2F] Session complete, total frames:",
              msg.total_frames,
            );
            if (this._a2fLipSyncDriver) {
              this._a2fLipSyncDriver.markSessionEnded();
            }
            this._a2fSessionActive = false;
            this._a2fDoneReceived = true;

            if (this._a2fSyncGateResolve) {
              // a2f_done with the gate still closed means the bridge declared
              // the session over before the preroll target was hit. Branch A
              // invariant: the 30 s hard ceiling is the ONLY path that
              // releases the gate with zero buffered frames. So we only
              // short-circuit the ceiling when we have at least some frames
              // to actually animate with — otherwise we fall through to the
              // ceiling, log the situation as a true A2F failure, and let
              // the existing timeout fire.
              const buffered = this._a2fBufferedFrames.length;
              if (buffered > 0) {
                this._v2log(
                  "[A2F] a2f_done arrived before gate released — releasing with",
                  buffered,
                  "buffered frames (preserved for playback)",
                );
                this._a2fTurnGateReleaseMs = performance.now();
                this._a2fTl("gate_released", {
                  reason: "a2f_done",
                  bufferedFrames: buffered,
                  totalFramesReported: msg.total_frames,
                });
                this._a2fReleaseSyncGate();
              } else {
                console.warn(
                  "[A2F] a2f_done arrived with zero buffered frames — true A2F failure; holding gate until 30s ceiling per Branch A spec",
                );
                this._a2fTl("a2f_done_zero_frames_holding", {
                  totalFramesReported: msg.total_frames,
                });
                // Intentionally do NOT call _a2fReleaseSyncGate() here.
                // The ceiling timer scheduled in _a2fArmSyncGate() will fire
                // and release with the standard `reason: "ceiling"` tag.
              }
            } else {
              this._a2fSyncGate = null;
            }
            return;
          }

          if (msg.type === "stopped") {
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) return;
            this._v2log("[A2F] Session stopped (hard cancel) gen=", msg.gen);
            if (this._a2fLipSyncDriver) {
              this._a2fLipSyncDriver.markSessionEnded();
            }
            this._a2fSessionActive = false;
            this._a2fAcceptingFrames = false;
            this._a2fReleaseSyncGate();
            this._a2fSyncGate = null;
            return;
          }

          if (msg.type === "ended") {
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) return;
            this._v2log(
              "[A2F] Session ended (natural completion) gen=",
              msg.gen,
            );
            return;
          }

          // Standalone emotion vector (no frames in this message). The
          // bridge emits these alongside the per-batch `emotions` field on
          // a2f_frames, so most consumers will already have an up-to-date
          // value. Still forward to the driver so subscribers get every
          // update even when frames are momentarily absent.
          if (msg.type === "a2f_emotions") {
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) return;
            if (msg.emotions && this._a2fLipSyncDriver) {
              // Task #99 (B): same normalization as the a2f_frames path.
              // `msg.time_code` is SECONDS, rebased by the bridge. Only
              // schedule it once a timeline origin exists; before the first
              // frame (no origin yet) pass null so it fires immediately,
              // preserving the legacy behavior for the rare leading vector.
              const tc = msg.time_code;
              const emoTcMs =
                this._a2fFirstFrameReceived &&
                typeof tc === "number" &&
                Number.isFinite(tc)
                  ? tc * 1000 - (this._a2fFirstFrameTimeMs ?? 0)
                  : null;
              this._a2fLipSyncDriver.setEmotions(msg.emotions, emoTcMs);
            }
            return;
          }

          // Bridge declined to open the gRPC stream for this utterance
          // because the per-utterance health check failed. Audio playback
          // proceeds normally without lipsync; release the gate so we don't
          // burn the 30 s ceiling waiting for frames that aren't coming.
          if (msg.type === "a2f_unavailable") {
            if (msg.gen !== undefined && msg.gen !== this._a2fActiveGen) return;
            console.warn(
              "[A2F] Bridge reports server unavailable for this utterance:",
              msg.reason || "(no reason)",
              msg.message || "",
            );
            this._a2fTl("a2f_unavailable", {
              reason: msg.reason || "unknown",
            });
            this._a2fSessionActive = false;
            this._a2fAcceptingFrames = false;
            if (this._a2fSyncGateResolve) {
              this._a2fTurnGateReleaseMs = performance.now();
              this._a2fTl("gate_released", {
                reason: "a2f_unavailable",
                bufferedFrames: this._a2fBufferedFrames.length,
              });
              this._a2fReleaseSyncGate();
            }
            return;
          }

          if (msg.type === "error") {
            console.error("[A2F] Bridge error:", msg.code, msg.message || "");
            if (msg.code === "unauthenticated") {
              console.warn(
                "[A2F] Bridge rejected connection: unauthenticated. Lip sync will not work until the user logs in.",
              );
              this._a2fEnabled = false;
              this._a2fSessionActive = false;
              this._a2fAcceptingFrames = false;
              // Release the sync gate immediately so audio isn't held for 30 s
              if (this._a2fSyncGateResolve) {
                this._a2fTurnGateReleaseMs = performance.now();
                this._a2fTl("gate_released", { reason: "unauthenticated" });
                this._a2fReleaseSyncGate();
              }
            }
            return;
          }

          this._v2log("[A2F] Bridge message:", msg.type, msg.message || "");
        } catch (_) {}
      });

      this._a2fWs.addEventListener("close", () => {
        this._v2log("[A2F] Bridge WebSocket closed");
        this._a2fWsOpen = false;
        this._a2fConnecting = false;
        this._a2fSessionActive = false;
        this._a2fStopKeepAlive();
      });

      this._a2fWs.addEventListener("error", (e) => {
        console.warn("[A2F] Bridge WebSocket error:", e);
        this._a2fWsOpen = false;
        this._a2fConnecting = false;
        this._a2fSessionActive = false;
        this._a2fStopKeepAlive();
      });
    } catch (e) {
      console.warn("[A2F] Failed to connect bridge:", e);
      this._a2fConnecting = false;
    }
  }

  _a2fStartKeepAlive() {
    this._a2fStopKeepAlive();
    this._a2fKeepAliveTimer = setInterval(() => {
      try {
        if (
          this._a2fWsOpen &&
          this._a2fWs &&
          this._a2fWs.readyState === WebSocket.OPEN
        ) {
          this._a2fWs.send(JSON.stringify({ type: "ping", t: Date.now() }));
        }
      } catch {}
    }, this._a2fKeepAliveIntervalMs);
  }

  _a2fStopKeepAlive() {
    if (this._a2fKeepAliveTimer) {
      clearInterval(this._a2fKeepAliveTimer);
      this._a2fKeepAliveTimer = null;
    }
  }

  _a2fSendStart() {
    if (!this._a2fWsOpen || this._a2fSessionActive) return;
    this._a2fSessionGen++;
    const gen = this._a2fSessionGen;
    try {
      this._a2fWs.send(
        JSON.stringify({
          type: "start",
          sampleRate: 16000,
          channels: 1,
          bitsPerSample: 16,
          gen,
        }),
      );
      this._a2fActiveGen = gen;
      this._a2fSessionActive = true;
      this._a2fAcceptingFrames = true;
      this._a2fAudioInputDone = false;
      this._a2fDoneReceived = false;
      // Task #99: fresh A2F session = fresh teardown one-shot.
      this._a2fIdleTeardownRan = false;
      // Task #21: fresh A2F session ⇒ fresh pacer state.
      this._a2fResetPacer("a2fSendStart");
      // Reset per-chunk browser-side timing state — each new bridge
      // session is a fresh chunkIndex space. Also cancel any deferred
      // emit timers from a previous session so they don't fire after
      // their bridge counterparts have been forgotten.
      if (this._a2fChunkTimings && this._a2fChunkTimings.size) {
        for (const r of this._a2fChunkTimings.values()) {
          if (r && r._pendingEmitTimer) {
            clearTimeout(r._pendingEmitTimer);
            r._pendingEmitTimer = null;
          }
        }
        this._a2fChunkTimings.clear();
      }
      if (!this._a2fSyncGateResolve && this._useA2FLipSync) {
        this._a2fArmSyncGate();
      }
      this._v2log("[A2F] Session started gen=", gen);
    } catch (e) {
      console.warn("[A2F] Failed to start session:", e);
    }
  }

  /**
   * First-chunk-from-ElevenLabs marker. Called from every ElevenLabs WS
   * audio entry point (binary ArrayBuffer, Blob, base64 JSON). Idempotent
   * per turn — only the first call emits a timeline line.
   */
  _a2fNoteFirstElevenChunk(byteLen, source) {
    if (this._a2fTurnFirstElevenChunkMs != null) return;
    if (this._a2fTurnStartMs == null) return; // no turn armed yet
    this._a2fTurnFirstElevenChunkMs = performance.now();
    this._a2fTl("first_eleven_audio_chunk", {
      bytes: byteLen,
      source,
    });
  }

  /**
   * Emit a single ``[A2F-TIMELINE]`` log line on the frontend side, mirroring
   * the bridge format. Aligned by ``gen=`` so a single tail can reconstruct
   * the full audio→frame→playback pipeline across both processes.
   */
  _a2fTl(label, extras = {}) {
    const t0 = this._a2fTurnStartMs;
    const gen = this._a2fActiveGen;
    if (t0 == null) {
      console.log(`[A2F-TIMELINE] gen=${gen} t=n/a ${label}`, extras);
      return;
    }
    const t = (performance.now() - t0).toFixed(1);
    console.log(`[A2F-TIMELINE] gen=${gen} t=${t}ms ${label}`, extras);
  }

  /**
   * Compute the current audio clock (in the same normalized timeline
   * the lipsync driver uses) and return it as ms, or `null` with a
   * reason when audio has not yet started for this turn.
   *
   * Source of truth is `audioCtx.currentTime` (the actual hardware
   * audio clock), converted to stream-relative ms using the
   * `_timeline.ctxStart` offset that was stamped at arm time. This is
   * the right clock for diagnosing lipsync starvation because it
   * tracks real playback progress rather than wall time — perf.now
   * can drift relative to the audio device under load and produce
   * misleading lead/starvation metrics.
   *
   * Falls back to `performance.now() - _timeline.wallStart` only when
   * the AudioContext is missing/closed (`audioCtx_unavailable`),
   * since the two stamps were captured at the same instant and share
   * the same zero point as `syncClock(0, wallStart)`.
   *
   * Returns `{ value: number, reason: null|string }` where reason is
   * non-null only on null value (`pre_audio_start`, `no_timeline`).
   * On a successful read it also carries `source` ("audioCtx" or
   * "wallClock") for diagnostic clarity.
   */
  _a2fCurrentAudioClockMs() {
    const tl = this._timeline;
    if (!tl) return { value: null, reason: "no_timeline" };
    if (!tl.armed || !tl.wallStart) {
      return { value: null, reason: "pre_audio_start" };
    }
    const ctx = this.audioCtx;
    if (ctx && ctx.state !== "closed" && typeof tl.ctxStart === "number") {
      const elapsed = (ctx.currentTime - tl.ctxStart) * 1000;
      // Arm precedes actual playback by LEAD_S (~80ms). During that
      // lead-in window `ctx.currentTime < ctxStart`, so a naive
      // subtraction yields a negative "elapsed" that misrepresents
      // playback progress. Spec requires `null` + "pre_audio_start"
      // for this window so pre-roll/sync-gate frames are accounted
      // for explicitly.
      if (elapsed < 0) {
        return { value: null, reason: "pre_audio_start", source: "audioCtx" };
      }
      return { value: elapsed, reason: null, source: "audioCtx" };
    }
    const elapsed = performance.now() - tl.wallStart;
    if (elapsed < 0) {
      return { value: null, reason: "pre_audio_start", source: "wallClock" };
    }
    return { value: elapsed, reason: null, source: "wallClock" };
  }

  /**
   * Per-chunk timing tracker called once per inbound `a2f_frames` batch
   * tagged with `chunk_idx`. Stamps fields 14-15 on the FIRST batch for
   * a given chunk and updates fields 16-17 (sticky) plus the running
   * minimum lead on every subsequent batch.
   *
   * `frames` is the raw bridge batch (un-normalized) — but for lead
   * computation we want the LATEST queued frame in the same timeline as
   * the audio clock. Frames carry `time_ms` shifted by the bridge's
   * `time_code_offset`; the browser then re-normalizes to "first frame
   * of this turn = 0". So the lead is:
   *
   *     lead = (lastFrame.time_ms - _a2fFirstFrameTimeMs) - audioClockMs
   *
   * Pre-first-frame the offset isn't known yet; in that window we use
   * the raw batch time_ms minus a 0-baseline — the audio clock is also
   * `null` there, so lead is null too and the sticky flags don't flip.
   */
  _a2fRecordChunkBatchTiming(chunkIdx, frames) {
    if (!frames || !frames.length) return;
    let rec = this._a2fChunkTimings.get(chunkIdx);
    // Treat a placeholder-only record (created by a deferred-emit
    // before any batch arrived) as "first batch" — the data fields
    // haven't been initialized yet.
    const isFirstBatchForChunk =
      !rec || rec.currentAudioTimeMsAtFirstFrameReason === undefined;
    if (isFirstBatchForChunk) {
      const fresh = {
        chunkIndex: chunkIdx,
        // Field 14 — audio-clock-at-frame, stamped on first batch and
        // refreshed on every subsequent batch so we get both endpoints.
        currentAudioTimeMsAtFirstFrame: null,
        currentAudioTimeMsAtFirstFrameReason: null,
        currentAudioTimeMsAtLastFrame: null,
        currentAudioTimeMsAtLastFrameReason: null,
        // Field 15 — frameQueue lead at first frame (point-in-time).
        frameQueueLeadMsAtFirstFrame: null,
        frameQueueLeadMsAtFirstFrameReason: null,
        // Field 15 (continued) — running min across all batches in the
        // chunk; spec calls this `minFrameQueueLeadMs`. Companion
        // reason carries why it's still null at emit time so the
        // joined record doesn't silently omit the cause.
        minFrameQueueLeadMs: null,
        minFrameQueueLeadMsReason: null,
        // Tracks the most recent leadMs reason across all batches —
        // used to pick a better `minFrameQueueLeadMsReason` than just
        // the first-batch reason when no batch ever produced a
        // measurable lead.
        _lastLeadReason: null,
        // Fields 16 / 17 — sticky flags accumulated across batches.
        frameQueueLeadEverBelow300Ms: false,
        frameQueueLeadEverBelow0Ms: false,
        // Preserve any pending deferred-emit timer that was stashed
        // here before the first batch arrived.
        _pendingEmitTimer: rec ? rec._pendingEmitTimer || null : null,
      };
      rec = fresh;
      this._a2fChunkTimings.set(chunkIdx, rec);
    }

    const clock = this._a2fCurrentAudioClockMs();

    // Compute the latest queued frame's normalized time_ms. If we
    // haven't observed the first frame yet, the offset isn't known,
    // so the lead is null with reason "pre_first_frame".
    const lastFrame = frames[frames.length - 1];
    const rawLastTimeMs =
      lastFrame && typeof lastFrame.time_ms === "number"
        ? lastFrame.time_ms
        : null;
    let leadMs = null;
    let leadReason = null;
    if (clock.value === null) {
      leadReason = clock.reason; // "pre_audio_start" | "no_timeline"
    } else if (rawLastTimeMs === null) {
      leadReason = "no_frame_time";
    } else if (this._a2fFirstFrameTimeMs == null) {
      // First batch of the turn arriving with audio already started:
      // the normalization baseline isn't fixed yet, so a precise lead
      // can't be computed — record it as null with a clear reason
      // rather than silently reporting 0 ms (which would understate
      // initial buffer lead).
      leadReason = "first_batch_no_baseline";
    } else {
      const normalizedLastMs = rawLastTimeMs - this._a2fFirstFrameTimeMs;
      leadMs = normalizedLastMs - clock.value;
    }

    if (isFirstBatchForChunk) {
      rec.currentAudioTimeMsAtFirstFrame = clock.value;
      rec.currentAudioTimeMsAtFirstFrameReason = clock.reason;
      rec.frameQueueLeadMsAtFirstFrame = leadMs;
      rec.frameQueueLeadMsAtFirstFrameReason = leadReason;
    }

    // Field 14 (last): refresh on every batch so it ends up holding
    // the audio clock as of the *last* batch we received for the chunk.
    rec.currentAudioTimeMsAtLastFrame = clock.value;
    rec.currentAudioTimeMsAtLastFrameReason = clock.reason;

    // Track the latest lead reason so emission can attribute a still-
    // null minFrameQueueLeadMs to the most recent (and most relevant)
    // cause rather than the first batch's reason.
    rec._lastLeadReason = leadReason;

    // Sticky flags: only update once we have a real numeric lead. A
    // null lead (pre_audio_start, etc.) deliberately does not flip the
    // sticky bits — the contract is "ever observed this condition", and
    // an unmeasured window is not an observation either way.
    if (leadMs !== null) {
      if (leadMs < 300) rec.frameQueueLeadEverBelow300Ms = true;
      if (leadMs < 0) rec.frameQueueLeadEverBelow0Ms = true;
      if (
        rec.minFrameQueueLeadMs === null ||
        leadMs < rec.minFrameQueueLeadMs
      ) {
        rec.minFrameQueueLeadMs = leadMs;
        rec.minFrameQueueLeadMsReason = null;
      }
    }
  }

  /**
   * Joined-record emitter: triggered when the bridge sends an
   * `a2f_chunk_summary`. Per spec, emits "when summary arrives OR
   * when chunk audio has fully played, whichever is later" — so when
   * the summary arrives mid-playback, emission is deferred via
   * setTimeout until the browser audio clock has crossed the chunk's
   * `chunkEndAudioMs` boundary. If audio is not running (clock null),
   * deferral is impossible and the record is emitted immediately —
   * the audio-clock fields will already carry their null+reason.
   */
  _a2fEmitChunkSummary(bridgeMsg) {
    // Per spec: emit "when summary arrives OR when chunk audio has
    // fully played, whichever is later". Translate that into a single
    // attempt loop: each attempt either emits now (if both gates
    // open) or schedules the next try with a delay tuned to the gate
    // we're still waiting on. A hard cap (~20s) prevents runaway
    // pending timers if audio never starts.
    this._a2fScheduleChunkSummaryEmit(bridgeMsg, 0);
  }

  /**
   * Single attempt of the deferred-emit loop kicked off by
   * `_a2fEmitChunkSummary`. The two gates we wait on are:
   *
   *   gate A: timeline armed (else we don't even know when audio
   *           starts — poll at 100 ms)
   *   gate B: audioCtx.currentTime >= ctxStart + chunkEndAudioMs/1000
   *           (else schedule precisely the remaining time + 10 ms)
   *
   * `_pendingEmitTimer` is stashed on the per-chunk record so
   * cancel()/shutdown() can clear it. After MAX_ATTEMPTS we emit
   * anyway so a stuck turn never silently swallows a chunk record.
   */
  _a2fScheduleChunkSummaryEmit(bridgeMsg, attempts) {
    const MAX_ATTEMPTS = 200; // ≥20s worst case at 100ms polling
    const POLL_MS = 100;

    const chunkIdx =
      typeof bridgeMsg.chunkIndex === "number" ? bridgeMsg.chunkIndex : null;
    const chunkEndAudioMs =
      typeof bridgeMsg.chunkEndAudioMs === "number"
        ? bridgeMsg.chunkEndAudioMs
        : null;

    const stashTimer = (timerId) => {
      if (chunkIdx === null) return;
      let rec = this._a2fChunkTimings.get(chunkIdx);
      if (rec) {
        rec._pendingEmitTimer = timerId;
      } else {
        this._a2fChunkTimings.set(chunkIdx, {
          chunkIndex: chunkIdx,
          _pendingEmitTimer: timerId,
        });
      }
    };
    const clearStashedTimer = () => {
      if (chunkIdx === null) return;
      const rec = this._a2fChunkTimings.get(chunkIdx);
      if (rec) rec._pendingEmitTimer = null;
    };

    if (attempts >= MAX_ATTEMPTS) {
      clearStashedTimer();
      this._a2fEmitChunkSummaryNow(bridgeMsg, "max_wait_exceeded");
      return;
    }

    // Bridge didn't give us a target — nothing to wait for.
    if (chunkEndAudioMs === null) {
      clearStashedTimer();
      this._a2fEmitChunkSummaryNow(bridgeMsg, null);
      return;
    }

    const tl = this._timeline;
    const armed = !!(
      tl &&
      tl.armed &&
      tl.wallStart &&
      typeof tl.ctxStart === "number"
    );
    const ctx = this.audioCtx;
    const ctxOk = !!(ctx && ctx.state !== "closed");

    // Gate A: timeline not armed yet (audio playback hasn't begun).
    // Poll at a coarse interval — we have no precise prediction.
    if (!armed) {
      const timerId = setTimeout(() => {
        clearStashedTimer();
        this._a2fScheduleChunkSummaryEmit(bridgeMsg, attempts + 1);
      }, POLL_MS);
      stashTimer(timerId);
      return;
    }

    // Gate B: armed — compute the exact remaining audio time until
    // playback crosses chunkEndAudioMs and schedule one shot. Add
    // 10 ms grace so the last `a2f_frames` batch for the chunk has
    // landed in `_a2fRecordChunkBatchTiming` before we sample.
    const targetCtxSec = tl.ctxStart + chunkEndAudioMs / 1000;
    const nowCtxSec = ctxOk ? ctx.currentTime : null;
    let remainingMs = 0;
    if (nowCtxSec !== null) {
      remainingMs = (targetCtxSec - nowCtxSec) * 1000;
    } else {
      // AudioContext gone — fall back to wall clock.
      remainingMs = tl.wallStart + chunkEndAudioMs - performance.now();
    }

    if (remainingMs <= 0) {
      clearStashedTimer();
      this._a2fEmitChunkSummaryNow(bridgeMsg, null);
      return;
    }

    const delayMs = Math.min(remainingMs + 10, 5000);
    const timerId = setTimeout(() => {
      clearStashedTimer();
      this._a2fScheduleChunkSummaryEmit(bridgeMsg, attempts + 1);
    }, delayMs);
    stashTimer(timerId);
  }

  /**
   * Build the joined 17-field record and log it. Always called once
   * per chunk — either inline from `_a2fEmitChunkSummary` or from the
   * deferred timer.
   */
  _a2fEmitChunkSummaryNow(bridgeMsg, emitDelayReason) {
    const chunkIdx =
      typeof bridgeMsg.chunkIndex === "number" ? bridgeMsg.chunkIndex : null;
    const browserRec =
      chunkIdx !== null ? this._a2fChunkTimings.get(chunkIdx) : null;
    const haveBrowserSamples = !!(
      browserRec &&
      (browserRec.currentAudioTimeMsAtFirstFrame !== undefined ||
        browserRec.currentAudioTimeMsAtFirstFrameReason !== undefined)
    );

    const joined = {
      // Bridge fields. Each nullable field is paired with its
      // bridge-supplied `*Reason` so the joined record carries
      // explicit "null with a reason" metadata, never silently
      // omitting a missing value.
      //
      // The Task #25 bridge additions (`expectedDurationMs`,
      // `expectedBytes`, `tFirstSubChunkSentMonoMs` /
      // `tLastSubChunkSentMonoMs`, `firstRawA2FTimeCodeMs` /
      // `lastRawA2FTimeCodeMs`, `timeCodeOffsetMs`,
      // `gapFromPreviousChunkMs` / `overlapFromPreviousChunkMs`,
      // `processingLatencyMs`, `totalChunkLatencyMs`) power these
      // recorder warning types when the data shows trouble:
      //   - FRAME_TIMELINE_RESTARTED_AT_ZERO  (raw codes never shifted)
      //   - FRAME_TIMELINE_GAP                (gapFromPreviousChunkMs)
      //   - FRAME_TIMELINE_OVERLAP            (overlapFromPreviousChunkMs)
      //   - A2F_CHUNK_PROCESSING_TOO_SLOW     (processingLatencyMs)
      chunkIndex: bridgeMsg.chunkIndex ?? null,
      chunkStartAudioMs: bridgeMsg.chunkStartAudioMs ?? null,
      chunkEndAudioMs: bridgeMsg.chunkEndAudioMs ?? null,
      bytesSentToA2f: bridgeMsg.bytesSentToA2f ?? null,
      expectedDurationMs: bridgeMsg.expectedDurationMs ?? null,
      expectedBytes: bridgeMsg.expectedBytes ?? null,
      timeCodeOffsetMs: bridgeMsg.timeCodeOffsetMs ?? null,
      tFirstByteBufferedMonoMs: bridgeMsg.tFirstByteBufferedMonoMs ?? null,
      tFirstByteBufferedMonoMsReason:
        bridgeMsg.tFirstByteBufferedMonoMsReason ?? null,
      tChunkFullMonoMs: bridgeMsg.tChunkFullMonoMs ?? null,
      tChunkFullMonoMsReason: bridgeMsg.tChunkFullMonoMsReason ?? null,
      tGrpcStartMonoMs: bridgeMsg.tGrpcStartMonoMs ?? null,
      tGrpcStartMonoMsReason: bridgeMsg.tGrpcStartMonoMsReason ?? null,
      tFirstSubChunkSentMonoMs: bridgeMsg.tFirstSubChunkSentMonoMs ?? null,
      tFirstSubChunkSentMonoMsReason:
        bridgeMsg.tFirstSubChunkSentMonoMsReason ?? null,
      tLastSubChunkSentMonoMs: bridgeMsg.tLastSubChunkSentMonoMs ?? null,
      tLastSubChunkSentMonoMsReason:
        bridgeMsg.tLastSubChunkSentMonoMsReason ?? null,
      tEoaSentMonoMs: bridgeMsg.tEoaSentMonoMs ?? null,
      tEoaSentMonoMsReason: bridgeMsg.tEoaSentMonoMsReason ?? null,
      tFirstFrameMonoMs: bridgeMsg.tFirstFrameMonoMs ?? null,
      tFirstFrameMonoMsReason: bridgeMsg.tFirstFrameMonoMsReason ?? null,
      tLastFrameMonoMs: bridgeMsg.tLastFrameMonoMs ?? null,
      tLastFrameMonoMsReason: bridgeMsg.tLastFrameMonoMsReason ?? null,
      framesReceived: bridgeMsg.framesReceived ?? null,
      firstRawA2FTimeCodeMs: bridgeMsg.firstRawA2FTimeCodeMs ?? null,
      firstRawA2FTimeCodeMsReason:
        bridgeMsg.firstRawA2FTimeCodeMsReason ?? null,
      lastRawA2FTimeCodeMs: bridgeMsg.lastRawA2FTimeCodeMs ?? null,
      lastRawA2FTimeCodeMsReason: bridgeMsg.lastRawA2FTimeCodeMsReason ?? null,
      firstFrameTimeMsAfterOffset:
        bridgeMsg.firstFrameTimeMsAfterOffset ?? null,
      firstFrameTimeMsAfterOffsetReason:
        bridgeMsg.firstFrameTimeMsAfterOffsetReason ?? null,
      lastFrameTimeMsAfterOffset: bridgeMsg.lastFrameTimeMsAfterOffset ?? null,
      lastFrameTimeMsAfterOffsetReason:
        bridgeMsg.lastFrameTimeMsAfterOffsetReason ?? null,
      gapFromPreviousChunkMs: bridgeMsg.gapFromPreviousChunkMs ?? null,
      gapFromPreviousChunkMsReason:
        bridgeMsg.gapFromPreviousChunkMsReason ?? null,
      overlapFromPreviousChunkMs: bridgeMsg.overlapFromPreviousChunkMs ?? null,
      overlapFromPreviousChunkMsReason:
        bridgeMsg.overlapFromPreviousChunkMsReason ?? null,
      processingLatencyMs: bridgeMsg.processingLatencyMs ?? null,
      processingLatencyMsReason: bridgeMsg.processingLatencyMsReason ?? null,
      totalChunkLatencyMs: bridgeMsg.totalChunkLatencyMs ?? null,
      totalChunkLatencyMsReason: bridgeMsg.totalChunkLatencyMsReason ?? null,
      // Bridge errors (when present) propagate so the joined record
      // doesn't silently look "fine" for failed chunks.
      bridgeError: bridgeMsg.error ?? null,
      // Browser field 14 — audio clock at first/last frame batch.
      currentAudioTimeMsAtFirstFrame: haveBrowserSamples
        ? (browserRec.currentAudioTimeMsAtFirstFrame ?? null)
        : null,
      currentAudioTimeMsAtFirstFrameReason: haveBrowserSamples
        ? (browserRec.currentAudioTimeMsAtFirstFrameReason ?? null)
        : "no_browser_batch_for_chunk",
      currentAudioTimeMsAtLastFrame: haveBrowserSamples
        ? (browserRec.currentAudioTimeMsAtLastFrame ?? null)
        : null,
      currentAudioTimeMsAtLastFrameReason: haveBrowserSamples
        ? (browserRec.currentAudioTimeMsAtLastFrameReason ?? null)
        : "no_browser_batch_for_chunk",
      // Browser field 15 — frameQueue lead at first frame + min over chunk.
      frameQueueLeadMsAtFirstFrame: haveBrowserSamples
        ? (browserRec.frameQueueLeadMsAtFirstFrame ?? null)
        : null,
      frameQueueLeadMsAtFirstFrameReason: haveBrowserSamples
        ? (browserRec.frameQueueLeadMsAtFirstFrameReason ?? null)
        : "no_browser_batch_for_chunk",
      minFrameQueueLeadMs: haveBrowserSamples
        ? (browserRec.minFrameQueueLeadMs ?? null)
        : null,
      // Companion reason: only set when the value is null. Prefer the
      // most-recent batch's lead reason (more representative of why
      // we couldn't measure) over the explicit field, falling back to
      // "no_browser_batch_for_chunk" when no batch ever arrived.
      minFrameQueueLeadMsReason: (() => {
        if (!haveBrowserSamples) return "no_browser_batch_for_chunk";
        if (browserRec.minFrameQueueLeadMs !== null) return null;
        return (
          browserRec.minFrameQueueLeadMsReason ||
          browserRec._lastLeadReason ||
          "no_measurable_lead"
        );
      })(),
      // Browser fields 16/17 — sticky flags.
      frameQueueLeadEverBelow300Ms: haveBrowserSamples
        ? !!browserRec.frameQueueLeadEverBelow300Ms
        : false,
      frameQueueLeadEverBelow0Ms: haveBrowserSamples
        ? !!browserRec.frameQueueLeadEverBelow0Ms
        : false,
      // Diagnostic: tells you whether the emit was inline or deferred.
      emitDelayReason: emitDelayReason,
      gen: this._a2fActiveGen,
    };

    console.log("[A2F_CHUNK_TIMING]", joined);

    // Recorder hook (Task #25): when the per-utterance debug recorder
    // is active for the current turn, push this joined per-chunk
    // record into the `a2fBridgeChunks` array. The recorder no-ops
    // internally when not recording.
    //
    // The downstream tasks (#26 / #27) populate the matching
    // browser-receive / scheduling / render arrays — this hook only
    // carries bridge fields plus the already-joined browser
    // audio-clock samples that `_a2fEmitChunkSummaryNow` builds
    // above. Pure observability — any failure is swallowed so a
    // recorder bug can never poison the lipsync path.
    //
    // Stale-chunk guard: we compare the BRIDGE'S gen
    // (`bridgeMsg.gen`, attached server-side by `_emit_chunk_summary`
    // — see `a2f_bridge.py`) against the active turn gen. A deferred
    // `_a2fEmitChunkSummaryNow` callback queued before a turn
    // rollover can otherwise execute under the new turn's recorder
    // and pollute the new utterance's `a2fBridgeChunks`. Comparing
    // `bridgeMsg.gen` (immutable, captured when the chunk left the
    // bridge) catches that race; comparing `this._a2fActiveGen`
    // against itself would not.
    try {
      const msgGen = bridgeMsg && bridgeMsg.gen;
      if (
        this._a2fDebugRecorder &&
        this._a2fDebugRecorder.isRecording &&
        msgGen !== undefined &&
        msgGen !== null &&
        msgGen === this._a2fActiveGen
      ) {
        this._a2fDebugRecorder.addA2FBridgeChunk(joined);
      }
    } catch (_) {
      // Pure observability — never fail an a2f_chunk_summary emit.
    }

    if (chunkIdx !== null && browserRec) {
      this._a2fChunkTimings.delete(chunkIdx);
    }
  }

  // Task #99: computed-clock end-of-reply teardown.
  //
  // The mouth closes on a timer pinned to the wall-clock instant the
  // currently-scheduled audio is known to finish. `_armComputedTeardown`
  // is re-armed by `_scheduleBuffer` every time more audio is scheduled,
  // so while audio keeps flowing the teardown is pushed out to the new
  // true end. When audio stops arriving, the last-armed timer survives
  // and fires `_a2fForceIdleTeardown("computed")` at the real end of
  // speech — no dependence on any ElevenLabs / bridge callback. The
  // natural `src.onended` fullyIdle branch remains as a backstop; both
  // run through the one-shot `_a2fForceIdleTeardown`.
  _armComputedTeardown(ctxEndTime) {
    if (!Number.isFinite(ctxEndTime) || !this.audioCtx) return;
    this._clearComputedTeardown();
    this._a2fComputedTeardownCtxEnd = ctxEndTime;
    this._a2fComputedTeardownArmedAtWall = performance.now();
    // The end is in AudioContext time, but setTimeout runs on wall time.
    const delay = Math.max(
      0,
      (ctxEndTime - this.audioCtx.currentTime) * 1000,
    );
    this._computedTeardownTimer = setTimeout(() => {
      this._computedTeardownTimer = null;

      // Task #99 (rejection fix): the reply must be TRULY finished before
      // the mouth closes, otherwise a mid-reply gap (LLM still thinking,
      // synthesis still in flight, or the audio queue momentarily empty)
      // would cut the tail — the "words being skipped" the user rejected.
      // Require: the inbound LLM stream closed, no batches still being
      // synthesized, the outbound text queue drained, and no decoded
      // audio still waiting to schedule.
      if (this._streamActive) return;
      if (this.pendingBatches > 0) return;
      if (this.outboundQueue && this.outboundQueue.length > 0) return;
      if (this.audioQueue && this.audioQueue.length > 0) return;

      // Guard against an early wall-timer fire (e.g. clock skew or a hold
      // that ate wall time): if the context clock hasn't actually reached
      // the scheduled end yet, re-arm for the remaining context time.
      const remainingMs =
        (this._a2fComputedTeardownCtxEnd - this.audioCtx.currentTime) * 1000;
      if (remainingMs > 30) {
        this._armComputedTeardown(this._a2fComputedTeardownCtxEnd);
        return;
      }

      try {
        this._a2fTl("computed_teardown_fire", {
          ctxEnd: Number(ctxEndTime.toFixed(3)),
        });
      } catch (_) {}
      try {
        this._a2fForceIdleTeardown("computed");
      } catch (e) {
        console.warn("[A2F] computed-clock teardown failed", e);
      }
    }, delay);
  }

  _clearComputedTeardown() {
    if (this._computedTeardownTimer) {
      clearTimeout(this._computedTeardownTimer);
      this._computedTeardownTimer = null;
    }
  }

  _a2fArmSyncGate() {
    if (!this._useA2FLipSync) return;

    this._a2fFirstFrameReceived = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fBufferedFrames = [];
    this._a2fDriverStarted = false;

    this._a2fClearQuietEndWatchdog();

    // Task #30: reset per-turn syncClock counter so the
    // SYNC_CLOCK_CALLED_MORE_THAN_ONCE warning only fires for
    // duplicate calls WITHIN the current turn.
    this._a2fSyncClockCallCount = 0;

    // Reset per-turn timeline timestamps.
    this._a2fTurnStartMs = performance.now();
    this._a2fTurnFirstElevenChunkMs = null;
    this._a2fTurnFirstForwardMs = null;
    this._a2fTurnEndOfAudioMs = null;
    this._a2fTurnFirstHeaderMs = null;
    this._a2fTurnFirstFramesMs = null;
    this._a2fTurnGateReleaseMs = null;
    this._a2fTurnPlaybackStartMs = null;

    this._a2fSyncGate = new Promise((resolve) => {
      this._a2fSyncGateResolve = resolve;
    });

    // Simple preroll gate. The release condition lives in the a2f_frames
    // message handler (see `a2f_frames` branch) — as soon as enough
    // frames are buffered, audio playback can start with mouth motion
    // already on hand. The hard ceiling below only fires when the bridge
    // truly produces nothing for the entire turn.
    this._a2fGateArmedAtMs = performance.now();
    this._a2fScheduleGateTimeout(this._a2fGateMaxMs);
    this._a2fTl("turn_armed", {
      prerollFrames: this._a2fPrerollFrames,
      prerollMs: this._a2fPrerollMs,
      ceilingMs: this._a2fGateMaxMs,
    });
    this._v2log(
      `[A2F] Sync gate armed — preroll ≥${this._a2fPrerollFrames} frames OR ≥${this._a2fPrerollMs}ms (ceiling ${this._a2fGateMaxMs}ms)`,
    );
  }

  /**
   * Schedule the hard ceiling that fires only on a truly dead bridge.
   * The normal release path lives inside the a2f_frames handler.
   */
  _a2fScheduleGateTimeout(ms) {
    if (this._a2fGateTimeoutId) {
      clearTimeout(this._a2fGateTimeoutId);
      this._a2fGateTimeoutId = null;
    }
    this._a2fGateTimeoutId = setTimeout(() => {
      this._a2fGateTimeoutId = null;
      if (this._a2fSyncGateResolve) {
        console.warn(
          `[A2F] Sync gate ceiling reached (${ms}ms) — releasing with ${this._a2fBufferedFrames.length} buffered frames; bridge likely dead`,
        );
        this._a2fTurnGateReleaseMs = performance.now();
        this._a2fTl("gate_released", {
          reason: "ceiling",
          bufferedFrames: this._a2fBufferedFrames.length,
        });
        this._a2fReleaseSyncGate();
      }
    }, ms);
  }

  _a2fReleaseSyncGate() {
    if (this._a2fGateTimeoutId) {
      clearTimeout(this._a2fGateTimeoutId);
      this._a2fGateTimeoutId = null;
    }
    this._a2fGateArmedAtMs = null;
    if (this._a2fSyncGateResolve) {
      this._a2fSyncGateResolve();
      this._a2fSyncGateResolve = null;
    }
  }

  _a2fStartSession() {
    if (!this._a2fEnabled || this._a2fSessionActive) return;
    if (!this._a2fWsOpen) {
      this._v2log(
        "[A2F] Bridge not connected, reconnecting before session start",
      );
      this._a2fPendingAudio = this._a2fPendingAudio || [];
      this._a2fConnect();
      return;
    }
    this._a2fSendStart();
  }

  _a2fForwardAudio(data) {
    if (!this._a2fEnabled || !this._a2fAcceptingFrames) return;

    if (
      !this._a2fWsOpen ||
      !this._a2fWs ||
      this._a2fWs.readyState !== WebSocket.OPEN
    ) {
      if (!this._a2fConnecting) this._a2fConnect();
      if (data instanceof ArrayBuffer) {
        if (this._a2fPendingAudio.length < 500)
          this._a2fPendingAudio.push(data);
      }
      return;
    }

    if (!this._a2fSessionActive) this._a2fSendStart();
    if (!this._a2fSessionActive) return;

    if (data instanceof ArrayBuffer) {
      this._a2fPacerEnqueueOrSend(data);
    } else if (typeof Blob !== "undefined" && data instanceof Blob) {
      data.arrayBuffer().then((buf) => {
        if (
          this._a2fWsOpen &&
          this._a2fSessionActive &&
          this._a2fAcceptingFrames
        ) {
          this._a2fPacerEnqueueOrSend(buf);
        }
      });
    }
  }

  // -----------------------------------------------------------------
  // Task #21 — pacer plumbing.
  //
  // `_a2fPacerEnqueueOrSend` is the single gateway for binary audio
  // headed to the bridge WS. When pacing is disabled it sends
  // immediately (current behavior). When enabled it appends to the
  // queue and `_a2fPacerKick`s the wall-clock drain loop.
  //
  // `_a2fSendBytesToBridge` is the actual WS write + first-forward
  // bookkeeping. Both the pacer drain loop and the disabled-path
  // call here so the timeline log line keeps working unchanged.
  // -----------------------------------------------------------------
  _a2fSendBytesToBridge(buf) {
    try {
      this._a2fWs.send(buf);
      if (
        this._a2fTurnFirstForwardMs == null &&
        this._a2fTurnStartMs != null
      ) {
        this._a2fTurnFirstForwardMs = performance.now();
        this._a2fTl("first_forward_to_bridge", { bytes: buf.byteLength });
      }
    } catch (e) {
      console.warn("[A2F] send failed → forcing reconnect:", e);
      this._a2fWsOpen = false;
      this._a2fSessionActive = false;
      this._a2fStopKeepAlive();
      try {
        this._a2fWs?.close();
      } catch {}
      this._a2fWs = null;
      if (buf instanceof ArrayBuffer) {
        if (this._a2fPendingAudio.length < 500)
          this._a2fPendingAudio.push(buf);
      }
      this._a2fConnect();
    }
  }

  _a2fPacerEnqueueOrSend(buf) {
    if (!A2F_FORWARD_PACING_ENABLED || A2F_FORWARD_PACING_RATE <= 0) {
      this._a2fSendBytesToBridge(buf);
      return;
    }
    const audioMs = pcm16Mono16000BytesToMs(buf.byteLength);
    this._pacerChunkCount++;
    this._pacerQueue.push({
      buf,
      audioMs,
      enqueuedAtWall: performance.now(),
    });
    if (this._pacerQueue.length > this._pacerMaxQueueDepth) {
      this._pacerMaxQueueDepth = this._pacerQueue.length;
    }
    this._a2fPacerKick();
  }

  _a2fPacerKick() {
    if (this._pacerDrainTimer) return;
    while (this._pacerQueue.length > 0) {
      const now = performance.now();
      const head = this._pacerQueue[0];
      // Warmup window: send unthrottled until the cumulative audio-ms
      // forwarded exceeds WARMUP_MS. Engage pacing on the first chunk
      // that would cross the threshold.
      if (!this._pacerEngaged) {
        if (
          this._pacerForwardedMs + head.audioMs <= A2F_FORWARD_PACING_WARMUP_MS
        ) {
          this._pacerQueue.shift();
          this._pacerHeldMsTotal += Math.max(0, now - head.enqueuedAtWall);
          this._a2fSendBytesToBridge(head.buf);
          this._pacerForwardedMs += head.audioMs;
          continue;
        }
        this._pacerEngaged = true;
        this._pacerNextAllowedWall = now;
      }
      if (now >= this._pacerNextAllowedWall) {
        this._pacerQueue.shift();
        this._pacerHeldMsTotal += Math.max(0, now - head.enqueuedAtWall);
        this._a2fSendBytesToBridge(head.buf);
        this._pacerForwardedMs += head.audioMs;
        this._pacerNextAllowedWall =
          now + head.audioMs / A2F_FORWARD_PACING_RATE;
        continue;
      }
      const wait = Math.max(1, this._pacerNextAllowedWall - now);
      this._pacerDrainTimer = setTimeout(() => {
        this._pacerDrainTimer = null;
        this._a2fPacerKick();
      }, wait);
      return;
    }
    // Queue empty — if EndOfAudio was deferred while we drained,
    // emit it now.
    if (this._pacerPendingEndOfAudio) {
      this._pacerPendingEndOfAudio = false;
      this._a2fEndSession("end");
    }
  }

  _a2fResetPacer(reason) {
    if (this._pacerDrainTimer) {
      clearTimeout(this._pacerDrainTimer);
      this._pacerDrainTimer = null;
    }
    const dropped = this._pacerQueue.length;
    this._pacerQueue.length = 0;
    this._pacerEngaged = false;
    this._pacerForwardedMs = 0;
    this._pacerNextAllowedWall = 0;
    this._pacerPendingEndOfAudio = false;
    this._pacerHeldMsTotal = 0;
    this._pacerMaxQueueDepth = 0;
    this._pacerChunkCount = 0;
    if (dropped > 0) {
      this._a2fTl("pacer_reset", { reason, droppedChunks: dropped });
    }
  }

  // Task #99 (tail fix): deterministic end-of-audio. The bridge holds its
  // final partial 1 s slice until it receives an explicit `end`, and the
  // ONLY previous trigger for `_a2fEndSession("end")` was ElevenLabs'
  // isFinal/audio_end/flush_complete marker — which routinely arrives many
  // seconds late or never. That late marker is exactly why the last word's
  // lip frames showed up after the audio had already finished, leaving the
  // mouth open with an un-synced last word. This watchdog ends the session
  // a short quiet window after ElevenLabs stops sending audio for a CLOSED
  // turn, independent of any marker. It is armed by `flushAndClose` (LLM
  // stream closed) and re-armed on every inbound audio chunk, so it only
  // fires once audio has truly stopped flowing. `_a2fEndSession("end")`
  // stays deferred behind the forward-pacer queue, so firing early is
  // harmless — the bridge still receives every paced byte before EOA.
  _a2fArmQuietEndWatchdog() {
    if (!this._a2fEnabled) return;
    this._a2fQuietEndArmed = true;
    this._a2fRescheduleQuietEndWatchdog();
  }

  _a2fRescheduleQuietEndWatchdog() {
    if (!this._a2fQuietEndArmed) return;
    if (this._a2fQuietEndTimer) {
      clearTimeout(this._a2fQuietEndTimer);
      this._a2fQuietEndTimer = null;
    }
    this._a2fQuietEndTimer = setTimeout(() => {
      this._a2fQuietEndTimer = null;
      if (!this._a2fQuietEndArmed) return;
      this._a2fQuietEndArmed = false;
      try {
        this._a2fTl("quiet_end_fire", { quietMs: this._a2fQuietEndMs });
      } catch (_) {}
      try {
        this._a2fEndSession("end");
      } catch (_) {}
    }, this._a2fQuietEndMs);
  }

  _a2fNoteElevenAudioArrived() {
    // Only meaningful after the turn's LLM stream has closed; until then
    // the gaps between sentences are normal and must not end the session.
    if (this._a2fQuietEndArmed) this._a2fRescheduleQuietEndWatchdog();
  }

  _a2fClearQuietEndWatchdog() {
    this._a2fQuietEndArmed = false;
    if (this._a2fQuietEndTimer) {
      clearTimeout(this._a2fQuietEndTimer);
      this._a2fQuietEndTimer = null;
    }
  }

  _a2fEndSession(mode = "stop") {
    if (!this._a2fEnabled) return;
    if (!this._a2fSessionActive) return;

    // Any actual end/stop supersedes a pending quiet-end watchdog.
    this._a2fClearQuietEndWatchdog();

    const gen = this._a2fActiveGen;

    // For graceful "end" mode, the second call (e.g. from the playback
    // drain handler) is a no-op — we already sent EndOfAudio and the
    // server is responsible for terminating the response stream.
    if (mode === "end" && this._a2fAudioInputDone) {
      return;
    }

    // Task #21: if the pacer is still holding bytes for this turn,
    // defer the EndOfAudio so the bridge receives all paced audio
    // before EOA. The pacer drain loop will re-call this method
    // once its queue is empty. Hard "stop" mode skips this — a stop
    // is meant to drop in-flight audio immediately.
    if (mode === "end" && this._pacerQueue.length > 0) {
      this._pacerPendingEndOfAudio = true;
      return;
    }

    try {
      if (this._a2fWsOpen) {
        this._a2fWs.send(JSON.stringify({ type: mode, gen }));
      }
    } catch (_) {}

    if (mode === "stop") {
      // Hard abort — tear down everything immediately. The bridge will
      // cancel the gRPC read loop on its side. Any frames already in
      // flight from the server will be ignored.
      this._a2fAcceptingFrames = false;
      this._a2fSessionActive = false;
      this._a2fAudioInputDone = false;

      this._a2fTl("session_stop_sent", { gen });
      this._a2fReleaseSyncGate();
      this._a2fSyncGate = null;

      this._a2fFirstFrameReceived = false;
      this._a2fFirstFrameTimeMs = null;
      this._a2fBufferedFrames = [];
      this._a2fDriverStarted = false;

      // Task #99: drop any pending computed-clock teardown. Doing it
      // here covers ALL "stop" mode call sites (stop, stopNow,
      // shutdown, _a2fDisconnect, etc.) without touching each one.
      this._clearComputedTeardown();

      this._v2log(`[A2F] Session stopped gen=`, gen);
    } else {
      // Graceful end — ElevenLabs has finished sending audio for this
      // turn. We forward EndOfAudio to the bridge so the gRPC iterator
      // can flush, but we do NOT do anything special to the sync gate:
      // the gate is already watching buffer fill and will release as
      // soon as enough frames are on hand (whether they arrived before
      // or after EndOfAudio is irrelevant to the gate). The session is
      // finalised when `a2f_done` arrives (or a hard `stop` is issued).
      this._a2fAcceptingFrames = false; // no more outbound audio
      this._a2fAudioInputDone = true;
      if (this._a2fTurnEndOfAudioMs == null && this._a2fTurnStartMs != null) {
        this._a2fTurnEndOfAudioMs = performance.now();
      }
      this._a2fTl("end_of_audio_sent", {
        gen,
        bufferedFrames: this._a2fBufferedFrames.length,
        firstFrameReceived: this._a2fFirstFrameReceived,
      });
      this._v2log(
        `[A2F] Audio input finished gen=`,
        gen,
        "— bridge will drain remaining frames",
      );
    }
  }

  _a2fStopDriver() {
    if (this._a2fLipSyncDriver) {
      try {
        this._a2fLipSyncDriver.stop();
      } catch (_) {}
    }
  }

  _a2fDisconnect() {
    this._a2fEndSession();
    this._a2fReleaseSyncGate();
    this._a2fSyncGate = null;
    this._a2fStopKeepAlive();

    this._a2fFirstFrameReceived = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fBufferedFrames = [];
    this._a2fDriverStarted = false;

    if (this._a2fWs) {
      try {
        this._a2fWs.close();
      } catch (_) {}
      this._a2fWs = null;
      this._a2fWsOpen = false;
    }

    this._a2fConnecting = false;
    this._a2fPendingAudio = [];
  }

  shutdown({ viaPageUnload = false } = {}) {
    console.log("[TTS] Shutdown requested");

    this._noteIdleActivity("shutdown");

    // A2F debug recorder: shutdown / page unload — finalize current
    // session as early termination so we still get a diagnostic file.
    // `viaPageUnload` is set true by the beforeunload listener so the
    // recorder uses its keepalive fetch shape (best-effort, may fail
    // for >64 KB bodies). Programmatic callers (e.g. session rotation
    // in StreamingTTSManager) leave it false → reliable plain fetch.
    this._a2fDebugFinalize(true, viaPageUnload);

    this._userCanceled = true;
    this._hardClose();
    this._resetFirstChunkBuffer();
    this._resetAudio();
    this._resetText();
    this._zeroLipSyncBlendshapes();
    this._PlayIdleAnimaiton();

    // reset stop gate
    this._resetStopGate();

    // Close A2F bridge
    this._a2fDisconnect();

    // Drop any per-chunk timing records — same rationale as cancel().
    if (this._a2fChunkTimings && this._a2fChunkTimings.size) {
      for (const r of this._a2fChunkTimings.values()) {
        if (r && r._pendingEmitTimer) {
          clearTimeout(r._pendingEmitTimer);
          r._pendingEmitTimer = null;
        }
      }
      this._a2fChunkTimings.clear();
    }

    this._userCanceled = false;
    this._awaitingNewResponse = true;
    this._streamActive = false;
    this._clearStreamIdleTimer();
  }

  setVoiceId(voiceId) {
    console.log("[TTS] Voice changed to " + voiceId);
    this.voiceId = voiceId;
  }

  /* ---------- Volume API ---------- */
  setVolume(v) {
    const val = Math.max(0, Math.min(1, Number(v)));
    this.volume = val;
    if (this.masterGain)
      this.masterGain.gain.value = this.muted ? 0 : this.volume;
    this.events.emit("volume-change", {
      volume: this.getVolume(),
      muted: this.muted,
    });
    this._noteIdleActivity("setVolume");
  }

  getVolume() {
    return this.muted ? 0 : this.volume;
  }

  mute() {
    this.muted = true;
    if (this.masterGain) this.masterGain.gain.value = 0;
    this.events.emit("mute-change", { muted: true, volume: this.getVolume() });
    this._noteIdleActivity("mute");
  }

  unmute() {
    this.muted = false;
    if (this.masterGain) this.masterGain.gain.value = this.volume;
    this.events.emit("mute-change", { muted: false, volume: this.getVolume() });
    this._noteIdleActivity("unmute");
  }

  toggleMute() {
    this.muted ? this.unmute() : this.mute();
  }

  /* ---------- STOP button behavior ---------- */

  // New stop() method that properly resets everything
  async stop() {
    console.log("[TTS] stop(): preempting all playback and clearing queues");

    this._noteIdleActivity("stop");
    // Task #21: stop is a hard preempt — drop paced bytes.
    this._a2fResetPacer("stop");

    // Stop all active audio
    await this._preemptStopAllAudio(160);

    // Keep drop shield ACTIVE - it will be reset by handleLLMDelta()
    // when the first text chunk of the new response arrives.
    // This prevents any lingering audio from the previous response from playing.
    this._audioEpoch++;
    this._droppingIncomingAudio = true;
    this._hasBeganThisEpoch = false;
    this._primerSentThisEpoch = false;
    this._userStoppedCurrentResponse = true; // Block residual deltas from resetting drop shield

    // Reset playing flag so _playLoop can restart
    this.playing = false;

    // Clear any pending outbound messages
    this.outboundQueue.length = 0;
    this.pendingBatches = 0;
    this._resetFirstChunkBuffer();

    // Clear audio queue
    this._clearAudioQueueOnly();

    // Reset text processing state
    this._resetText();

    // Ensure lip sync is ready for next response
    if (this._lipSyncTap) {
      try {
        this._lipSyncTap.stop();
        console.log("[TTS] Lip sync stopped");
      } catch (e) {
        console.warn("[TTS] Error stopping lip sync:", e);
      }
    }

    // Zero mouth shapes and go to idle
    this._zeroLipSyncBlendshapes();
    this._PlayIdleAnimaiton();

    // A2F: stop driver + end current session (active gen)
    this._a2fAcceptingFrames = false;
    if (this._a2fSyncGateResolve) this._a2fReleaseSyncGate();
    this._a2fSyncGate = null;
    this._a2fStopDriver();
    if (this._a2fLipSyncDriver) {
      try {
        this._a2fLipSyncDriver.reset();
      } catch (_) {}
    }
    this._a2fDriverStarted = false;
    this._a2fFirstFrameReceived = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fBufferedFrames = [];
    this._a2fEndSession();

    // Reset stop gate
    this._resetStopGate();

    // Reset timeline
    this._resetTimeline();

    // Ensure audio context is ready for new audio (especially for Safari)
    try {
      await this._ensureAudioContext();
    } catch (e) {
      console.warn("[TTS] Failed to ensure audio context in stop:", e);
    }

    console.log(
      "[TTS] Stop complete - drop shield active, waiting for new turn to reset",
    );
  }

  async stopNow(options = {}) {
    const fadeMs = options.fadeMs === undefined ? 160 : Number(options.fadeMs);
    console.log("[TTS] stopNow()");

    this._noteIdleActivity("stopNow");

    // Task #99 (diagnostics): hard stop / barge-in. Emit the interrupted
    // turn's ledger before any state is torn down so a stopped reply
    // still leaves a record, then halt the heartbeat.
    try {
      if (
        !this._audioStatsPublished &&
        this._audioStats &&
        this._audioStats.received > 0
      ) {
        this._audioStatsPublished = true;
        this._publishAudioStats("stop");
      }
    } catch (_) {}
    this._stopLedgerHeartbeat();

    // Task #21: hard stop — drop paced bytes.
    this._a2fResetPacer("stopNow");

    // stop current & block new
    this._sendFlush();
    this._audioEpoch++;
    this._droppingIncomingAudio = true;
    this._hasBeganThisEpoch = false;
    this._primerSentThisEpoch = false;
    // Task #33: drop any in-flight chunk-recorder session on hard stop.
    if (this._elevenChunkRecorder) {
      this._elevenChunkRecorder.stop({ reason: "stopNow" });
    }
    this._userStoppedCurrentResponse = true; // Block residual deltas from resetting drop shield

    // clear pending outbound
    this.outboundQueue.length = 0;
    this.pendingBatches = 0;
    this._resetFirstChunkBuffer();

    // clear queued audio + fade/stop current
    this._clearAudioQueueOnly();
    await this._preemptStopAllAudio(fadeMs);

    // pause lip sync & zero mouth
    this._pauseLipSync();
    this._zeroLipSyncBlendshapes();

    // A2F: stop driver + end current session (active gen)
    this._a2fAcceptingFrames = false;
    if (this._a2fSyncGateResolve) this._a2fReleaseSyncGate();
    this._a2fSyncGate = null;

    // tear down A2F: stop driver + end gRPC session
    this._a2fStopDriver();
    if (this._a2fLipSyncDriver) {
      try {
        this._a2fLipSyncDriver.reset();
      } catch (_) {}
    }
    this._a2fDriverStarted = false;
    this._a2fFirstFrameReceived = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fBufferedFrames = [];
    this._a2fEndSession();

    // animation back to idle
    this._PlayIdleAnimaiton();

    // reset gate (Stop click → all false)
    this._resetStopGate();

    // reset timeline
    this._resetTimeline();

    if (options.hard && !this._userCanceled) {
      // Task #274 (iOS jetsam): a `_sendFlush()` alone does NOT stop
      // ElevenLabs from finishing the interrupted reply — the rest of its
      // audio keeps streaming in over the WS and gets shield-dropped chunk
      // by chunk, burning network/CPU/GC on a phone that is already under
      // memory pressure (confirmed [PAGE-KILL] during mic barge-in).
      // Hard-closing the socket cancels the dead generation server-side.
      // Suppress the close handler's auto-reconnect: the next turn's
      // beginNewResponse → _ensureConnected() opens the fresh socket.
      this._suppressReconnectOnce = true;
      this._userCanceled = true;
      this._hardClose();
      this._userCanceled = false;
    }

    // NOTE: We intentionally do NOT call allowUpcoming() here.
    // The drop shield (_droppingIncomingAudio = true) must remain active
    // until a new assistant turn begins. This prevents queued audio chunks
    // that are still arriving from ElevenLabs from playing after user stops.

    console.log(
      "[TTS] stopNow complete - drop shield active, waiting for new turn",
    );
  }

  stopUpcomingOnly() {
    this._sendFlush();
    this._droppingIncomingAudio = true;
    this.outboundQueue.length = 0;
    this.pendingBatches = 0;
    this._resetFirstChunkBuffer();
    this._noteIdleActivity("stopUpcomingOnly");
    // Task #21: drop paced bytes for the cancelled upcoming turn.
    this._a2fResetPacer("stopUpcomingOnly");
  }

  allowUpcoming() {
    if (this._droppingIncomingAudio) {
      console.log("[TTS] allowUpcoming(): Now accepting audio buffers");
      this._droppingIncomingAudio = false;

      // Ensure playback can start if there are queued audio buffers
      if (this.audioQueue.length > 0 && !this.playing) {
        this._playLoop();
      }
    }
  }

  // ===== NEW: convenience volume setters =====
  /**
   * Normalize incoming value:
   * - If >1 and <=100, treat as percentage (e.g. 70 => 0.7)
   * - Clamp to [0,1]
   */
  _normalizeVolumeInput(value) {
    let v = Number(value);
    if (!Number.isFinite(v)) return this.getVolume();
    if (v > 1 && v <= 100) v = v / 100;
    return Math.max(0, Math.min(1, v));
  }

  /**
   * Temporarily change volume and remember the old one.
   * @param {number} value 0..1 or 0..100
   * @param {{fadeMs?: number}=} opts
   */
  async ModifiedVloume(value, opts = {}) {
    // save current volume before overwriting
    this._lastVolume = this.getVolume();

    const v = this._normalizeVolumeInput(value);
    const fadeMs = Math.max(0, Number(opts.fadeMs || 0));
    await this._ensureAudioContext();

    if (this.masterGain && this.audioCtx && fadeMs > 0) {
      const g = this.masterGain.gain;
      const now = this.audioCtx.currentTime;
      try {
        g.cancelScheduledValues(now);
      } catch {}
      try {
        g.setValueAtTime(g.value, now);
      } catch {}
      try {
        g.linearRampToValueAtTime(v, now + fadeMs / 1000);
      } catch {}
    } else if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : v;
    }

    this.volume = v;
    this.muted = false; // make sure not muted when explicitly setting volume
    this.events.emit("volume-change", {
      volume: this.getVolume(),
      muted: this.muted,
    });

    this._noteIdleActivity("ModifiedVloume");
  }

  /**
   * Restore the last remembered volume (if any).
   * @param {{fadeMs?: number}=} opts
   */
  async restoreVolume(opts = {}) {
    if (this._lastVolume == null) {
      console.warn("[TTS] No previous volume saved");
      return;
    }
    const fadeMs = Math.max(0, Number(opts.fadeMs || 0));
    await this.ModifiedVloume(this._lastVolume, { fadeMs });
    this._lastVolume = null; // clear so it doesn't keep restoring forever
  }

  /* -------------------- Internals -------------------- */

  _resetTimeline() {
    this._timeline.armed = false;
    this._timeline.started = false;
    this._timeline.ctxStart = 0;
    this._timeline.wallStart = 0;
    this._timeline.nextCtxTime = 0;
    if (this._timelineStartTimer) {
      clearTimeout(this._timelineStartTimer);
      this._timelineStartTimer = null;
    }
    // Task #99: the timeline is the basis for the computed-clock
    // teardown — resetting it invalidates any pending teardown timer.
    this._clearComputedTeardown();
  }

  _pauseLipSync() {
    try {
      if (this._lipSyncTap && this._lipSyncTap.running) this._lipSyncTap.stop();
    } catch (e) {}
  }

  /**
   * Zero out lipsync morphs.
   *
   * Hard path (default): synchronous `reset()` — drops the queue and snaps
   * shapes to 0. Used by interrupts/cancels/shutdown where we MUST stop
   * animating immediately and the visible snap is acceptable (the avatar
   * was just yanked out of the user's intent).
   *
   * Graceful path (`graceful: true`): hand off to `flushAndDecay()` which
   * applies the last queued frame, runs an rAF decay over ~400 ms, and
   * THEN snaps to 0. Used at natural end-of-audio so the mouth doesn't
   * freeze open mid-vowel and the abandoned frame tail still plays.
   * Task #33.
   */
  _zeroLipSyncBlendshapes({ graceful = false } = {}) {
    if (this._useA2FLipSync && this._a2fLipSyncDriver) {
      try {
        if (graceful) {
          this._a2fLipSyncDriver.flushAndDecay();
        } else {
          this._a2fLipSyncDriver.reset();
        }
      } catch (_) {}
      return;
    }
    try {
      const c = this._lipSyncController;
      if (!c) return;

      if (typeof c.zeroAllPhonemes === "function") {
        try {
          c.zeroAllPhonemes();
        } catch (_) {}
        return;
      }

      const names = new Set();
      try {
        if (c.mapper && c.supportedPhonemes && c.mapper.standardPhonemes) {
          for (const ph of c.supportedPhonemes) {
            const entry = c.mapper.standardPhonemes[ph];
            if (Array.isArray(entry))
              for (const nm of entry) names.add(String(nm));
            else if (entry && typeof entry === "object")
              for (const nm of Object.keys(entry)) names.add(String(nm));
          }
        }
      } catch (_) {}
      if (names.size === 0 && c.blendShapeControls) {
        const re =
          /(viseme|phoneme|mouth|lip|AA|AE|AH|AO|EH|ER|IH|IY|UH|UW|MBP|FV|L|R|S|SH|TH|CH|JH|D|T|K|G|P|B|M|N|NG|O|U|E|A|I|OO|MOUTH)/i;
        for (const key of Object.keys(c.blendShapeControls))
          if (re.test(key)) names.add(key);
      }
      if (names.size > 0 && c.blendShapeControls) {
        for (const nm of names) {
          const raw = c.blendShapeControls[nm];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
          for (const ctl of items) {
            if (ctl && ctl.mesh && typeof ctl.index === "number") {
              try {
                ctl.mesh.morphTargetInfluences[ctl.index] = 0;
              } catch (_) {}
              try {
                ctl.value = 0;
              } catch (_) {}
            }
          }
        }
      }
    } catch (e) {
      console.warn("[LipSync] zero blendshapes failed", e);
    }
  }

  _noteStreamActivity() {
    this._streamActive = true;

    // Stream activity = active
    this._noteIdleActivity("stream-activity");

    this._clearStreamIdleTimer();
    this._streamIdleTimer = setTimeout(
      () => {
        this._streamActive = false;
      },
      Math.max(300, this.newResponseIdleMs),
    );
  }

  _clearStreamIdleTimer() {
    if (this._streamIdleTimer) {
      clearTimeout(this._streamIdleTimer);
      this._streamIdleTimer = null;
    }
  }

  async _ensureConnected() {
    if (this.wsOpen && this.ws && this.ws.readyState === WebSocket.OPEN) return;
    await this._connectElevenLabs();
  }

  // Task #99: return the next COMPLETE phrase from `this.buf`, or "" when
  // only a partial sentence remains. Order of preference:
  //   1. A terminal-punctuation sentence boundary (. ! ? : ;) followed by
  //      whitespace — the natural utterance unit.
  //   2. A hard newline boundary.
  //   3. A safety-cap word break once the buffer exceeds
  //      `phraseSafetyCapChars`, so a punctuation-free reply can't buffer
  //      forever.
  //
  // Task #99 (rejection fix): the previous "first-phrase early
  // comma/clause break" tier was REMOVED. Emitting a tiny opening
  // fragment to ElevenLabs reintroduced the well-documented
  // dropped/clipped opening-words symptom (the same reason
  // `firstPhraseFast` + `try_trigger_generation` were removed earlier).
  // First-audio latency is instead kept low by the prompt instruction to
  // open with a short sentence — which produces a real terminal boundary
  // quickly via tier 1, with no fragmentation.
  _takePhrase() {
    const s = this.buf;
    if (!s) return "";

    // 1. Sentence boundary: terminal punctuation (+ optional closing
    // quote/bracket) followed by whitespace.
    let m = s.match(/^([\s\S]*?[.!?:;]["'\)\]]?\s)([\s\S]*)$/);
    if (m && m[1].trim()) {
      this.buf = m[2];
      return m[1].trim();
    }

    // 2. Newline boundary.
    m = s.match(/^([\s\S]*?\n)([\s\S]*)$/);
    if (m && m[1].trim()) {
      this.buf = m[2];
      return m[1].trim();
    }

    // 3. Safety cap: emit at the last word break to avoid unbounded
    // buffering when no punctuation ever arrives.
    if (s.length >= this.phraseSafetyCapChars) {
      let cut = s.lastIndexOf(" ", this.phraseSafetyCapChars);
      if (cut < 40) cut = this.phraseSafetyCapChars;
      const phrase = s.slice(0, cut).trim();
      this.buf = s.slice(cut);
      if (phrase) return phrase;
    }

    return "";
  }

  enableLipSync(lipSyncController, opts, characterModelId) {
    opts = opts || {};
    this._wantLipSync = true;
    this._lipSyncController =
      lipSyncController || new MinimalLipSyncController();
    for (const k in opts)
      if (Object.prototype.hasOwnProperty.call(opts, k)) {
        this._lipSyncOpts[k] = opts[k];
      }

    if (
      characterModelId &&
      shouldUseA2FLipSync(characterModelId) &&
      lipSyncController &&
      lipSyncController.blendShapeControls
    ) {
      this._useA2FLipSync = true;
      this._a2fLipSyncDriver = new A2FLipSyncDriver({
        blendShapeControls: lipSyncController.blendShapeControls,
        debug: true,
        // Render lead-lag is now folded into the audio clock closure below
        // via the REAL measured `audioCtx.outputLatency` (Task #99 A), so
        // the fixed jitter offset is no longer needed — set it to 0. The
        // ~1800ms start lead keeps frames buffered well ahead, so no late-
        // frame cushion is required here.
        jitterMs: 0,
      });
      // Drive the lip cursor from the audio clock — the A2F-timecode
      // source of truth. Every frame's `time_ms` is stamped off the same
      // 16 kHz PCM byte stream the speaker plays, so reading the cursor
      // straight from AudioContext.currentTime (via _a2fCurrentAudioClockMs,
      // already normalized to "first sample == 0") makes the mouth track
      // the audio with zero drift over long replies. Returns null during
      // the pre-audio lead-in, so the driver holds frame 0 until playback
      // actually starts and never re-anchors the once-per-turn syncClock.
      this._a2fLipSyncDriver.setAudioClock(() => {
        const clk = this._a2fCurrentAudioClockMs();
        if (!clk || !Number.isFinite(clk.value)) return null;
        // Task #99 (A): fold the REAL output latency into the lip cursor so
        // the mouth tracks what is actually HEARD, not what the audio
        // context has merely processed. A sample at `ctx.currentTime`
        // reaches the speaker `outputLatency` seconds later, so the audio
        // being heard right now sits at `cursor - outputLatency`. This
        // replaces the old fixed ~40ms jitter guess with a per-device
        // measurement. We apply it ONLY here (the lip cursor), not inside
        // `_a2fCurrentAudioClockMs`, so the starvation/lead diagnostics keep
        // measuring the true playhead. Falls back to `baseLatency`, then a
        // 40ms constant, on browsers that don't expose `outputLatency`
        // (e.g. Safari); clamped so an absurd device value can't shove the
        // mouth far out of sync.
        const ctx = this.audioCtx;
        let latMs = 40;
        if (ctx) {
          if (Number.isFinite(ctx.outputLatency) && ctx.outputLatency > 0) {
            latMs = ctx.outputLatency * 1000;
          } else if (Number.isFinite(ctx.baseLatency) && ctx.baseLatency > 0) {
            latMs = ctx.baseLatency * 1000;
          }
        }
        if (latMs > 250) latMs = 250;
        return clk.value - latMs;
      });
      // Single source of truth for facial expression: A2F's per-utterance
      // emotion vector → curated ARKit-52 blendshape mapping. Renderer
      // subscribes to the driver via `onEmotions` (target updates) and
      // `onAfterTick` (per-frame apply during speech) — see
      // A2FEmotionRenderer.js for the mapping table and decay behaviour.
      try {
        this._a2fEmotionRenderer = new A2FEmotionRenderer({
          blendShapeControls: lipSyncController.blendShapeControls,
          driver: this._a2fLipSyncDriver,
          debug: true,
        });
        // Expose on the lipSyncController so debug UIs (and the model
        // viewer's debug emotion panel) can pin a manual vector without
        // having to reach into the TTS instance.
        try {
          lipSyncController.a2fEmotionRenderer = this._a2fEmotionRenderer;
        } catch (_) {}
      } catch (e) {
        try {
          console.warn("[A2FEmotionRenderer] init failed:", e);
        } catch (_) {}
      }
      // Task #27: hand the driver a reference to the per-instance
      // recorder + a small snapshot fn so it can stamp the master
      // audio clock + AudioContext.currentTime + playbackStartTime
      // on each render sample. The fn must NOT throw — wrapped in
      // try/catch so a hot-path call site never has to think about
      // it. When the recorder is absent (debug flag off / pre-init)
      // the driver-side hook short-circuits on isRecording.
      try {
        if (this._a2fDebugRecorder) {
          this._a2fLipSyncDriver.setDebugContext({
            recorder: this._a2fDebugRecorder,
            getDebugSnapshot: () => {
              try {
                const clk = this._a2fCurrentAudioClockMs();
                const currentAudioTimeMs =
                  clk && Number.isFinite(clk.value) ? clk.value : null;
                let audioContextCurrentTime = null;
                try {
                  audioContextCurrentTime =
                    this.audioCtx && this.audioCtx.state !== "closed"
                      ? this.audioCtx.currentTime
                      : null;
                } catch (_) {}
                const playbackStartTime =
                  this._timeline && Number.isFinite(this._timeline.wallStart)
                    ? this._timeline.wallStart
                    : null;
                return {
                  currentAudioTimeMs,
                  audioContextCurrentTime,
                  playbackStartTime,
                };
              } catch (_) {
                return {
                  currentAudioTimeMs: null,
                  audioContextCurrentTime: null,
                  playbackStartTime: null,
                };
              }
            },
          });
        }
      } catch (e) {
        try {
          console.warn("[A2FDebug] setDebugContext wiring failed:", e);
        } catch (_) {}
      }
      console.log(
        `[A2F] Model ${characterModelId} configured for A2F lip sync`,
      );
    } else {
      this._useA2FLipSync = false;
      this._a2fLipSyncDriver = null;
      if (characterModelId) {
        console.log(
          `[LipSync] Model ${characterModelId} using phoneme-based lip sync`,
        );
      }
    }

    this._lslog("enableLipSync called", {
      hasController: !!lipSyncController,
      characterModelId: characterModelId || "none",
      useA2FLipSync: this._useA2FLipSync,
      opts: this._lipSyncOpts,
    });

    if (this.audioCtx && !this._useA2FLipSync) this._ensureLipSyncTap();
  }

  /** Turn off lip-sync and release analyser/tap. */
  disableLipSync() {
    this._lslog("disableLipSync called");
    this._wantLipSync = false;

    try {
      if (this._lipSyncTap && this._lipSyncTap.running) this._lipSyncTap.stop();
    } catch (_) {}
    this._lipSyncTap = null;

    try {
      if (this._analyser) this._analyser.disconnect();
    } catch (_) {}
    this._analyser = null;

    // Dispose renderer FIRST so its onAfterTick listener is unregistered
    // before the driver's reset/null-out tears down the listener array.
    if (this._a2fEmotionRenderer) {
      try {
        this._a2fEmotionRenderer.dispose();
      } catch (_) {}
      this._a2fEmotionRenderer = null;
    }
    try {
      if (this._lipSyncController) {
        this._lipSyncController.a2fEmotionRenderer = null;
      }
    } catch (_) {}

    // Task #99: drop any pending computed-clock teardown before the
    // driver is nulled.
    this._clearComputedTeardown();

    if (this._a2fLipSyncDriver) {
      this._a2fLipSyncDriver.reset();
      this._a2fLipSyncDriver = null;
    }
    this._useA2FLipSync = false;

    this._tapCalibrated = false;
  }

  async _connectElevenLabs() {
    if (!this.voiceId) throw new Error("voiceId is required");
    if (this.wsOpen && this.ws && this.ws.readyState === WebSocket.OPEN) return;
    // Task #274: a connect is already in flight — don't stack a second
    // socket on top of it (barge-in hard-close made concurrent
    // _ensureConnected calls more likely).
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;

    this.readyPromise = new Promise((res) => {
      this.readyResolve = res;
    });

    const qs = new URLSearchParams({
      model_id: this.modelId,
      output_format: this.outputFormat,
      sync_alignment: this.syncAlignment ? "true" : "false",
      inactivity_timeout: String(this.inactivityTimeoutSec),
    });
    const url = `wss://api.elevenlabs.io/v1/text-to-speech/${encodeURIComponent(this.voiceId)}/stream-input?${qs.toString()}`;
    console.log("[WS] Connecting to:", url);

    if (typeof WebSocket === "undefined")
      throw new Error("WebSocket is not available in this environment");

    this.ws = new WebSocket(url);
    this.ws.binaryType = "arraybuffer";

    if (!this.ws || typeof this.ws.addEventListener !== "function") {
      console.error("[WS] WebSocket instance is invalid");
      throw new Error("WebSocket instance invalid");
    }

    this.ws.addEventListener("open", () => {
      console.log("[WS] Connection open");
      this._reconnecting = false;
      this._reconnectAttempt = 0;
      this.wsOpen = true;

      this._startKeepAlive();

      // Task #33: lazy-init the raw chunk recorder. Costs nothing when
      // disabled (isEnabled() check short-circuits before any allocation).
      // Task #37: client-side master off-switch. When
      // `ELEVEN_CHUNK_RECORDER_ENABLED` is false, the recorder is never
      // constructed, so all `if (this._elevenChunkRecorder)` guards at
      // the start/stop/recordChunk call-sites short-circuit and no
      // POSTs to `/api/debug/elevenlabs-chunks` are made.
      if (
        ELEVEN_CHUNK_RECORDER_ENABLED &&
        !this._elevenChunkRecorder &&
        ElevenLabsChunkRecorder.isEnabled()
      ) {
        this._elevenChunkRecorder = new ElevenLabsChunkRecorder();
      }
      if (this._elevenChunkRecorder) {
        this._elevenChunkRecorder.start({
          outputFormat: this.outputFormat,
          voiceId: this.voiceId,
          turnId: this._audioEpoch,
          wallStart: performance.now(),
          reason: "ws-open",
        });
      }

      const initMsg = {
        xi_api_key: this.apiKey,
        text: " ",
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.85,
          style: 0,
          use_speaker_boost: true,
        },
        // Task #33: follow ElevenLabs' documented realtime WS flow
        // (https://elevenlabs.io/docs/eleven-api/guides/how-to/websockets/realtime-tts).
        // A/B test (controlled by `ELEVEN_AUTO_MODE` at the top of this
        // file): `auto_mode: true` lets ElevenLabs auto-tune buffering
        // and is their recommended fix for missing-audio-at-start; the
        // manual `chunk_length_schedule` is the previous behavior.
        generation_config: ELEVEN_AUTO_MODE
          ? { auto_mode: true }
          : { chunk_length_schedule: [370, 400, 430, 470] },
      };
      console.log(
        `[WS] init generation_config mode=${ELEVEN_AUTO_MODE ? "auto_mode" : "chunk_length_schedule"}`,
      );
      console.log("[WS] Sending init message");
      try {
        this.ws.send(JSON.stringify(initMsg));
      } catch (e) {
        console.warn("[WS] init send failed", e);
      }
      console.log("🎤 [RealtimeTTS] ElevenLabs streaming started");
      this._a2fConnect();
      this._drainOutboundQueue();
      if (this.readyResolve) this.readyResolve();
    });

    this.ws.addEventListener("message", async (ev) => {
      try {
        if (this._droppingIncomingAudio) {
          // Task #99 (accounting): if a real audio frame is discarded by
          // the barge-in shield, record it so "lost the start of a word"
          // is attributable instead of invisible.
          if (
            ev.data instanceof ArrayBuffer ||
            (typeof Blob !== "undefined" && ev.data instanceof Blob)
          ) {
            this._audioStats.droppedShield++;
          }
          return;
        }

        if (ev.data instanceof ArrayBuffer) {
          const epoch = this._audioEpoch;
          this._a2fStartSession();
          this._a2fNoteFirstElevenChunk(ev.data.byteLength, "binary");
          if (this._elevenChunkRecorder)
            this._elevenChunkRecorder.recordChunk(ev.data, "binary");
          this._a2fForwardAudio(ev.data);
          this._a2fNoteElevenAudioArrived();
          this._wsNoteAudio();
          this._audioStats.received++;
          this.audioQueue.push({ epoch, buf: ev.data });
          this._playLoop();
          // Task #26: record after the audio has actually been queued.
          this._a2fDebugRecordElevenLabsChunk(
            ev.data.byteLength,
            "binary",
            true,
          );
          return;
        }
        if (typeof Blob !== "undefined" && ev.data instanceof Blob) {
          const buf = await ev.data.arrayBuffer();
          const epoch = this._audioEpoch;
          this._a2fStartSession();
          this._a2fNoteFirstElevenChunk(buf.byteLength, "blob");
          if (this._elevenChunkRecorder)
            this._elevenChunkRecorder.recordChunk(buf, "blob");
          this._a2fForwardAudio(buf);
          this._a2fNoteElevenAudioArrived();
          this._wsNoteAudio();
          this._audioStats.received++;
          this.audioQueue.push({ epoch, buf });
          this._playLoop();
          // Task #26
          this._a2fDebugRecordElevenLabsChunk(buf.byteLength, "blob", true);
          return;
        }
        if (typeof ev.data === "string") {
          const msg = JSON.parse(ev.data);
          const b64 = msg.audio || msg.audio_base64 || null;
          // Task #347 — the alignment payloads we asked for with
          // sync_alignment:true used to be discarded right here. Aggregate
          // them into per-turn word timings (LECTURE_STAGE_NOTES kickoff (a)).
          // Purely additive accounting: never gates, delays or drops audio.
          const align = msg.alignment || msg.normalizedAlignment ||
            msg.normalized_alignment || null;
          if (align) {
            try {
              const n = this._wsAlignment.push(align, this._alignAudioMsSoFar || 0);
              if (n > 0) {
                this._audioStats.alignMsgs++;
                this._audioStats.alignChars += n;
              }
            } catch (_) {}
          }
          if (b64) this._alignNoteAudioBytes(Math.floor(b64.length * 0.75));
          if (b64) {
            if (!this._droppingIncomingAudio) {
              const audioBuf = this._b64ToArrayBuffer(b64);
              const epoch = this._audioEpoch;
              this._a2fStartSession();
              this._a2fNoteFirstElevenChunk(audioBuf.byteLength, "base64");
              if (this._elevenChunkRecorder)
                this._elevenChunkRecorder.recordChunk(audioBuf, "base64");
              this._a2fForwardAudio(audioBuf);
              this._a2fNoteElevenAudioArrived();
              this._wsNoteAudio();
              this._audioStats.received++;
              this.audioQueue.push({ epoch, buf: audioBuf });
              this._playLoop();
              // Task #26
              this._a2fDebugRecordElevenLabsChunk(
                audioBuf.byteLength,
                "base64",
                true,
              );
            }
          }
          if (
            msg.isFinal ||
            msg.final ||
            msg.done ||
            msg.event === "audio_end" ||
            msg.type === "flush_complete"
          ) {
            this._a2fEndSession("end");
            if (this.pendingBatches > 0) this.pendingBatches -= 1;
            try {
              this._audioStats.elevenAcks++;
            } catch (_) {}
            this._attemptCloseIfDrained();
          }
          if (msg.error || msg.type === "error")
            console.error("[WS][ERR]:", msg);
          return;
        }
        console.warn("[WS] Unknown frame type", typeof ev.data, ev.data);
      } catch (e) {
        console.warn("[WS] message handler error:", e);
      }
    });

    this.ws.addEventListener("error", (e) => {
      console.error("[WS] Error:", e);
      this._stopKeepAlive();
      this._a2fEndSession();
    });

    this.ws.addEventListener("close", async () => {
      console.log("[WS] Connection closed]");
      this.wsOpen = false;
      this._stopKeepAlive();
      this._a2fEndSession();

      // Task #274: consume the one-shot suppression set by a barge-in
      // hard-close (see stopNow). The next turn's beginNewResponse →
      // _ensureConnected() opens the fresh socket instead.
      if (this._suppressReconnectOnce) {
        this._suppressReconnectOnce = false;
        console.log("[WS] Reconnect suppressed (barge-in hard-close)");
        return;
      }

      if (this.persistentWs && !this._userCanceled) {
        await this._scheduleReconnect();
      } else if (!this.persistentWs) {
        if (!this._userCanceled && !this.closeAfterDrain) {
          console.log("[WS] Attempting quick reconnect...");
          try {
            await this._connectElevenLabs();
            this._drainOutboundQueue();
          } catch (e) {
            console.warn("[WS] Reconnect failed", e);
          }
        }
      }
    });

    await this.readyPromise;
  }

  _startKeepAlive() {
    this._stopKeepAlive();
    const interval = this._keepAliveIntervalMs;
    this._wsKeepAliveTimer = setInterval(() => {
      try {
        if (this.wsOpen && this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ text: " " }));
        }
      } catch (e) {
        console.warn("[WS] keep-alive send failed", e);
      }
    }, interval);
    console.log(
      `[WS] Keep-alive started: every ${Math.round(interval / 1000)}s`,
    );
  }

  _stopKeepAlive() {
    if (this._wsKeepAliveTimer) {
      clearTimeout(this._wsKeepAliveTimer);
      this._wsKeepAliveTimer = null;
      console.log("[WS] Keep-alive stopped");
    }
  }

  async _scheduleReconnect() {
    if (this._reconnecting) return;
    this._reconnecting = true;

    const n = Math.min(
      this._maxBackoffMs,
      Math.floor(this._baseBackoffMs * Math.pow(2, this._reconnectAttempt)),
    );
    const jitter = Math.floor(Math.random() * 250);
    const wait = Math.max(300, n + jitter);

    this._reconnectAttempt += 1;
    console.log(
      `[WS] Reconnecting in ~${wait}ms (attempt ${this._reconnectAttempt})`,
    );
    await new Promise((r) => setTimeout(r, wait));

    try {
      await this._connectElevenLabs();
      this._drainOutboundQueue();
    } catch (e) {
      console.warn("[WS] Reconnect attempt failed", e);
      this._reconnecting = false;
      await this._scheduleReconnect();
    }
  }

  _enqueuePhrase(text) {
    if (!text) return;
    // Task #33 decoy primer: prepend a throwaway leading text to the
    // FIRST phrase of each new epoch so the ElevenLabs "missing audio
    // at start" symptom lands on the decoy instead of the real words.
    // Controlled by `ELEVEN_FIRST_TEXT_PRIMER` at the top of this file.
    if (!this._primerSentThisEpoch && ELEVEN_FIRST_TEXT_PRIMER) {
      text = ELEVEN_FIRST_TEXT_PRIMER + text;
      console.log(
        `[TTS] Prepending decoy primer to first phrase: ${JSON.stringify(ELEVEN_FIRST_TEXT_PRIMER)}`,
      );
    }
    // Task #99: prepend one inaudible leading space to the FIRST phrase of
    // the turn so ElevenLabs' generation-start audio-eat lands on silence
    // instead of the opening words ("As you asked, let ..." losing its head).
    if (!this._primerSentThisEpoch && ELEVEN_LEADING_SPACE_PRIME) {
      text = " " + text;
    }
    this._primerSentThisEpoch = true;

    // Task #7 first-chunk buffer: hold the first outgoing payload of the
    // turn until it reaches MIN_CHARS or the idle timeout fires. After
    // the first flush, behave exactly as today.
    if (ELEVEN_FIRST_CHUNK_BUFFER_ENABLED && !this._firstChunkSent) {
      this._firstChunkBuffer += text;
      if (this._firstChunkBuffer.length >= ELEVEN_FIRST_CHUNK_MIN_CHARS) {
        this._flushFirstChunkBuffer("size");
      } else {
        this._armFirstChunkTimer();
      }
      return;
    }

    this.outboundQueue.push({ kind: "text", text });
    this._drainOutboundQueue();
  }
  _enqueueFlush() {
    // Task #7: ensure any buffered first chunk goes out BEFORE the flush
    // marker so ElevenLabs receives the opening text in this generation.
    if (
      ELEVEN_FIRST_CHUNK_BUFFER_ENABLED &&
      !this._firstChunkSent &&
      this._firstChunkBuffer.length > 0
    ) {
      this._flushFirstChunkBuffer("eot");
    }
    this.outboundQueue.push({ kind: "flush" });
    this._drainOutboundQueue();
  }

  // Task #7 first-chunk buffer helpers. The timer is rearmed on every
  // buffered phrase, so it only fires after `ELEVEN_FIRST_CHUNK_TIMEOUT_MS`
  // of true silence from the LLM (not just `MS` after the first phrase).
  _armFirstChunkTimer() {
    if (this._firstChunkTimer) {
      clearTimeout(this._firstChunkTimer);
      this._firstChunkTimer = null;
    }
    this._firstChunkTimer = setTimeout(() => {
      this._firstChunkTimer = null;
      if (this._firstChunkSent) return;
      if (this._firstChunkBuffer.length === 0) return;
      this._flushFirstChunkBuffer("timeout");
    }, ELEVEN_FIRST_CHUNK_TIMEOUT_MS);
  }
  _flushFirstChunkBuffer(reason) {
    if (this._firstChunkTimer) {
      clearTimeout(this._firstChunkTimer);
      this._firstChunkTimer = null;
    }
    const text = this._firstChunkBuffer;
    this._firstChunkBuffer = "";
    this._firstChunkSent = true;
    if (!text) return;
    console.log(
      `[TTS] First-chunk buffer flushed reason=${reason} len=${text.length}`,
    );
    this.outboundQueue.push({ kind: "text", text });
    this._drainOutboundQueue();
  }
  _resetFirstChunkBuffer() {
    if (this._firstChunkTimer) {
      clearTimeout(this._firstChunkTimer);
      this._firstChunkTimer = null;
    }
    this._firstChunkBuffer = "";
    this._firstChunkSent = false;
  }

  _sendFlush() {
    try {
      if (this.wsOpen && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ flush: true }));
        this.ws.send(JSON.stringify({ text: " " }));
      } else {
        this._enqueueFlush();
      }
    } catch (_) {
      this._enqueueFlush();
    }
    this.outboundQueue.length = 0;
    this.pendingBatches = 0;
    this._resetFirstChunkBuffer();
    // Task #21: turn boundary — drop any paced bytes for the
    // previous turn. The actual session teardown happens elsewhere.
    this._a2fResetPacer("sendFlush");
  }

  _drainOutboundQueue() {
    if (!this.wsOpen || this.ws.readyState !== WebSocket.OPEN) return;
    while (this.outboundQueue.length) {
      const item = this.outboundQueue.shift();
      if (item.kind === "text") {
        // Task #33: send the documented payload only — no
        // `try_trigger_generation` hint. The init message's
        // `chunk_length_schedule` controls when ElevenLabs starts
        // synthesizing, which is what the realtime WS docs recommend.
        // Task #99: ALWAYS end the wire payload with a single space.
        // ElevenLabs concatenates incoming text into one buffer; without a
        // separating space, consecutive sentence payloads fuse
        // ("...word.Next word") and the model drops/slurs the seam word —
        // the "missing word at the start of a sentence" symptom.
        const wire = item.text.endsWith(" ") ? item.text : item.text + " ";
        this.ws.send(JSON.stringify({ text: wire }));
        try {
          this._audioStats.outboundText++;
          this._audioStats.outboundTextLog.push(wire);
          // Task #274: cap the wire-text log so a long session can't
          // grow it unbounded. 60 entries comfortably covers any single
          // reply (the only window the ledger/stats read it for).
          const _otl = this._audioStats.outboundTextLog;
          if (_otl.length > 60) _otl.splice(0, _otl.length - 60);
        } catch (_) {}
      } else if (item.kind === "flush") {
        this.ws.send(JSON.stringify({ flush: true }));
        this.ws.send(JSON.stringify({ text: " " }));
        this.pendingBatches += 1;
        try {
          this._audioStats.outboundFlush++;
        } catch (_) {}
      }
    }
  }

  _wsNoteAudio() {
    this.lastAudioAt = this._nowMs();

    // Audio frames arriving = active work
    this._noteIdleActivity("ws-audio");

    if (this.idleCloseTimer) {
      clearTimeout(this.idleCloseTimer);
      this.idleCloseTimer = null;
    }
  }

  _attemptCloseIfDrained() {
    if (this.persistentWs) return;
    if (!this.closeAfterDrain) return;
    if (this.pendingBatches === 0 && this.outboundQueue.length === 0) {
      if (this.idleCloseTimer) return;
      this.idleCloseTimer = setTimeout(() => {
        this._closeWsGracefully();
        console.log("🎤 [RealtimeTTS] stream closed");
      }, 400);
    }
  }

  _closeWsGracefully() {
    if (this.wsOpen && this.ws && typeof this.ws.close === "function") {
      try {
        this.ws.send(JSON.stringify({ close: true }));
      } catch (e) {}
      try {
        this.ws.close();
      } catch (e) {}
    }
    this.wsOpen = false;
    this._stopKeepAlive();
  }

  _hardClose() {
    if (this.ws && typeof this.ws.close === "function") {
      try {
        this.ws.close();
      } catch (e) {}
    }
    this.wsOpen = false;
    this._stopKeepAlive();
  }

  _resetText() {
    this.buf = "";
    this._emittedAnyPhrase = false;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  _clearAudioQueueOnly() {
    this.audioQueue = [];
  }

  // Task #99 (accounting): a zeroed audio ledger for a new turn.
  _freshAudioStats() {
    return {
      received: 0, // ElevenLabs chunks handed to the message handler
      scheduled: 0, // chunks that reached the Web Audio graph
      droppedShield: 0, // discarded by the barge-in drop shield
      droppedStaleEpoch: 0, // discarded as belonging to a preempted turn
      tinyKept: 0, // sub-20ms chunks we now KEEP (previously dropped)
      stallGaps: 0, // times audio fell behind realtime (clean gap inserted)
      frameUnderruns: 0, // lip-sync late-frame events (read from driver)
      decodeFailed: 0, // chunks received but failed to decode (silent loss)
      outboundText: 0, // text payloads we delivered to ElevenLabs
      outboundFlush: 0, // flush markers we delivered to ElevenLabs
      elevenAcks: 0, // isFinal/audio_end/flush_complete acks received back
      alignMsgs: 0, // Task #347: WS alignment payloads consumed (were discarded)
      alignChars: 0, // Task #347: total aligned characters aggregated this turn
      outboundTextLog: [], // exact wire `{text}` strings sent to ElevenLabs
    };
  }

  /**
   * Task #347 — word timings aggregated from this turn's WS alignment
   * messages: [{w, startMs, endMs}] on the turn's audio timeline. Live/debug
   * consumers only (the hall has no stored anchors); lesson anchors resolve
   * against the HTTP path's hash-pinned alignment instead.
   */
  getTurnWordTimings() {
    try { return this._wsAlignment.words(); } catch (_) { return []; }
  }

  /**
   * Task #347 — cumulative audio-ms accounting for alignment rebasing. Only
   * PCM formats have a knowable bytes→ms ratio; for anything else we add
   * nothing and the aggregator's lastEndMs chaining does the rebasing.
   */
  _alignNoteAudioBytes(byteLength) {
    try {
      const fmt = this.outputFormat || "";
      if (fmt.indexOf("pcm_") !== 0) return;
      const rate = parseInt(fmt.slice(4), 10);
      if (!rate || !isFinite(rate)) return;
      this._alignAudioMsSoFar =
        (this._alignAudioMsSoFar || 0) + byteLength / ((rate * 2) / 1000);
    } catch (_) {}
  }

  // Task #99 (accounting): publish the per-turn ledger so it can be read
  // from the console / window without the debug recorder. Called once per
  // turn at teardown. `received === scheduled` ⇒ no audio was dropped.
  _startLedgerHeartbeat() {
    try {
      this._stopLedgerHeartbeat();
      if (typeof setInterval !== "function") return;
      this._ledgerHeartbeatTimer = setInterval(() => {
        try {
          if (this._audioStats && this._audioStats.received > 0) {
            this._publishAudioStats("tick");
          }
        } catch (_) {}
      }, 2000);
    } catch (_) {}
  }

  _stopLedgerHeartbeat() {
    try {
      if (this._ledgerHeartbeatTimer) {
        clearInterval(this._ledgerHeartbeatTimer);
        this._ledgerHeartbeatTimer = null;
      }
    } catch (_) {}
  }

  _publishAudioStats(phase) {
    try {
      const s = this._audioStats || this._freshAudioStats();
      if (this._a2fLipSyncDriver) {
        const d = this._a2fLipSyncDriver;
        s.frameUnderruns =
          typeof d.getUnderflowCount === "function"
            ? d.getUnderflowCount()
            : s.frameUnderruns;
      }
      // Audio dropped on OUR side of the wire = received-but-not-scheduled
      // plus chunks that failed to decode. If this is 0 and words were still
      // missed, the loss is upstream (ElevenLabs never produced the audio) —
      // check outbound (text sent) vs elevenAcks.
      s.lostAudio = Math.max(0, s.received - s.scheduled) + s.decodeFailed;
      if (typeof window !== "undefined") window.__audioStats = s;
      console.log(
        `[AUDIO-LEDGER:${phase || "turn"}] received=${s.received} scheduled=${s.scheduled} lostAudio=${s.lostAudio} (shield=${s.droppedShield} staleEpoch=${s.droppedStaleEpoch} decodeFailed=${s.decodeFailed}) tinyKept=${s.tinyKept} stallGaps=${s.stallGaps} frameUnderruns=${s.frameUnderruns} | outbound: text=${s.outboundText} flush=${s.outboundFlush} acks=${s.elevenAcks} | align: msgs=${s.alignMsgs} chars=${s.alignChars}`,
      );
      // Task #99 (diagnostics): the served bundle strips every console.* call,
      // so the ledger above is invisible in production. Mirror the per-turn
      // ledger AND the exact text we sent to ElevenLabs to the server log,
      // which is reliable and readable. `sentText` lets us prove whether a
      // missing span ever reached ElevenLabs (client bug) or was dropped by
      // ElevenLabs during synthesis (upstream). Best-effort, never throws.
      if (AUDIO_LEDGER_SERVER_LOG && typeof fetch === "function") {
        try {
          fetch("/api/debug/audio-ledger", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              phase: phase || "turn",
              stats: {
                received: s.received,
                scheduled: s.scheduled,
                lostAudio: s.lostAudio,
                droppedShield: s.droppedShield,
                droppedStaleEpoch: s.droppedStaleEpoch,
                decodeFailed: s.decodeFailed,
                tinyKept: s.tinyKept,
                stallGaps: s.stallGaps,
                frameUnderruns: s.frameUnderruns,
                outboundText: s.outboundText,
                outboundFlush: s.outboundFlush,
                elevenAcks: s.elevenAcks,
              },
              sentText: (s.outboundTextLog || []).join("|"),
            }),
            keepalive: true,
          }).catch(() => {});
        } catch (_) {}
      }
    } catch (_) {}
  }

  async _prewarmAudio() {
    await this._ensureAudioContext();
    try {
      const frames = Math.max(1, Math.floor(this.audioCtx.sampleRate * 0.01)); // ~10ms
      const buf = this.audioCtx.createBuffer(
        1,
        frames,
        this.audioCtx.sampleRate,
      );
      const src = this.audioCtx.createBufferSource();
      src.buffer = buf;
      const gain = this.audioCtx.createGain();
      gain.gain.value = 0; // silent
      src.connect(gain);
      gain.connect(this.masterGain || this.audioCtx.destination);
      src.start();
      src.onended = () => {
        try {
          src.disconnect();
        } catch (_) {}
        try {
          gain.disconnect();
        } catch (_) {}
      };
    } catch (e) {
      console.warn("[Audio] prewarm failed (ok to ignore):", e);
    }
  }

  _resetAudio() {
    for (const node of this._activeNodes) {
      try {
        if (node.src) node.src.stop();
      } catch (_) {}
      try {
        if (node.src) node.src.disconnect();
      } catch (_) {}
      try {
        if (node.gain) node.gain.disconnect();
      } catch (_) {}
    }
    this._activeNodes.clear();

    this._clearAudioQueueOnly();
    this.playing = false;

    if (this._lipSyncTap) {
      try {
        this._lipSyncTap.stop();
      } catch (e) {}
    }
    this._lipSyncTap = null;
    this._analyser = null;

    if (this.masterGain) {
      try {
        this.masterGain.disconnect();
      } catch (_) {}
      this.masterGain = null;
    }

    if (this.audioCtx) {
      try {
        this.audioCtx.close();
      } catch (e) {}
      this.audioCtx = null;
      this._lslog("AudioContext closed");
    }

    this._zeroLipSyncBlendshapes();
    this._PlayIdleAnimaiton();
  }

  _b64ToArrayBuffer(b64) {
    const byteStr = atob(b64);
    const buf = new ArrayBuffer(byteStr.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < byteStr.length; i++) view[i] = byteStr.charCodeAt(i);
    return buf;
  }

  async _ensureAudioContext() {
    // If Safari left an old context in 'closed', drop it so we can recreate.
    if (this.audioCtx && this.audioCtx.state === "closed") {
      console.log("[Audio] Context is closed, recreating...");
      try {
        this.audioCtx = null;
        this.masterGain = null; // Also reset master gain when context is closed
      } catch {}
    }

    // Create if missing
    if (!this.audioCtx) {
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      this._lslog("AudioContext created with state:", this.audioCtx.state);
    }

    // Explicit resume (Safari requires this after user actions)
    if (this.audioCtx.state === "suspended") {
      try {
        await this.audioCtx.resume();
        console.log("[Audio] resumed from suspended state");
      } catch (e) {
        console.warn("[Audio] resume failed, attempting recreation", e);
        // If resume fails, try to recreate the context (Safari fallback)
        try {
          this.audioCtx = new (window.AudioContext ||
            window.webkitAudioContext)();
          this.masterGain = null;
          console.log("[Audio] Context recreated after failed resume");
        } catch (recreateErr) {
          console.error("[Audio] Failed to recreate context:", recreateErr);
        }
      }
    }

    // Master gain - ensure it's properly connected
    if (!this.masterGain || !this.masterGain.context) {
      this.masterGain = this.audioCtx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.volume;
      this.masterGain.connect(this.audioCtx.destination);
      console.log("[Audio] Master gain created/reconnected");
    }

    // Lip-sync taps
    if (this._wantLipSync) {
      this._ensureLipSyncTap();
      if (
        this._lipSyncTap &&
        !this._tapCalibrated &&
        this._lipSyncOpts.autoCalibrate
      ) {
        try {
          if (this._lipSyncTap.calibrateNoise)
            this._lipSyncTap.calibrateNoise(
              this._lipSyncOpts.calibrateMs || 450,
            );
        } catch (e) {}
        this._tapCalibrated = true;
      }
    }

    // Tiny “tick” to unstick Safari’s first buffer after a stop
    try {
      const frames = Math.max(1, Math.floor(this.audioCtx.sampleRate * 0.005)); // ~5ms
      const buf = this.audioCtx.createBuffer(
        1,
        frames,
        this.audioCtx.sampleRate,
      );
      const src = this.audioCtx.createBufferSource();
      src.buffer = buf;
      const g = this.audioCtx.createGain();
      g.gain.value = 0;
      src.connect(g);
      g.connect(this.masterGain);
      src.start();
      src.onended = () => {
        try {
          src.disconnect();
          g.disconnect();
        } catch {}
      };
    } catch (_) {}
  }

  /**
   * Call this right before you start a NEW question/stream.
   * Ensures new epoch, allows upcoming audio, reconnects WS if needed,
   * and resumes/recreates AudioContext (Safari-safe).
   */
  async ensureReadyForNextResponse(fadeMs = 120) {
    await this.beginNewResponse(fadeMs); // preempt, reset gates/queues
    await this._ensureConnected(); // WS ready
    await this._ensureAudioContext(); // Safari resume/priming
    console.log(
      "[ensureReadyForNextResponse] ready (drop shield cleared in beginNewResponse)",
    );
  }

  _ensureLipSyncTap() {
    if (this._useA2FLipSync) return;
    this._lslog("_ensureLipSyncTap()", {
      wantLipSync: this._wantLipSync,
      hasAudioCtx: !!this.audioCtx,
      hasController: !!this._lipSyncController,
      hasAnalyser: !!this._analyser,
      hasTap: !!this._lipSyncTap,
    });
    if (!this._wantLipSync || !this.audioCtx || !this._lipSyncController)
      return;

    if (!this._analyser) {
      this._analyser = this.audioCtx.createAnalyser();
      this._analyser.fftSize =
        this._lipSyncOpts.fftSize === undefined
          ? 1024
          : this._lipSyncOpts.fftSize;
      this._analyser.smoothingTimeConstant =
        this._lipSyncOpts.smoothing === undefined
          ? 0.6
          : this._lipSyncOpts.smoothing;
    }

    try {
      this._analyser.disconnect();
    } catch (e) {}
    try {
      this._analyser.connect(this.masterGain);
      this._lslog("Analyser connected → masterGain");
    } catch (e) {}

    if (!this._lipSyncTap) {
      this._lipSyncTap = new RealTimeLipSyncTap(
        this.audioCtx,
        this._analyser,
        this._lipSyncController,
        this._lipSyncOpts,
      );
      try {
        this._lipSyncTap.start();
      } catch (e) {}
      this._lslog("RealTimeLipSyncTap created and started");
    }
  }

  async _playLoop() {
    if (this.playing) return;
    this.playing = true;

    await this._ensureAudioContext();

    if (this._wantLipSync && this._lipSyncTap && !this._lipSyncTap.running) {
      try {
        this._lipSyncTap.start();
      } catch (e) {}
    }

    if (this._useA2FLipSync) {
      if (this._a2fSyncGate) {
        this._v2log(
          "[A2F] Waiting for first blendshape frame before starting audio...",
        );
        await this._a2fSyncGate;
        this._v2log("[A2F] Gate released (first frame arrived OR timeout).");
      }
    }

    while (this.audioQueue.length) {
      const item = this.audioQueue.shift();
      if (!item) continue;
      const enc = item.buf !== undefined ? item.buf : item;
      if (item.epoch !== undefined && item.epoch !== this._audioEpoch) {
        // Task #30 diagnostic: surface any cross-epoch chunks dropped
        // by the scheduler so unexplained mid-response audio gaps are
        // visible in the console without recompiling.
        const sz = (enc && (enc.byteLength ?? enc.length)) || 0;
        this._audioStats.droppedStaleEpoch++;
        console.warn(
          `[Audio] chunk dropped (stale epoch): bytes=${sz} chunkEpoch=${item.epoch} currentEpoch=${this._audioEpoch}`,
        );
        continue;
      }
      try {
        let audioBuf;
        // Task #30: capture the source PCM byte length so the schedule
        // hook can validate audioBuf.duration against the canonical
        // 16 kHz PCM duration (AUDIO_DURATION_MISMATCH warning). null
        // for mp3/wav since compressed-frame bytes don't map 1:1 to
        // duration.
        let sourcePcmBytes = null;
        if (
          this.outputFormat.indexOf("mp3") === 0 ||
          this.outputFormat.indexOf("wav") === 0
        ) {
          audioBuf = await this.audioCtx.decodeAudioData(enc.slice(0));
        } else if (this.outputFormat.indexOf("pcm_16000") === 0) {
          const floatBuf = this._pcm16ToFloat32(enc);
          audioBuf = this.audioCtx.createBuffer(1, floatBuf.length, 16000);
          audioBuf.getChannelData(0).set(floatBuf);
          sourcePcmBytes = (enc && (enc.byteLength ?? enc.length)) || 0;
        } else {
          audioBuf = await this.audioCtx.decodeAudioData(enc.slice(0));
        }
        // Task #29: bypass the tiny-silent skip for the very first
        // audio chunk of a new epoch. ElevenLabs sometimes emits a
        // short (<20ms) chunk at the start of an utterance — under
        // the lowered `chunk_length_schedule` this is more likely —
        // and dropping it produced an audible "missing opening
        // syllable" symptom. Mid-utterance tiny chunks are still
        // skipped as before.
        const isFirstChunkOfEpoch = this._firstChunkOfEpochPending === true;
        if (isFirstChunkOfEpoch) {
          this._firstChunkOfEpochPending = false;
        }
        // Task #99: NEVER discard received audio. The old "tiny-silent
        // skip" dropped ANY sub-20ms chunk mid-utterance — a genuine
        // data-loss path that could clip a real syllable. Per the
        // "store every bottle, drop nothing" rule we now KEEP every
        // chunk and only COUNT the tiny ones for visibility. A handful
        // of milliseconds of extra silence is harmless on the gapless
        // single-clock scheduler; a dropped syllable is not.
        if (this._shouldSkipTinySilent(audioBuf)) {
          this._audioStats.tinyKept++;
        }
        this._scheduleBuffer(audioBuf, { sourcePcmBytes });
      } catch (e) {
        // Task #30 diagnostic: include byte size + epoch so silent
        // decode failures mid-response can be traced.
        const sz = (enc && (enc.byteLength ?? enc.length)) || 0;
        try {
          this._audioStats.decodeFailed++;
        } catch (_) {}
        console.warn(
          `[Audio] decode/play failed: bytes=${sz} epoch=${this._audioEpoch}`,
          e,
        );
      }
    }
    this.playing = false;

    if (this._activeNodes.size === 0 && this.audioQueue.length === 0) {
      this._zeroLipSyncBlendshapes();
      this._PlayIdleAnimaiton();
      this._resetStopGate();
      // Task #99 (accounting): all queued audio for this turn has now
      // played out. Emit the per-turn ledger exactly once so "words were
      // skipped" can be checked against hard numbers (received vs
      // scheduled = audio loss; frameUnderruns = mouth-skip) instead of
      // by ear.
      if (!this._audioStatsPublished && this._audioStats.received > 0) {
        this._audioStatsPublished = true;
        this._publishAudioStats("turn");
      }
      this._stopLedgerHeartbeat();
    }

    if (this._lipSyncTap && this._lipSyncTap.running) {
      const done =
        !this.playing &&
        this.audioQueue.length === 0 &&
        this._activeNodes.size === 0;
      if (done) {
        try {
          this._lipSyncTap.stop();
        } catch (e) {}
      }
    }
  }

  _shouldSkipTinySilent(audioBuf) {
    const duration = audioBuf.duration || audioBuf.length / audioBuf.sampleRate;
    return duration < 0.02;
  }

  _scheduleBuffer(audioBuf, opts) {
    const src = this.audioCtx.createBufferSource();
    src.buffer = audioBuf;
    // Task #30: source-PCM byte length is threaded in for the
    // AUDIO_DURATION_MISMATCH check. Default null when caller didn't
    // provide it (e.g. mp3/wav decode path, or any future call site).
    const sourcePcmBytes =
      opts && Number.isFinite(opts.sourcePcmBytes) ? opts.sourcePcmBytes : null;

    this._ensureLipSyncTap();

    const gain = this.audioCtx.createGain();
    gain.gain.value = this.muted ? 0 : this.volume;

    if (this._analyser) {
      src.connect(gain);
      gain.connect(this._analyser);
    } else {
      src.connect(gain);
      gain.connect(this.masterGain);
    }

    const node = { src, gain };
    this._activeNodes.add(node);

    // Task #26: capture "first schedule of turn" BEFORE we flip
    // `_timeline.armed`, so the recorder hook below knows whether
    // this is the buffer that armed the timeline (and therefore the
    // one whose syncClock(0, wallStart) call to attribute).
    const firstScheduleOfTurn = !this._timeline.armed;

    // ---- ARM TIMELINE ONCE (first scheduled buffer of response) ----
    if (!this._timeline.armed) {
      const LEAD_S = 0.08;
      const ctxNow = this.audioCtx.currentTime;

      this._timeline.armed = true;
      this._timeline.started = true;

      this._timeline.ctxStart = ctxNow + LEAD_S;
      this._timeline.wallStart = performance.now() + LEAD_S * 1000;
      this._timeline.nextCtxTime = this._timeline.ctxStart;

      if (this._useA2FLipSync && this._a2fLipSyncDriver) {
        if (this._a2fBufferedFrames.length) {
          this._a2fLipSyncDriver.enqueueFrames(this._a2fBufferedFrames, {
            autoStart: false,
          });
          this._a2fBufferedFrames = [];
        }

        // Task #30: count syncClock calls and warn on duplicates.
        // Calling syncClock more than once in the same turn is a
        // serious lipsync hazard — it re-anchors frame timing
        // mid-playback, causing visible re-snap and lead loss. The
        // helper is a no-op when the recorder isn't active.
        this._a2fLipSyncDriver.syncClock(0, this._timeline.wallStart);
        this._a2fSyncClockCallCount++;
        this._a2fDebugMaybeWarnSyncClockDup(
          "initial_schedule",
          this._timeline.wallStart,
        );
        this._a2fLipSyncDriver.startPlaybackAtWall(this._timeline.wallStart);
        this._a2fDriverStarted = true;

        // Mouth closes on the computed-clock teardown, re-armed per
        // scheduled buffer below.
        this._clearComputedTeardown();

        this._v2log("[SYNC TIMELINE]", {
          wallNow: performance.now(),
          wallStart: this._timeline.wallStart,
          untilStartMs: this._timeline.wallStart - performance.now(),
          ctxStart: this._timeline.ctxStart,
          ctxNow,
          leadMs: LEAD_S * 1000,
          bufferedFrames: this._a2fLipSyncDriver._frameQueue?.length || 0,
        });

        const watchdogDriver = this._a2fLipSyncDriver;
        const watchdogWallStart = this._timeline.wallStart;
        setTimeout(() => {
          if (
            this.playing &&
            this._a2fDriverStarted &&
            !watchdogDriver._playing
          ) {
            console.warn(
              "[A2F] Watchdog: driver not playing, restarting (no clock rebase)",
            );
            // Task #30: the watchdog used to call `syncClock(0,
            // wallStart)` here, which re-anchored frame timing
            // mid-playback and caused a visible re-snap + lead loss.
            // The spec requires syncClock to be called EXACTLY ONCE
            // per turn, so the watchdog now only nudges playback via
            // `startPlaybackAtWall` (which does NOT touch the clock
            // origin). The defensive duplicate-check below remains in
            // case some other path tries to rebase.
            watchdogDriver.startPlaybackAtWall(watchdogWallStart);
          }
        }, 250);
      }

      const delayMs = Math.max(0, this._timeline.wallStart - performance.now());
      const bufferedAtArm = this._a2fLipSyncDriver?._frameQueue?.length || 0;
      this._timelineStartTimer = setTimeout(() => {
        this._timelineStartTimer = null;
        if (
          this._a2fTurnPlaybackStartMs == null &&
          this._a2fTurnStartMs != null
        ) {
          this._a2fTurnPlaybackStartMs = performance.now();
          const sinceArm = (
            this._a2fTurnPlaybackStartMs - this._a2fTurnStartMs
          ).toFixed(1);
          const sinceGateRelease =
            this._a2fTurnGateReleaseMs != null
              ? (
                  this._a2fTurnPlaybackStartMs - this._a2fTurnGateReleaseMs
                ).toFixed(1)
              : "n/a";
          this._a2fTl("audio_playback_start", {
            bufferedFramesAtArm: bufferedAtArm,
            currentDriverQueue:
              this._a2fLipSyncDriver?._frameQueue?.length || 0,
            msSinceTurnStart: sinceArm,
            msSinceGateRelease: sinceGateRelease,
            leadMs: 80,
          });
        }
        this._PlaySpeakAnimaiton();
        this._onAudioStartedForGate();
      }, delayMs);
    }

    // ---- SCHEDULE THIS BUFFER CONTIGUOUSLY ----
    // Task #99: never schedule into the past. If audio production fell
    // behind realtime, `nextCtxTime` can be < currentTime; Web Audio then
    // starts the buffer IMMEDIATELY, overlapping the previous buffer —
    // two voices at once, which sounds exactly like garbled / skipped
    // words. Clamp to `currentTime` so a stall becomes a clean gap
    // instead of an overlap, and count it so stalls are visible in the
    // per-turn ledger.
    const ctxNowSched = this.audioCtx.currentTime;
    let startAt = this._timeline.nextCtxTime;
    if (startAt < ctxNowSched) {
      this._audioStats.stallGaps++;
      startAt = ctxNowSched;
    }
    src.start(startAt);
    this._audioStats.scheduled++;

    this._timeline.nextCtxTime =
      startAt + (audioBuf.duration || audioBuf.length / audioBuf.sampleRate);

    // Task #99: re-arm the computed-clock teardown to the AudioContext
    // instant this just-scheduled audio is known to finish. Each new
    // buffer pushes the teardown out to the new true end; when audio
    // stops arriving the last-armed timer fires the mouth-close at the
    // real end of speech — no dependence on any ElevenLabs/bridge
    // signal. `src.onended` remains a backstop (same one-shot). The end
    // is expressed in AudioContext time (not wall time) so it stays
    // correct across lip-lock holds, during which the context clock
    // freezes but wall time keeps running.
    try {
      this._armComputedTeardown(this._timeline.nextCtxTime);
    } catch (_) {}

    // Task #26: record this buffer schedule. Done AFTER src.start()
    // and after `nextCtxTime` is bumped, so the next call's gap
    // computation reads the real "previous end" cleanly. No-op when
    // the debug recorder isn't actively recording. Task #30 threads
    // sourcePcmBytes through for the AUDIO_DURATION_MISMATCH check.
    this._a2fDebugRecordAudioBufferSchedule(
      audioBuf,
      startAt,
      firstScheduleOfTurn,
      sourcePcmBytes,
    );

    if (this._wantLipSync && this._lipSyncTap && !this._lipSyncTap.running) {
      try {
        this._lipSyncTap.start();
      } catch (e) {}
    }

    this.lastAudioAt = performance.now();

    src.onended = () => {
      try {
        src.disconnect();
      } catch (_) {}
      try {
        gain.disconnect();
      } catch (_) {}
      this._activeNodes.delete(node);

      const fullyIdle =
        this._activeNodes.size === 0 && this.audioQueue.length === 0;
      if (fullyIdle) {
        // Task #99: backstop path. The computed-clock timer
        // (`_armComputedTeardown`) is the primary mouth-close; this
        // natural `src.onended` branch runs the IDENTICAL teardown via
        // the shared one-shot (`_a2fIdleTeardownRan`) in case the timer
        // is delayed. Whichever fires first wins; the other no-ops.
        this._a2fForceIdleTeardown("natural");
      }
    };
  }

  /**
   * Task #99: end-of-reply teardown — shared by the COMPUTED-clock
   * timer (`_armComputedTeardown`, primary) and the natural
   * `src.onended` fullyIdle branch (backstop).
   *
   * One-shot per turn via `_a2fIdleTeardownRan` so the computed timer
   * and the natural onended firing for the same turn can race without
   * ever running the teardown twice. The flag is reset at the start of
   * every A2F session and at every turn start.
   *
   * Steps (same order as the original src.onended fullyIdle block):
   *   1. Snapshot `_a2fDoneReceived` BEFORE `_resetStopGate()` zeros
   *      it — otherwise every clean A2F utterance would be flagged
   *      as earlyTermination in the debug recorder.
   *   2. Graceful zero-blendshapes (flushAndDecay over ~400 ms).
   *   3. Play idle animation.
   *   4. Reset stop gate (also clears `_a2fDoneReceived`).
   *   5. Send EndOfAudio to the bridge (idempotent on second call).
   *   6. Reset A2F per-turn driver state.
   *   7. Clear any pending computed-clock teardown.
   *   8. Reset the master playback timeline.
   *   9. Finalize the per-utterance debug recording JSON.
   */
  _a2fForceIdleTeardown(reason) {
    if (this._a2fIdleTeardownRan) return;
    this._a2fIdleTeardownRan = true;

    const a2fDoneSnapshot = this._a2fDoneReceived;

    try { this._zeroLipSyncBlendshapes({ graceful: true }); } catch (e) {
      console.warn("[A2F] forced teardown: zero blendshapes failed", e);
    }
    try { this._PlayIdleAnimaiton(); } catch (e) {
      console.warn("[A2F] forced teardown: idle anim failed", e);
    }
    try { this._resetStopGate(); } catch (_) {}

    try { this._a2fEndSession("end"); } catch (_) {}
    this._a2fDriverStarted = false;
    this._a2fFirstFrameTimeMs = null;
    this._a2fFirstFrameReceived = false;
    this._a2fBufferedFrames = [];

    try { this._clearComputedTeardown(); } catch (_) {}
    try { this._resetTimeline(); } catch (_) {}

    const naturalEnd = !this._useA2FLipSync || a2fDoneSnapshot;
    try { this._a2fDebugFinalize(!naturalEnd); } catch (_) {}

    // Task #99 (accounting): this is the PRIMARY end-of-reply funnel for
    // the A2F path (computed-clock timer + natural onended backstop both
    // run through here). The onended drain block only fires for the
    // non-A2F path, so the per-turn ledger must also publish here, gated
    // by the same one-shot so it can never double-emit.
    try {
      if (!this._audioStatsPublished && this._audioStats && this._audioStats.received > 0) {
        this._audioStatsPublished = true;
        this._publishAudioStats("turn");
      }
    } catch (_) {}
    this._stopLedgerHeartbeat();

    this._v2log("[A2F] Idle teardown ran reason=" + reason);
  }

  async _preemptStopAllAudio(fadeMs) {
    try {
      const sec = Math.max(0, (fadeMs || 0) / 1000);

      for (const { gain } of this._activeNodes) {
        try {
          if (gain && this.audioCtx) {
            const when = this.audioCtx.currentTime;
            try {
              gain.gain.cancelScheduledValues(when);
            } catch (_) {}
            try {
              gain.gain.setValueAtTime(gain.gain.value, when);
            } catch (_) {}
            try {
              gain.gain.linearRampToValueAtTime(0.0001, when + sec);
            } catch (_) {}
          }
        } catch (_) {}
      }

      if (sec > 0)
        await new Promise((r) => setTimeout(r, Math.round(sec * 1000)));

      for (const node of this._activeNodes) {
        try {
          if (node.src) node.src.stop();
        } catch (_) {}
        try {
          if (node.src) node.src.disconnect();
        } catch (_) {}
        try {
          if (node.gain) node.gain.disconnect();
        } catch (_) {}
      }
      this._activeNodes.clear();

      this._zeroLipSyncBlendshapes();
      this._PlayIdleAnimaiton();
    } catch (e) {
      console.warn("[Audio] preemptStopAllAudio failed", e);
      this._resetAudio(); // fallback includes idle flip
    }
  }

  _pcm16ToFloat32(arrayBuffer) {
    const view = new DataView(arrayBuffer);
    const out = new Float32Array(arrayBuffer.byteLength / 2);
    for (let i = 0, j = 0; i < arrayBuffer.byteLength; i += 2, j++) {
      const s = view.getInt16(i, true);
      out[j] = Math.max(-1, Math.min(1, s / 32768));
    }
    return out;
  }
}
